import * as React from 'react';
import React__default, { createContext, forwardRef, useRef, useState, useEffect, useContext, useCallback, Fragment, useMemo } from 'react';
import cn from 'classnames';
import { useButton } from '@react-aria/button';
import { useHover, useFocusWithin, useFocus, isFocusVisible, PressResponder } from '@react-aria/interactions';
import { mergeProps, useViewportSize, useId, useLayoutEffect, filterDOMProps, chain, useLabels, isAppleDevice } from '@react-aria/utils';
import { FocusRing as FocusRing$1, FocusScope, useFocusRing } from '@react-aria/focus';
import { usePreventScroll, useModalProvider, ModalProvider, useOverlay, useModal, DismissButton, useOverlayPosition, ariaHideOutside, useOverlayTrigger, OverlayContainer } from '@react-aria/overlays';
import { useTextField } from '@react-aria/textfield';
import { useRadioGroupState } from '@react-stately/radio';
import { useRadioGroup, useRadio } from '@react-aria/radio';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { Item, getItemCount } from '@react-stately/collections';
import { useSelectState } from '@react-stately/select';
import { useSelect, HiddenSelect } from '@react-aria/select';
import MediaQuery, { useMediaQuery as useMediaQuery$1 } from 'react-responsive';
import { ClearSlots, SlotProvider, useMediaQuery, useStyleProps, useDOMRef, shouldKeepSpectrumClassNames, useSlotProps, useFocusableRef, useUnwrapDOMRef } from '@react-spectrum/utils';
import { useOption, useListBoxSection, useListBox, listData, getItemId } from '@react-aria/listbox';
import { Text } from '@react-spectrum/text';
import { layoutInfoToStyle, Virtualizer, VirtualizerItem } from '@react-aria/virtualizer';
import { useLocale, useCollator, useFilter } from '@react-aria/i18n';
import { useSeparator } from '@react-aria/separator';
import { Size } from '@react-stately/virtualizer';
import { ListLayout } from '@react-stately/layout';
import { ProgressCircle } from '@react-spectrum/progress';
import Transition from 'react-transition-group/Transition';
import { theme } from '@react-spectrum/theme-default';
import clsx from 'clsx';
import ReactDOM from 'react-dom';
import { useComboBoxState } from '@react-stately/combobox';
import { ListKeyboardDelegate, useSelectableCollection } from '@react-aria/selection';
import { useMenuTrigger } from '@react-aria/menu';
import { useProviderProps } from '@react-spectrum/provider';
import { useCheckbox, useCheckboxGroup, useCheckboxGroupItem } from '@react-aria/checkbox';
import { useToggleState } from '@react-stately/toggle';
import { useCheckboxGroupState } from '@react-stately/checkbox';
import Select, { components } from 'react-select';
import DatePickerComponent from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, isEqual, isWithinRange, getDate, getMonth, getYear } from 'date-fns';
import { ToastContainer as ToastContainer$1 } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { isMobile as isMobile$1 } from 'react-device-detect';
import { useComboBox } from '@react-aria/combobox';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { useDialog } from '@react-aria/dialog';
import { useDropzone } from 'react-dropzone';

var defaultTheme = {
    mode: "LIGHT",
    textColor: "blue-gray",
    bgColor: "base-white",
};
var themeContext = createContext(defaultTheme);
function OmegaProvider(_a) {
    var children = _a.children, theme = _a.theme;
    var customTheme = theme !== null && theme !== void 0 ? theme : defaultTheme;
    return (React__default.createElement(themeContext.Provider, { value: customTheme }, children));
}

var cardShadowSize = {
    inner: "INNER",
    small: "SMALL",
    base: "BASE",
    medium: "MEDIUM",
    large: "LARGE",
    extraLarge: "EXTRA_LARGE",
    giant: "GIANT",
};
var Card = function (_a) {
    var shadowSize = _a.shadowSize, className = _a.className, children = _a.children, wrapperStyles = _a.wrapperStyles;
    var renderCardBasedOnSize = function () {
        switch (shadowSize) {
            case cardShadowSize.inner:
                return (React__default.createElement("div", { className: cn("card shadow-inner", className), style: wrapperStyles }, children));
            case cardShadowSize.small:
                return (React__default.createElement("div", { className: cn("card shadow-small1", className), style: wrapperStyles }, children));
            case cardShadowSize.base:
                return (React__default.createElement("div", { className: cn("card shadow-base", className), style: wrapperStyles }, children));
            case cardShadowSize.medium:
                return (React__default.createElement("div", { className: cn("card shadow-medium1", className), style: wrapperStyles }, children));
            case cardShadowSize.large:
                return (React__default.createElement("div", { className: cn("card shadow-large1", className), style: wrapperStyles }, children));
            case cardShadowSize.extraLarge:
                return (React__default.createElement("div", { className: cn("card shadow-xLarge1", className), style: wrapperStyles }, children));
            case cardShadowSize.giant:
                return (React__default.createElement("div", { className: cn("card shadow-giant", className), style: wrapperStyles }, children));
            default:
                console.warn("Unknown card shadow size: " + shadowSize);
                return React__default.createElement("div", null);
        }
    };
    return renderCardBasedOnSize();
};
Card.shadowSizes = cardShadowSize;

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var colorConstants = {
    blueGray: "blue-gray",
    red: "red",
    amber: "amber",
    lime: "lime",
    emerald: "emerald",
    teal: "teal",
    cyan: "cyan",
    indigo: "indigo",
    violet: "violet",
    orange: "orange",
    yellow: "yellow",
    lightBlue: "light-blue",
    green: "green",
    purple: "purple",
    fuchsia: "fuchsia",
    pink: "pink",
    rose: "rose",
    blue: "blue",
};

var getAvatarProfileColor = function (firstLetter) {
    switch (firstLetter) {
        case "A":
            return getColorClassesOf(colorConstants.blueGray);
        case "B":
            return getColorClassesOf(colorConstants.pink);
        case "C":
            return getColorClassesOf(colorConstants.fuchsia);
        case "D":
            return getColorClassesOf(colorConstants.purple);
        case "E":
            return getColorClassesOf(colorConstants.violet);
        case "F":
            return getColorClassesOf(colorConstants.indigo);
        case "G":
            return getColorClassesOf(colorConstants.blue);
        case "H":
            return getColorClassesOf(colorConstants.cyan);
        case "I":
            return getColorClassesOf(colorConstants.teal);
        case "J":
            return getColorClassesOf(colorConstants.emerald);
        case "K":
            return getColorClassesOf(colorConstants.green);
        case "L":
            return getColorClassesOf(colorConstants.lime);
        case "M":
            return getColorClassesOf(colorConstants.yellow);
        case "N":
            return getColorClassesOf(colorConstants.amber);
        case "O":
            return getColorClassesOf(colorConstants.orange);
        case "P":
            return getColorClassesOf(colorConstants.rose);
        case "Q":
            return getColorClassesOf(colorConstants.blueGray);
        case "R":
            return getColorClassesOf(colorConstants.pink);
        case "S":
            return getColorClassesOf(colorConstants.fuchsia);
        case "T":
            return getColorClassesOf(colorConstants.purple);
        case "U":
            return getColorClassesOf(colorConstants.violet);
        case "V":
            return getColorClassesOf(colorConstants.indigo);
        case "W":
            return getColorClassesOf(colorConstants.blue);
        case "X":
            return getColorClassesOf(colorConstants.cyan);
        case "Y":
            return getColorClassesOf(colorConstants.teal);
        case "Z":
            return getColorClassesOf(colorConstants.emerald);
        default:
            return getColorClassesOf(colorConstants.green);
    }
};
var getRandomColorClassNamesForAvatar = function (letters) {
    var color = getColorClassesOf(colorConstants.blue);
    if (letters) {
        color = getAvatarProfileColor(letters[0]);
    }
    return color;
};
var getColorClassesOf = function (color) {
    switch (color) {
        case colorConstants.blueGray:
            return {
                c500: "blue-gray-500",
                text500: "text-blue-gray-500",
                bg500: "bg-blue-gray-500",
                c600: "blue-gray-600",
                text600: "text-blue-gray-600",
                bg600: "bg-blue-gray-600",
                c200: "blue-gray-200",
                text200: "text-blue-gray-200",
                bg200: "bg-blue-gray-200",
                c700: "blue-gray-700",
                text700: "text-blue-gray-700",
                bg700: "bg-blue-gray-700",
                c800: "blue-gray-800",
                text800: "text-blue-gray-800",
                bg800: "bg-blue-gray-800",
                c400: "blue-gray-400",
                text400: "text-blue-gray-400",
                bg400: "bg-blue-gray-400",
                ring400: "ring-blue-gray-400",
                c900: "blue-gray-900",
                text900: "text-blue-gray-900",
                bg900: "bg-blue-gray-900",
                c300: "blue-gray-300",
                text300: "text-blue-gray-300",
                bg300: "bg-blue-gray-300",
                c100: "blue-gray-100",
                text100: "text-blue-gray-100",
                bg100: "bg-blue-gray-100",
            };
        case colorConstants.red:
            return {
                c50: "red-50",
                text50: "text-red-50",
                bg50: "bg-red-50",
                c100: "red-100",
                text100: "text-red-100",
                bg100: "bg-red-100",
                c200: "red-200",
                text200: "text-red-200",
                bg200: "bg-red-200",
                c300: "red-300",
                text300: "text-red-300",
                bg300: "bg-red-300",
                c500: "red-500",
                text500: "text-red-500",
                bg500: "bg-red-500",
                c400: "red-400",
                text400: "text-red-400",
                bg400: "bg-red-400",
                ring400: "ring-red-400",
                c600: "red-600",
                text600: "text-red-600",
                bg600: "bg-red-600",
                c700: "red-700",
                text700: "text-red-700",
                bg700: "bg-red-700",
                c800: "red-800",
                text800: "text-red-800",
                bg800: "bg-red-800",
                c900: "red-900",
                text900: "text-red-900",
                bg900: "bg-red-900",
            };
        case colorConstants.pink:
            return {
                c50: "pink-50",
                text50: "text-pink-50",
                bg50: "bg-pink-50",
                c100: "pink-100",
                text100: "text-pink-100",
                bg100: "bg-pink-100",
                c200: "pink-200",
                text200: "text-pink-200",
                bg200: "bg-pink-200",
                c300: "pink-300",
                text300: "text-pink-300",
                bg300: "bg-pink-300",
                c400: "pink-400",
                text400: "text-pink-400",
                bg400: "bg-pink-400",
                ring400: "ring-pink-400",
                c500: "pink-500",
                text500: "text-pink-500",
                bg500: "bg-pink-500",
                c700: "pink-700",
                text700: "text-pink-700",
                bg700: "bg-pink-700",
                c600: "pink-600",
                text600: "text-pink-600",
                bg600: "bg-pink-600",
                c800: "pink-800",
                text800: "text-pink-800",
                bg800: "bg-pink-800",
                c900: "pink-900",
                text900: "text-pink-900",
                bg900: "bg-pink-900",
            };
        case colorConstants.fuchsia:
            return {
                c50: "fuchsia-50",
                text50: "text-fuchsia-50",
                bg50: "bg-fuchsia-50",
                c100: "fuchsia-100",
                text100: "text-fuchsia-100",
                bg100: "bg-fuchsia-100",
                c200: "fuchsia-200",
                text200: "text-fuchsia-200",
                bg200: "bg-fuchsia-200",
                c300: "fuchsia-300",
                text300: "text-fuchsia-300",
                bg300: "bg-fuchsia-300",
                c400: "fuchsia-400",
                text400: "text-fuchsia-400",
                bg400: "bg-fuchsia-400",
                ring400: "ring-fuchsia-400",
                c500: "fuchsia-500",
                text500: "text-fuchsia-500",
                bg500: "bg-fuchsia-500",
                c600: "fuchsia-600",
                text600: "text-fuchsia-600",
                bg600: "bg-fuchsia-600",
                c700: "fuchsia-700",
                text700: "text-fuchsia-700",
                bg700: "bg-fuchsia-700",
                c800: "fuchsia-800",
                text800: "text-fuchsia-800",
                bg800: "bg-fuchsia-800",
                c900: "fuchsia-900",
                text900: "text-fuchsia-900",
                bg900: "bg-fuchsia-900",
            };
        case colorConstants.purple:
            return {
                c50: "purple-50",
                text50: "text-purple-50",
                bg50: "bg-purple-50",
                c100: "purple-100",
                text100: "text-purple-100",
                bg100: "bg-purple-100",
                c200: "purple-200",
                text200: "text-purple-200",
                bg200: "bg-purple-200",
                c300: "purple-300",
                text300: "text-purple-300",
                bg300: "bg-purple-300",
                c400: "purple-400",
                text400: "text-purple-400",
                bg400: "bg-purple-400",
                ring400: "ring-purple-400",
                c500: "purple-500",
                text500: "text-purple-500",
                bg500: "bg-purple-500",
                c600: "purple-600",
                text600: "text-purple-600",
                bg600: "bg-purple-600",
                c700: "purple-700",
                text700: "text-purple-700",
                bg700: "bg-purple-700",
                c800: "purple-800",
                text800: "text-purple-800",
                bg800: "bg-purple-800",
                c900: "purple-900",
                text900: "text-purple-900",
                bg900: "bg-purple-900",
            };
        case colorConstants.violet:
            return {
                c50: "violet-50",
                text50: "text-violet-50",
                bg50: "bg-violet-50",
                c100: "violet-100",
                text100: "text-violet-100",
                bg100: "bg-violet-100",
                c200: "violet-200",
                text200: "text-violet-200",
                bg200: "bg-violet-200",
                c300: "violet-300",
                text300: "text-violet-300",
                bg300: "bg-violet-300",
                c400: "violet-400",
                text400: "text-violet-400",
                bg400: "bg-violet-400",
                ring400: "ring-violet-400",
                c500: "violet-500",
                text500: "text-violet-500",
                bg500: "bg-violet-500",
                c600: "violet-600",
                text600: "text-violet-600",
                bg600: "bg-violet-600",
                c700: "violet-700",
                text700: "text-violet-700",
                bg700: "bg-violet-700",
                c800: "violet-800",
                text800: "text-violet-800",
                bg800: "bg-violet-800",
                c900: "violet-900",
                text900: "text-violet-900",
                bg900: "bg-violet-900",
            };
        case colorConstants.indigo:
            return {
                c50: "indigo-50",
                text50: "text-indigo-50",
                bg50: "bg-indigo-50",
                c100: "indigo-100",
                text100: "text-indigo-100",
                bg100: "bg-indigo-100",
                c200: "indigo-200",
                text200: "text-indigo-200",
                bg200: "bg-indigo-200",
                c300: "indigo-300",
                text300: "text-indigo-300",
                bg300: "bg-indigo-300",
                c400: "indigo-400",
                text400: "text-indigo-400",
                bg400: "bg-indigo-400",
                ring400: "ring-indigo-400",
                c500: "indigo-500",
                text500: "text-indigo-500",
                bg500: "bg-indigo-500",
                c600: "indigo-600",
                text600: "text-indigo-600",
                bg600: "bg-indigo-600",
                c700: "indigo-700",
                text700: "text-indigo-700",
                bg700: "bg-indigo-700",
                c800: "indigo-800",
                text800: "text-indigo-800",
                bg800: "bg-indigo-800",
                c900: "indigo-900",
                text900: "text-indigo-900",
                bg900: "bg-indigo-900",
            };
        case colorConstants.cyan:
            return {
                c50: "cyan-50",
                text50: "text-cyan-50",
                bg50: "bg-cyan-50",
                c100: "cyan-100",
                text100: "text-cyan-100",
                bg100: "bg-cyan-100",
                c200: "cyan-200",
                text200: "text-cyan-200",
                bg200: "bg-cyan-200",
                c300: "cyan-300",
                text300: "text-cyan-300",
                bg300: "bg-cyan-300",
                c400: "cyan-400",
                text400: "text-cyan-400",
                bg400: "bg-cyan-400",
                ring400: "ring-cyan-400",
                c500: "cyan-500",
                text500: "text-cyan-500",
                bg500: "bg-cyan-500",
                c600: "cyan-600",
                text600: "text-cyan-600",
                bg600: "bg-cyan-600",
                c700: "cyan-700",
                text700: "text-cyan-700",
                bg700: "bg-cyan-700",
                c800: "cyan-800",
                text800: "text-cyan-800",
                bg800: "bg-cyan-800",
                c900: "cyan-900",
                text900: "text-cyan-900",
                bg900: "bg-cyan-900",
            };
        case colorConstants.teal:
            return {
                c300: "teal-300",
                text300: "text-teal-300",
                bg300: "bg-teal-300",
                c50: "teal-50",
                text50: "text-teal-50",
                bg50: "bg-teal-50",
                c200: "teal-200",
                text200: "text-teal-200",
                bg200: "bg-teal-200",
                c100: "teal-100",
                text100: "text-teal-100",
                bg100: "bg-teal-100",
                c500: "teal-500",
                text500: "text-teal-500",
                bg500: "bg-teal-500",
                c600: "teal-600",
                text600: "text-teal-600",
                bg600: "bg-teal-600",
                c400: "teal-400",
                text400: "text-teal-400",
                bg400: "bg-teal-400",
                ring400: "ring-teal-400",
                c800: "teal-800",
                text800: "text-teal-800",
                bg800: "bg-teal-800",
                c700: "teal-700",
                text700: "text-teal-700",
                bg700: "bg-teal-700",
                c900: "teal-900",
                text900: "text-teal-900",
                bg900: "bg-teal-900",
            };
        case colorConstants.emerald:
            return {
                c200: "emerald-200",
                text200: "text-emerald-200",
                bg200: "bg-emerald-200",
                c50: "emerald-50",
                text50: "text-emerald-50",
                bg50: "bg-emerald-50",
                c100: "emerald-100",
                text100: "text-emerald-100",
                bg100: "bg-emerald-100",
                c500: "emerald-500",
                text500: "text-emerald-500",
                bg500: "bg-emerald-500",
                c400: "emerald-400",
                text400: "text-emerald-400",
                bg400: "bg-emerald-400",
                ring400: "ring-emerald-400",
                c300: "emerald-300",
                text300: "text-emerald-300",
                bg300: "bg-emerald-300",
                c600: "emerald-600",
                text600: "text-emerald-600",
                bg600: "bg-emerald-600",
                c700: "emerald-700",
                text700: "text-emerald-700",
                bg700: "bg-emerald-700",
                c800: "emerald-800",
                text800: "text-emerald-800",
                bg800: "bg-emerald-800",
                c900: "emerald-900",
                text900: "text-emerald-900",
                bg900: "bg-emerald-900",
            };
        case colorConstants.green:
            return {
                c50: "green-50",
                text50: "text-green-50",
                bg50: "bg-green-50",
                c100: "green-100",
                text100: "text-green-100",
                bg100: "bg-green-100",
                c200: "green-200",
                text200: "text-green-200",
                bg200: "bg-green-200",
                c300: "green-300",
                text300: "text-green-300",
                bg300: "bg-green-300",
                c400: "green-400",
                text400: "text-green-400",
                bg400: "bg-green-400",
                ring400: "ring-green-400",
                c500: "green-500",
                text500: "text-green-500",
                bg500: "bg-green-500",
                c600: "green-600",
                text600: "text-green-600",
                bg600: "bg-green-600",
                c700: "green-700",
                text700: "text-green-700",
                bg700: "bg-green-700",
                c800: "green-800",
                text800: "text-green-800",
                bg800: "bg-green-800",
                c900: "green-900",
                text900: "text-green-900",
                bg900: "bg-green-900",
            };
        case colorConstants.lime:
            return {
                c50: "lime-50",
                text50: "text-lime-50",
                bg50: "bg-lime-50",
                c100: "lime-100",
                text100: "text-lime-100",
                bg100: "bg-lime-100",
                c200: "lime-200",
                text200: "text-lime-200",
                bg200: "bg-lime-200",
                c300: "lime-300",
                text300: "text-lime-300",
                bg300: "bg-lime-300",
                c400: "lime-400",
                text400: "text-lime-400",
                bg400: "bg-lime-400",
                ring400: "ring-lime-400",
                c500: "lime-500",
                text500: "text-lime-500",
                bg500: "bg-lime-500",
                c700: "lime-700",
                text700: "text-lime-700",
                bg700: "bg-lime-700",
                c600: "lime-600",
                text600: "text-lime-600",
                bg600: "bg-lime-600",
                c800: "lime-800",
                text800: "text-lime-800",
                bg800: "bg-lime-800",
                c900: "lime-900",
                text900: "text-lime-900",
                bg900: "bg-lime-900",
            };
        case colorConstants.yellow:
            return {
                c50: "yellow-50",
                text50: "text-yellow-50",
                bg50: "bg-yellow-50",
                c100: "yellow-100",
                text100: "text-yellow-100",
                bg100: "bg-yellow-100",
                c200: "yellow-200",
                text200: "text-yellow-200",
                bg200: "bg-yellow-200",
                c300: "yellow-300",
                text300: "text-yellow-300",
                bg300: "bg-yellow-300",
                c400: "yellow-400",
                text400: "text-yellow-400",
                bg400: "bg-yellow-400",
                ring400: "ring-yellow-400",
                c500: "yellow-500",
                text500: "text-yellow-500",
                bg500: "bg-yellow-500",
                c600: "yellow-600",
                text600: "text-yellow-600",
                bg600: "bg-yellow-600",
                c700: "yellow-700",
                text700: "text-yellow-700",
                bg700: "bg-yellow-700",
                c800: "yellow-800",
                text800: "text-yellow-800",
                bg800: "bg-yellow-800",
                c900: "yellow-900",
                text900: "text-yellow-900",
                bg900: "bg-yellow-900",
            };
        case colorConstants.amber:
            return {
                c50: "amber-50",
                text50: "text-amber-50",
                bg50: "bg-amber-50",
                c100: "amber-100",
                text100: "text-amber-100",
                bg100: "bg-amber-100",
                c200: "amber-200",
                text200: "text-amber-200",
                bg200: "bg-amber-200",
                c300: "amber-300",
                text300: "text-amber-300",
                bg300: "bg-amber-300",
                c400: "amber-400",
                text400: "text-amber-400",
                bg400: "bg-amber-400",
                ring400: "ring-amber-400",
                c500: "amber-500",
                text500: "text-amber-500",
                bg500: "bg-amber-500",
                c600: "amber-600",
                text600: "text-amber-600",
                bg600: "bg-amber-600",
                c700: "amber-700",
                text700: "text-amber-700",
                bg700: "bg-amber-700",
                c800: "amber-800",
                text800: "text-amber-800",
                bg800: "bg-amber-800",
                c900: "amber-900",
                text900: "text-amber-900",
                bg900: "bg-amber-900",
            };
        case colorConstants.orange:
            return {
                c50: "orange-50",
                text50: "text-orange-50",
                bg50: "bg-orange-50",
                c100: "orange-100",
                text100: "text-orange-100",
                bg100: "bg-orange-100",
                c200: "orange-200",
                text200: "text-orange-200",
                bg200: "bg-orange-200",
                c300: "orange-300",
                text300: "text-orange-300",
                bg300: "bg-orange-300",
                c400: "orange-400",
                text400: "text-orange-400",
                bg400: "bg-orange-400",
                ring400: "ring-orange-400",
                c500: "orange-500",
                text500: "text-orange-500",
                bg500: "bg-orange-500",
                c600: "orange-600",
                text600: "text-orange-600",
                bg600: "bg-orange-600",
                c700: "orange-700",
                text700: "text-orange-700",
                bg700: "bg-orange-700",
                c800: "orange-800",
                text800: "text-orange-800",
                bg800: "bg-orange-800",
                c900: "orange-900",
                text900: "text-orange-900",
                bg900: "bg-orange-900",
            };
        case colorConstants.rose:
            return {
                c50: "rose-50",
                text50: "text-rose-50",
                bg50: "bg-rose-50",
                c100: "rose-100",
                text100: "text-rose-100",
                bg100: "bg-rose-100",
                c200: "rose-200",
                text200: "text-rose-200",
                bg200: "bg-rose-200",
                c300: "rose-300",
                text300: "text-rose-300",
                bg300: "bg-rose-300",
                c400: "rose-400",
                text400: "text-rose-400",
                bg400: "bg-rose-400",
                ring400: "ring-rose-400",
                c500: "rose-500",
                text500: "text-rose-500",
                bg500: "bg-rose-500",
                c600: "rose-600",
                text600: "text-rose-600",
                bg600: "bg-rose-600",
                c800: "rose-800",
                text800: "text-rose-800",
                bg800: "bg-rose-800",
                c900: "rose-900",
                text900: "text-rose-900",
                bg900: "bg-rose-900",
                c700: "rose-700",
                text700: "text-rose-700",
                bg700: "bg-rose-700",
            };
        case colorConstants.blue:
            return {
                c50: "blue-50",
                text50: "text-blue-50",
                bg50: "bg-blue-50",
                c100: "blue-100",
                text100: "text-blue-100",
                bg100: "bg-blue-100",
                c200: "blue-200",
                text200: "text-blue-200",
                bg200: "bg-blue-200",
                c300: "blue-300",
                text300: "text-blue-300",
                bg300: "bg-blue-300",
                c400: "blue-400",
                text400: "text-blue-400",
                bg400: "bg-blue-400",
                ring400: "ring-blue-400",
                c500: "blue-500",
                text500: "text-blue-500",
                bg500: "bg-blue-500",
                c600: "blue-600",
                text600: "text-blue-600",
                bg600: "bg-blue-600",
                c700: "blue-700",
                text700: "text-blue-700",
                bg700: "bg-blue-700",
                c800: "blue-800",
                text800: "text-blue-800",
                bg800: "bg-blue-800",
                c900: "blue-900",
                text900: "text-blue-900",
                bg900: "bg-blue-900",
            };
        default:
            return {
                c50: "blue-50",
                text50: "text-blue-50",
                bg50: "bg-blue-50",
                c100: "blue-100",
                text100: "text-blue-100",
                bg100: "bg-blue-100",
                c200: "blue-200",
                text200: "text-blue-200",
                bg200: "bg-blue-200",
                c300: "blue-300",
                text300: "text-blue-300",
                bg300: "bg-blue-300",
                c400: "blue-400",
                text400: "text-blue-400",
                bg400: "bg-blue-400",
                ring400: "ring-blue-400",
                c500: "blue-500",
                text500: "text-blue-500",
                bg500: "bg-blue-500",
                c600: "blue-600",
                text600: "text-blue-600",
                bg600: "bg-blue-600",
                c700: "blue-700",
                text700: "text-blue-700",
                bg700: "bg-blue-700",
                c800: "blue-800",
                text800: "text-blue-800",
                bg800: "bg-blue-800",
                c900: "blue-900",
                text900: "text-blue-900",
                bg900: "bg-blue-900",
            };
    }
};

function FocusRing(_a) {
    var children = _a.children, _b = _a.focusClassProps, focusClassProps = _b === void 0 ? "ring-2 ring-offset-2 ring-blue-400" : _b, props = __rest(_a, ["children", "focusClassProps"]);
    return (React__default.createElement(FocusRing$1, __assign({}, props, { focusClass: "rounded-md ring-offset-white dark:ring-offset-gray-900 " + focusClassProps }), children));
}

var Loader = function (_a) {
    var _b = _a.width, width = _b === void 0 ? 17 : _b, _c = _a.height, height = _c === void 0 ? 18 : _c, fill = _a.fill, className = _a.className, rest = __rest(_a, ["width", "height", "fill", "className"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: fill ? fill : "currentColor", className: cn("animate-spin", className), xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("mask", { id: "omega-loader-id", fill: fill ? fill : "currentColor" },
            React.createElement("path", { d: "M8.5 1.917c0-.69-.563-1.26-1.245-1.157a8.333 8.333 0 1 0 9.207 5.78c-.204-.66-.961-.92-1.583-.62-.622.3-.868 1.048-.707 1.72A5.833 5.833 0 1 1 7.26 3.3c.674-.147 1.24-.693 1.24-1.383Z" })),
        React.createElement("path", { d: "M8.5 1.917c0-.69-.563-1.26-1.245-1.157a8.333 8.333 0 1 0 9.207 5.78c-.204-.66-.961-.92-1.583-.62-.622.3-.868 1.048-.707 1.72A5.833 5.833 0 1 1 7.26 3.3c.674-.147 1.24-.693 1.24-1.383Z", stroke: fill, strokeWidth: 8, strokeLinejoin: "round", mask: "url(#omega-loader-id)" })));
};

var variants = {
    primary: "Primary",
    neutral: "Neutral",
    secondary: "Secondary",
    plain: "Plain",
};
var sizes = {
    tiny: "Tiny",
    small: "Small",
    medium: "Medium",
    large: "Large",
};

var Button = forwardRef(function (_a, forwardedRef) {
    var id = _a.id, _b = _a.autoFocus, autoFocus = _b === void 0 ? false : _b, _c = _a.variant, variant = _c === void 0 ? variants.primary : _c, _d = _a.isDisabled, _isDisabled = _d === void 0 ? false : _d, _e = _a.fillParent, fillParent = _e === void 0 ? false : _e, _f = _a.isLoading, isLoading = _f === void 0 ? false : _f, leftIcon = _a.leftIcon, rightIcon = _a.rightIcon, children = _a.children, onClick = _a.onClick, _g = _a.size, size = _g === void 0 ? "Medium" : _g, _h = _a.color, color = _h === void 0 ? "blue" : _h, _j = _a.className, className = _j === void 0 ? "" : _j, _k = _a.childrenClassName, childrenClassName = _k === void 0 ? "" : _k, _l = _a.shouldShrinkButtonWhileLoading, shouldShrinkButtonWhileLoading = _l === void 0 ? true : _l, others = __rest(_a, ["id", "autoFocus", "variant", "isDisabled", "fillParent", "isLoading", "leftIcon", "rightIcon", "children", "onClick", "size", "color", "className", "childrenClassName", "shouldShrinkButtonWhileLoading"]);
    var _ref = useRef(null);
    var ref = forwardedRef || _ref;
    var _m = getColorClassesOf(color), bg600 = _m.bg600, bg700 = _m.bg700, bg800 = _m.bg800, bg100 = _m.bg100, bg200 = _m.bg200, bg300 = _m.bg300; _m.text300; var text600 = _m.text600, text700 = _m.text700, text800 = _m.text800, bg50 = _m.bg50, ring400 = _m.ring400;
    var isDisabled = _isDisabled;
    var _o = useButton(__assign({ id: id, isDisabled: isDisabled, children: children, autoFocus: autoFocus, onPress: onClick }, others), ref), buttonProps = _o.buttonProps, isPressed = _o.isPressed;
    var _p = useHover({ isDisabled: isDisabled }), hoverProps = _p.hoverProps, isHovered = _p.isHovered;
    var getSizeStyles = function () {
        var small = sizes.small, medium = sizes.medium, large = sizes.large, tiny = sizes.tiny;
        switch (size) {
            case tiny:
                return "px-12px py-6px rounded-6px";
            case small:
                return "px-16px py-6px rounded-8px";
            case large:
                return "px-32px py-12px rounded-12px";
            case medium:
            default:
                return "px-24px py-8px rounded-10px";
        }
    };
    var getPrimaryStyles = function () {
        if (variant !== variants.primary)
            return "";
        if (isHovered)
            return bg700 + " text-base-white";
        else if (isPressed)
            return bg800 + " text-base-white";
        else if (isDisabled)
            return "bg-blue-gray-300 text-blue-gray-400";
        return bg600 + " text-base-white";
    };
    var getNeutralStyles = function () {
        var defaultStyles = "border-1 border-blue-gray-300 shadow-sm";
        if (variant !== variants.neutral)
            return "";
        if (isHovered)
            return "bg-blue-gray-100 text-blue-gray-700 " + defaultStyles;
        else if (isPressed)
            return "bg-blue-gray-200  text-blue-gray-700 " + defaultStyles;
        else if (isDisabled)
            return "bg-blue-gray-200 text-blue-gray-300 " + defaultStyles;
        return "bg-base-white text-blue-gray-700 " + defaultStyles;
    };
    var getSecondaryStyles = function () {
        if (variant !== variants.secondary)
            return "";
        if (isHovered)
            return bg200 + " " + text600;
        else if (isPressed)
            return bg300 + " " + text600;
        else if (isDisabled && !isLoading)
            return "bg-blue-gray-200 text-blue-gray-400";
        return bg100 + " " + text600;
    };
    var getPlainStyles = function () {
        if (variant !== variants.plain)
            return "";
        if (isHovered)
            return "bg-transparent " + text700 + " underline";
        else if (isPressed)
            return text800 + " " + bg50 + " underline";
        else if (isDisabled && !isLoading)
            return "bg-transparent text-blue-gray-500";
        return "bg-transparent " + text600;
    };
    var renderLoader = function () {
        return React__default.createElement(Loader, null);
    };
    var renderChildren = function () {
        return (React__default.createElement("div", { className: cn("text-sm  leading-5  font-medium flex items-center", childrenClassName) },
            leftIcon ? leftIcon() : null,
            children,
            rightIcon ? rightIcon() : null));
    };
    var renderAbsoluteLoader = function () {
        return isLoading ? React__default.createElement(Loader, { className: "absolute inset-1/2" }) : null;
    };
    var renderTransparentChildren = function () {
        return (React__default.createElement("div", { className: cn("text-sm  leading-5  font-medium flex items-center", {
                "text-transparent": isLoading,
                childrenClassName: childrenClassName,
            }) },
            leftIcon ? leftIcon() : null,
            children,
            rightIcon ? rightIcon() : null));
    };
    var renderAbsoluteLoaderAndButton = function () { return (React__default.createElement(React__default.Fragment, null,
        renderAbsoluteLoader(),
        renderTransparentChildren())); };
    var renderNormalButtonContent = function () {
        return isLoading ? renderLoader() : renderChildren();
    };
    var getStylesBasedOnVariants = function () {
        var primary = variants.primary, neutral = variants.neutral, secondary = variants.secondary, plain = variants.plain;
        var defaultStyles = "ring-2 ring-offset-2";
        switch (variant) {
            case plain:
                return "ring-none";
            case neutral:
            case secondary:
            case primary:
            default:
                return defaultStyles + " " + ring400;
        }
    };
    return (React__default.createElement(FocusRing, { autoFocus: autoFocus, focusClassProps: " " + getStylesBasedOnVariants() },
        React__default.createElement("button", __assign({}, others, { id: id, "lens-role": "button", ref: ref }, mergeProps(hoverProps, buttonProps), { className: cn(getSizeStyles(), {
                "cursor-not-allowed": isDisabled,
                "flex-grow": fillParent,
                relative: !shouldShrinkButtonWhileLoading,
            }, getPrimaryStyles(), getNeutralStyles(), getSecondaryStyles(), getPlainStyles(), "flex justify-center items-center text-14px outline-none", className) }), shouldShrinkButtonWhileLoading
            ? renderNormalButtonContent()
            : renderAbsoluteLoaderAndButton())));
});

var _a;
var shapes = {
    rounded: "ROUNDED",
    circle: "CIRCLE",
};
var notificationPositions = {
    top: "TOP",
    bottom: "BOTTOM",
};
var types = {
    solo: "SOLO",
    team: "TEAM",
};
var placeholderGenders = {
    male: "MALE",
    female: "FEMALE",
    neutral: "NEUTRAL",
};
var notificationTypes = {
    positive: "POSITIVE",
    negative: "NEGATIVE",
    neutral: "NEUTRAL",
};
var notificationColors = (_a = {},
    _a[notificationTypes.positive] = "#059669",
    _a[notificationTypes.negative] = "#dc2626",
    _a[notificationTypes.neutral] = "#cbd5e1",
    _a);

function BaseAvatar(_a) {
    var containerClassName = _a.containerClassName, name = _a.name, height = _a.height, width = _a.width, url = _a.url, imageClassName = _a.imageClassName, imageStyles = _a.imageStyles, containerStyles = _a.containerStyles, borderRadius = _a.borderRadius;
    var renderImageOrIcon = function () {
        return (React__default.createElement("img", { src: url, alt: name, style: __assign({ objectFit: "cover", height: height, width: width, borderRadius: borderRadius }, imageStyles), className: imageClassName }));
    };
    return (React__default.createElement("div", { title: name, className: cn("avatar-container", containerClassName), style: __assign({ height: height, width: width, borderRadius: borderRadius }, containerStyles) }, renderImageOrIcon()));
}

var EMPTY_STRING = "";
var SPECIAL_CHARS_REGEX = /[^a-zA-z0-9\s]/;
var changeGivenTextToUpperCase = function (text) { return text.toUpperCase(); };
var getStringWithoutRegexSelectedCharacters = function (text, regex) {
    if (regex === void 0) { regex = SPECIAL_CHARS_REGEX; }
    return text.replace(regex, "");
};
var getFirstTwoLettersFromGivenName = function (name) {
    return name.slice(0, 2);
};
var getFirstLettersFromTheGivenStringList = function (stringList, stringsCountLimit) {
    if (stringsCountLimit === void 0) { stringsCountLimit = 2; }
    return stringList
        .slice(0, stringsCountLimit)
        .map(function (stringValue) { return stringValue.charAt(0); })
        .join(EMPTY_STRING);
};

function InitialsAvatar(props) {
    var getTwoLettersFromUsername = function () {
        var name = props.name;
        if (name) {
            var givenUsername = changeGivenTextToUpperCase(name);
            var stringList = getStringWithoutRegexSelectedCharacters(givenUsername, SPECIAL_CHARS_REGEX)
                .split(" ")
                .filter(function (string) { return string.length; });
            return stringList.length > 1
                ? getFirstLettersFromTheGivenStringList(stringList, 2)
                : getFirstTwoLettersFromGivenName(givenUsername);
        }
        return EMPTY_STRING;
    };
    var wrapperClassName = props.wrapperClassName, name = props.name, width = props.width, height = props.height, borderRadius = props.borderRadius, textClassName = props.textClassName, textStyles = props.textStyles;
    var twoLettersFromUsername = getTwoLettersFromUsername();
    var _a = getRandomColorClassNamesForAvatar(twoLettersFromUsername), bg800 = _a.bg800, text100 = _a.text100;
    return (React__default.createElement("div", { title: name, style: {
            width: width,
            height: height,
            borderRadius: borderRadius,
        }, className: cn("flex justify-center items-center", bg800, text100, wrapperClassName) },
        React__default.createElement("span", { className: (cn("flex justify-center items-center"), textClassName), style: textStyles }, twoLettersFromUsername)));
}

function ManIcon1(_a) {
    var height = _a.height, width = _a.width, fill = _a.fill, rest = __rest(_a, ["height", "width", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("mask", { id: "prefix__a", style: {
                maskType: "alpha",
            }, maskUnits: "userSpaceOnUse", x: 0, y: 0, width: width, height: height },
            React.createElement("path", { d: "M64 32c0 17.673-14.327 32-32 32C14.327 64 0 49.673 0 32 0 14.327 14.327 0 32 0c17.673 0 32 14.327 32 32z", fill: fill })),
        React.createElement("g", { mask: "url(#prefix__a)" },
            React.createElement("path", { fill: "#FFEDD5", d: "M-4.983 0h73.705v73.705H-4.983z" }),
            React.createElement("path", { d: "M41.237 19.471c.55 3.529-1.256 6.74-4.034 7.172-2.778.433-5.475-2.076-6.025-5.605-.55-3.528 1.256-6.74 4.034-7.172 2.778-.433 5.475 2.077 6.025 5.605z", fill: "#603813" }),
            React.createElement("path", { d: "M27.409 35.588l10.18.413-2.91 6.025-6.307-.252-.963-6.186z", fill: "#000" }),
            React.createElement("path", { d: "M13.146 97.043c3.211 2.423 38.326 2.111 38.303.389-.059-4.2-.706-8.738-1.309-13.178-.34-2.511-.667-4.99-.863-7.36-.464-5.582-2.03-23.17-2.251-26.28-.143-2.03-.499-6.63-.54-7.178a16.705 16.705 0 00-.79-.521l-.024-.015a57.45 57.45 0 00-.667-.39L45 42.508c-1.046-.609-2.42-1.385-3.328-1.816-2.272-1.078-4.607-1.738-7.063-2.092-4.087-.588-8.139.122-11.114.953-2.671.744-4.476 1.587-4.666 1.677a.317.317 0 01-.017.009s1.702.729 2.158 3.532c1.942 11.922-3.917 21.413-3.917 21.413s-1.482 10.378-2.34 15.672c-.545 3.36-2.014 14.85-1.566 15.188z", fill: "#ACDDD9" }),
            React.createElement("path", { d: "M27.242 29.958c.112.617.22 1.235.331 1.852l.02.111.009.05c.146.819.292 1.635.392 2.457a17.795 17.795 0 01.143 2.112c.01.714-.009 1.427-.03 2.14-.013.403-.019.805-.022 1.208-.003.466-.201.969-.011 1.421.24.572.789.87 1.272 1.205.527.365 1.1.665 1.701.89 1.178.441 2.508.606 3.727.231 2.33-.715 1.713-3.278 1.705-5.133l-.002-.514-.004-1.358-.007-1.934c-.667-.163-1.29-.466-1.909-.767-2.05-1.003-4.088-2.031-6.085-3.138l-.156-.086c-.401-.223-.73-.414-1.074-.747z", fill: "#FFCFCA" }),
            React.createElement("path", { d: "M36.478 38.502c-2.308-.596-4.528-1.52-6.545-2.782a32.79 32.79 0 01-1.94-1.291c-.1-.823-.245-1.639-.391-2.457l-.36-2.014c.386.375.755.571 1.23.834 1.996 1.106 4.034 2.135 6.085 3.137.618.302 1.241.604 1.909.767l.012 3.806z", fill: "#F9BFBB" }),
            React.createElement("path", { d: "M28.434 30.442c-1.053.735-2.503.863-3.663.308a3.657 3.657 0 01-1.95-2.466 3.457 3.457 0 01-.08-.586 2.648 2.648 0 01.14-1.023c.296-.802 1.112-1.37 1.965-1.425.854-.055 1.71.377 2.224 1.062l.524 1.588.84 2.542z", fill: "#FFCFCA" }),
            React.createElement("path", { d: "M28.82 103.111c-1.1 1.651-12.46-3.255-16.233-4.815-2.857-1.182-1.82-2.88-1.513-5.09.902-6.514 1.996-12.124 3.072-18.12.41-2.288.818-4.632 1.213-7.103l.048-.304c-.481 1.589-1.356 4.855-1.72 5.991-2.063 6.466-5.365 21.737-8.804 22.287-.593.095-7.136-2.207-7.429-3.027-2.063-5.778 4.334-26.959 5.365-29.578 1.789-4.54 7.439-16.226 8.943-18.16 2.889-3.714 12.261-5.896 12.725-6.025a72.46 72.46 0 014.086-1.034c1.358 3.221 1.692 6.391 2.039 9.869 1.847 18.56-.926 53.81-1.791 55.109z", fill: "#C15252" }),
            React.createElement("path", { d: "M31.786 42.192l-.1.524-.1.534-.168.879-.1.525-.276 1.45-.096.508-.352 1.852-5.686-9.436.395-.55.27-.377.477-.66.27-.374.405-.563.266-.37.393-.546 4.402 6.604z", fill: "#363740" }),
            React.createElement("path", { d: "M31.703 42.712l-.1.534a126.518 126.518 0 01-2.619-3.44c-.785-1.07-1.617-2.13-2.242-3.305l.266-.37c.313.613.693 1.188 1.093 1.752a132.592 132.592 0 003.602 4.829zm-.261 1.398l-.099.525a86.508 86.508 0 01-2.956-3.66c-.855-1.126-1.657-2.3-2.313-3.553l.27-.374c.305.6.645 1.182 1.01 1.748.996 1.55 2.162 2.982 3.33 4.401.25.306.504.61.758.912zm-.295 1.983l-.096.508a36.496 36.496 0 01-5.613-8.074l-.03-.06.27-.376.21.41a35.868 35.868 0 005.26 7.593z", fill: "#fff" }),
            React.createElement("path", { d: "M16.22 70.799c-.599 1.464-1.307 2.941-2.156 4.304.41-2.287.819-4.632 1.213-7.102.252-.568 2.7-6.212 3.419-12.06.275 7.154-1.238 11.831-2.476 14.858z", fill: "#AF4646" }),
            React.createElement("path", { d: "M78.432 63.077c-2.51 3.515-21.02 23.525-26.689 11.831a8.367 8.367 0 01-.3-.75c-.612-1.7-1.594-5.143-1.999-6.48l.048.305.097.6c.364 2.252.74 4.4 1.117 6.503 1.076 5.996 2.17 11.606 3.072 18.12.306 2.21 1.344 3.908-1.514 5.09-3.772 1.56-15.133 6.466-16.233 4.815-.549-.823-.102-15.474-.521-30.68-.242-8.787-1.947-17.632-1.27-24.429.346-3.478.68-6.648 2.039-9.87 1.371.306 2.734.651 4.085 1.035.464.13 9.837 2.311 12.726 6.026 1.503 1.933 8.008 18.022 8.008 18.022s5.567-4.155 12.244-7.017c.963-.412 5.778 5.916 5.09 6.88z", fill: "#C15252" }),
            React.createElement("path", { d: "M33.082 42.192l.1.524.1.534.169.879.099.525.276 1.45.096.508.353 1.852 5.686-9.436-.395-.55-.271-.377-.476-.66-.27-.374-.406-.563-.265-.37-.394-.546-4.402 6.604z", fill: "#363740" }),
            React.createElement("path", { d: "M33.082 42.712l.1.534a126.72 126.72 0 002.618-3.44c.786-1.07 1.618-2.13 2.243-3.305l-.266-.37c-.313.613-.693 1.188-1.093 1.752a132.362 132.362 0 01-3.602 4.829zm.334 1.398l.099.525a86.585 86.585 0 002.956-3.66c.854-1.126 1.656-2.3 2.313-3.553l-.27-.374c-.306.6-.645 1.182-1.01 1.748-.996 1.55-2.163 2.982-3.33 4.401-.25.306-.504.61-.758.912zm.332 1.983l.097.508a36.473 36.473 0 005.613-8.074l.03-.06-.271-.376c-.07.136-.139.274-.21.41a35.875 35.875 0 01-5.26 7.593z", fill: "#fff" }),
            React.createElement("path", { d: "M40.515 32.306c-.508 1.241-1.213 2.37-2.369 3.097a6.075 6.075 0 01-3.465.907c-1.337-.058-2.623-.524-3.76-1.24a9.87 9.87 0 01-1.575-1.237c-.84-.808-1.545-1.742-2.153-2.732-.987-1.611-1.784-3.447-2.178-5.342-.45-2.143-.388-4.364.485-6.42a8.197 8.197 0 011.459-2.293c2.568-2.86 6.982-3.353 10.121-1.132 1.635 1.159 2.855 2.899 3.506 4.782.207.597.385 1.21.53 1.832.751 3.222.651 6.719-.6 9.778z", fill: "#FFCFCA" }),
            React.createElement("path", { d: "M33.896 26.551c-.168.009-.4-.259-.465-.656a1.047 1.047 0 01.057-.575c.024-.054.09-.181.194-.198l.015-.002c.169-.008.4.26.465.657.034.21.013.419-.056.575-.024.054-.091.18-.194.197l-.016.002zm5.152-.467c-.142.008-.38-.23-.445-.634a1.013 1.013 0 01.054-.557c.049-.109.116-.177.184-.188l.015-.002c.142-.007.38.23.445.634.033.203.013.406-.054.557-.049.11-.115.178-.184.189l-.015.001zm-2.63.926a.623.623 0 01.565-.169.727.727 0 01.51.384.726.726 0 01.024.64.62.62 0 01-.462.366.85.85 0 00.241-.442.651.651 0 00-.065-.426.65.65 0 00-.313-.299.848.848 0 00-.5-.055zm-2.313-4.064a2.027 2.027 0 00-1.606-.072.393.393 0 00.282.732 1.227 1.227 0 01.978.044.392.392 0 10.346-.704zm4.767-.831a1.897 1.897 0 00-1.449.287.393.393 0 00.443.647 1.123 1.123 0 01.843-.167.39.39 0 00.465-.302.395.395 0 00-.302-.465z", fill: "#1F3C49" }),
            React.createElement("path", { d: "M33.81 25.416c-.245-.05-.325.332-.078.383.246.05.325-.331.079-.383zm5.173-.5c-.246-.05-.326.331-.08.382.247.051.326-.331.08-.382z", fill: "#fff" }),
            React.createElement("path", { d: "M39.56 28.945a3.523 3.523 0 01-.32 1.882c-.424.887-1.216 1.518-2.175 1.615-.982.1-1.905-.385-2.496-1.201a3.518 3.518 0 01-.65-1.725c-.002-.023-.001-.047-.003-.072l5.633-.57c.002.024.008.048.01.07z", fill: "#D55454" }),
            React.createElement("path", { d: "M39.258 28.873a3.568 3.568 0 01-2.478 1.353 3.569 3.569 0 01-2.698-.828l5.176-.525z", fill: "#fff" }),
            React.createElement("path", { d: "M39.255 30.812c-.425.886-1.217 1.518-2.176 1.615-.982.1-1.905-.385-2.495-1.202a10.332 10.332 0 014.67-.413z", fill: "#F37C7C" }),
            React.createElement("path", { d: "M33.733 28.118c.034.642-.644 1.199-1.514 1.245-.87.046-1.604-.436-1.638-1.077-.034-.641.644-1.198 1.514-1.245.87-.046 1.604.436 1.638 1.077zm7.317-1.059c.023.433-.435.809-1.023.84-.587.031-1.083-.294-1.106-.727-.023-.433.435-.81 1.023-.84.588-.032 1.083.294 1.106.727z", fill: "#F9BFBB" }),
            React.createElement("path", { d: "M51.467 74.17c-.612-1.699-1.594-5.142-1.999-6.478l.048.304.097.6-.184-.966-1-14.942S51.577 66.79 51.62 71.58c.004.488-.069 1.541-.153 2.591z", fill: "#AF4646" }),
            React.createElement("path", { d: "M24.369 17.674c-.257-.997.069-2.104.714-2.863.645-.76 1.572-1.195 2.518-1.35.946-.157 1.916-.052 2.858.138.908.184 1.817.448 2.738.393 1.13-.068 2.224-.642 2.97-1.558.704-.866 2.221-.739 3.138-.133.918.605 1.822 1.667 2.011 2.809.181 1.094.079 2.917-1.633 3.92-2.019 1.182-8.365.763-9.222.742-.857-.021-1.778.203-2.343.894-1.123 1.372-1.343 3.439-1.422 5.272a3.888 3.888 0 00-2.042-.527c-.329.002-.687.028-1.088.319-.709-1.371-1.126-2.844-1.45-4.372-.16-.758-.272-1.54-.177-2.309.036-.293.105-.588.262-.83.236-.364.648-.561 1.059-.605.41-.044.71-.059 1.109.06z", fill: "#603813" }))));
}
ManIcon1.defaultProps = {
    fill: "#C4C4C4",
    height: 48,
    width: 48,
};

function NeutralAvatarIcon(_a) {
    var height = _a.height, width = _a.width, fill = _a.fill, rest = __rest(_a, ["height", "width", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("path", { opacity: 0.4, d: "M24 4c-5.24 0-9.5 4.26-9.5 9.5 0 5.14 4.02 9.3 9.26 9.48.16-.02.32-.02.44 0h.14c5.12-.18 9.14-4.34 9.16-9.48C33.5 8.26 29.24 4 24 4z", fill: fill || "#334155" }),
        React.createElement("path", { d: "M34.16 28.3c-5.58-3.72-14.68-3.72-20.3 0-2.54 1.7-3.94 4-3.94 6.46s1.4 4.74 3.92 6.42C16.64 43.06 20.32 44 24 44c3.68 0 7.36-.94 10.16-2.82 2.52-1.7 3.92-3.98 3.92-6.46-.02-2.46-1.4-4.74-3.92-6.42z", fill: fill || "#334155" })));
}
NeutralAvatarIcon.defaultProps = {
    fill: "#334155",
    height: 48,
    width: 48,
};

function TeamIcon(_a) {
    var width = _a.width, height = _a.height, fill = _a.fill, rest = __rest(_a, ["width", "height", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: fill, xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("path", { d: "M18 7.91h-.13c-1.89-.06-3.3-1.52-3.3-3.32 0-1.84 1.5-3.33 3.33-3.33s3.33 1.5 3.33 3.33a3.327 3.327 0 01-3.22 3.33c0-.01 0-.01-.01-.01zm-.1-5.16c-1.01 0-1.83.82-1.83 1.83 0 .99.77 1.79 1.76 1.83.01-.01.09-.01.18 0a1.832 1.832 0 00-.11-3.66zm.11 12.53c-.39 0-.78-.03-1.17-.1a.75.75 0 01-.61-.87.75.75 0 01.87-.61c1.23.21 2.53-.02 3.4-.6.47-.31.72-.7.72-1.09 0-.39-.26-.77-.72-1.08-.87-.58-2.19-.81-3.43-.59-.41.08-.8-.2-.87-.61-.07-.41.2-.8.61-.87 1.63-.29 3.32.02 4.52.82.88.59 1.39 1.43 1.39 2.33 0 .89-.5 1.74-1.39 2.34-.91.6-2.09.93-3.32.93zM5.97 7.91h-.02a3.324 3.324 0 01-3.22-3.32c0-1.84 1.5-3.34 3.33-3.34s3.33 1.5 3.33 3.33c0 1.81-1.41 3.27-3.21 3.33l-.21-.75.07.75h-.07zm.1-1.5c.06 0 .11 0 .17.01.89-.04 1.67-.84 1.67-1.83a1.83 1.83 0 10-1.93 1.83c.01-.01.05-.01.09-.01zm-.11 8.87c-1.23 0-2.41-.33-3.32-.93-.88-.59-1.39-1.44-1.39-2.34 0-.89.51-1.74 1.39-2.33 1.2-.8 2.89-1.11 4.52-.82.41.07.68.46.61.87-.07.41-.46.69-.87.61-1.24-.22-2.55.01-3.43.59-.47.31-.72.69-.72 1.08 0 .39.26.78.72 1.09.87.58 2.17.81 3.4.6.41-.07.8.21.87.61.07.41-.2.8-.61.87-.39.07-.78.1-1.17.1zm6.04.1h-.13c-1.89-.06-3.3-1.52-3.3-3.32 0-1.84 1.5-3.33 3.33-3.33s3.33 1.5 3.33 3.33a3.327 3.327 0 01-3.22 3.33c0-.01 0-.01-.01-.01zm-.1-5.16c-1.01 0-1.83.82-1.83 1.83 0 .99.77 1.79 1.76 1.83.01-.01.09-.01.18 0 .97-.05 1.72-.85 1.73-1.83 0-1-.82-1.83-1.84-1.83zm.1 12.54c-1.2 0-2.4-.31-3.33-.94-.88-.59-1.39-1.43-1.39-2.33 0-.89.5-1.75 1.39-2.34 1.87-1.24 4.8-1.24 6.66 0 .88.59 1.39 1.43 1.39 2.33 0 .89-.5 1.75-1.39 2.34-.93.62-2.13.94-3.33.94zm-2.5-4.35c-.47.31-.72.7-.72 1.09 0 .39.26.77.72 1.08 1.35.91 3.64.91 4.99 0 .47-.31.72-.7.72-1.09 0-.39-.26-.77-.72-1.08-1.34-.91-3.63-.9-4.99 0z", fill: fill })));
}
TeamIcon.defaultProps = {
    width: 24,
    height: 24,
    fill: "#67E8F9",
};

function WomenIcon1(_a) {
    var height = _a.height, width = _a.width; _a.fill; var rest = __rest(_a, ["height", "width", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("mask", { id: "prefix__a", style: {
                maskType: "alpha",
            }, maskUnits: "userSpaceOnUse", x: 0, y: 0, width: width, height: height },
            React.createElement("path", { d: "M64 32c0 17.673-14.327 32-32 32C14.327 64 0 49.673 0 32 0 14.327 14.327 0 32 0c17.673 0 32 14.327 32 32z", fill: "#C4C4C4" })),
        React.createElement("g", { mask: "url(#prefix__a)" },
            React.createElement("path", { fill: "#FFEDD5", d: "M-4.722 0h73.705v73.705H-4.722z" }),
            React.createElement("path", { d: "M51.712 47.15l-.005-.014c-.894-2.334-2.483-4.485-4.25-6.244-1.938-1.928-3.559-3.601-4.191-6.358-.47-2.048-.457-4.171-.492-6.272-.054-3.397-.375-7.05-2.325-9.948-1.71-2.543-4.48-4.267-7.601-4.313-4.01-.06-7.821 2.333-9.845 5.705-2.139 3.56-2.357 7.753-3.907 11.535-.864 2.111-2.311 3.997-3.528 5.93-1.786 2.833-3.932 5.625-4.758 8.904-1.893 7.563 2.249 15.686 9.56 18.56a15.324 15.324 0 003.115.86c5.05.84 10.187.194 15.093-1.145 2.892-.79 5.75-1.847 8.194-3.573 4.325-3.054 6.91-8.464 4.94-13.628z", fill: "#603813" }),
            React.createElement("path", { d: "M85.95 68.553a70.57 70.57 0 00-2.476-.488c-.952-.175-1.907-.329-2.854-.529-.065-.013-.221-.063-.216-.149 0-.034.032-.06.065-.07.094-.028.216.016.306.04.665.178 1.318.291 1.996.398.591.093 1.178.198 1.763.316.413.083.84.221 1.258.254.294.023.611-.055.715-.364.306-.904-1.527-1.14-2.068-1.255-1.65-.35-3.286-.77-4.94-1.099-.147-.029-.3-.04-.433-.115-.026-.015-.054-.035-.061-.064-.01-.04.02-.078.05-.107.225-.21.547-.301.824-.42.386-.168.816-.355 1.134-.638.235-.21.403-.567.214-.857-.156-.243-.46-.374-.744-.387-.36-.017-.71.116-1.048.244-.755.284-1.516.55-2.285.799-.709.228-1.469.245-2.173.493a7.86 7.86 0 00-1.34.626c-.18-.064-10.999-5.254-15.89-6.214l-8.464-11.432c-.368.41-.745.811-1.12 1.214-1.032 1.103-2.032 2.233-3.068 3.331a95.54 95.54 0 01-.987 1.026l-.38.509c3.015 3.688 8.078 9.878 9.601 11.713.097.239.242.421.446.526 5.23 2.706 18.356 3.037 18.556 3.13.142.066.25.267.355.386.13.147.268.287.412.421a7.877 7.877 0 002.942 1.728c.435.141.888.185 1.34.24.958.118 1.91.265 2.867.373.956.108 1.917.096 2.876.06.35-.014.812-.029 1.013-.368.129-.219.075-.48-.158-.6-.23-.119-.526-.097-.779-.117a148.005 148.005 0 00-2.336-.161c-.234-.014-.503.02-.723-.071-.132-.054.01-.174.087-.193.155-.038.342.017.499.034a47.746 47.746 0 001.749.158 95.97 95.97 0 002.278.133c.207.01.414.018.616-.023.372-.075.838-.499.532-.895-.155-.201-.446-.225-.702-.234a29.355 29.355 0 01-2.54-.164 39.661 39.661 0 01-1.894-.246c-.081-.012-.411-.011-.436-.11-.036-.14.239-.119.308-.11.586.07 1.174.14 1.761.212l2.937.354c.47.056 1.42.294 1.627-.315.215-.634-.55-.821-1.012-.923z", fill: "#FFCFCA" }),
            React.createElement("path", { d: "M54.265 53.082c-2.225 2.542-4.083 4.358-6.355 6.858-.27-.29-.538-.591-.795-.884a60.136 60.136 0 01-1.746-2.087l-.002-.001s-.693 15.774-.81 16.345c-.204 1.015 1.966 8.916 1.504 9.373-5.9 5.834-23.229 4.23-26.464-2.058-.375-.727 5.419-8.915 5.419-8.915s-2.382-7.544-3.563-11.923a275.706 275.706 0 01-.816-3.05c-.126.375-.347 1.37-.693 2.514-1.156-.343-8.79-1.92-9.591-2.286 2.696-8.455 6.135-12.72 7.711-13.966 1.114-.882 8.483-3.626 16.695-3.016 4.62.343 10.12 2.4 11.054 3.236 2.34 2.095 6.018 6.724 8.452 9.86z", fill: "#FBB03B" }),
            React.createElement("path", { d: "M36.829 42.37c-.486 1.543-2.126 1.904-3.575 1.888-1.597-.018-3.71-.382-4.28-2.113-.391-1.193-.109-2.563-.104-3.793a27.611 27.611 0 00-.089-2.45 22.716 22.716 0 00-.168-1.498 27.201 27.201 0 00-.366-2.004c-.04-.186-.267-.688-.047-.817.202-.12.617.234.785.335a59.396 59.396 0 007.745 3.858c.105.043.215.09.277.184.06.09.061.203.061.31l.003 2.577c0 .334.01.668.015 1.002.016.85.004 1.698-.257 2.522z", fill: "#FFCFCA" }),
            React.createElement("path", { d: "M37.086 39.849a25.067 25.067 0 01-6.588-2.833 38.84 38.84 0 01-1.717-1.115c-.04-.5-.095-1-.168-1.497a27.201 27.201 0 00-.366-2.004c-.04-.186-.267-.688-.047-.817.202-.12.617.234.785.335a59.383 59.383 0 007.745 3.858c.105.043.215.09.277.184.06.09.061.203.061.31l.003 2.577c0 .334.01.668.015 1.002z", fill: "#F9BFBB" }),
            React.createElement("path", { d: "M40.87 33.038c-.497 1.202-1.187 2.293-2.316 2.996a5.99 5.99 0 01-3.39.877c-1.307-.056-2.563-.507-3.675-1.2a9.65 9.65 0 01-1.541-1.196c-.821-.78-1.511-1.685-2.104-2.641-.965-1.558-1.744-3.333-2.13-5.167a12.178 12.178 0 01-.12-.636c-.3-1.874-.173-3.787.595-5.573a7.892 7.892 0 011.426-2.217c2.51-2.765 6.828-3.243 9.895-1.095 1.6 1.12 2.792 2.804 3.428 4.625a17.714 17.714 0 01.589 2.085c.656 3.028.527 6.284-.657 9.142z", fill: "#FFCFCA" }),
            React.createElement("path", { d: "M34.614 27.5c-.165.008-.391-.25-.455-.636a.994.994 0 01.056-.556c.023-.052.09-.175.19-.19l.015-.002c.164-.009.39.25.454.635.034.202.013.405-.054.556-.024.052-.09.175-.191.19l-.015.002zm4.771-.487c-.14.007-.373-.223-.436-.614a.968.968 0 01.053-.538c.047-.105.113-.172.18-.183l.014-.001c.14-.007.373.223.436.613a.968.968 0 01-.053.539c-.047.105-.114.172-.18.182l-.014.002zm-2.401.9c.13-.14.348-.2.553-.163a.71.71 0 01.5.372.699.699 0 01.023.617.608.608 0 01-.452.355.82.82 0 00.236-.427.63.63 0 00-.064-.413.635.635 0 00-.306-.288.839.839 0 00-.49-.053zm-2.524-3.264a.114.114 0 01-.046-.004 1.477 1.477 0 00-.662-.04 1.48 1.48 0 00-.502.178.118.118 0 01-.082.013.12.12 0 01-.075-.053.114.114 0 01.04-.155 1.714 1.714 0 01.672-.218c.228-.026.462-.005.675.058a.113.113 0 01-.014.22h-.006zm4.03-.66l-.022-.008a.115.115 0 01-.056-.056.112.112 0 01.06-.147 1.629 1.629 0 011.232 0 .11.11 0 01.06.146.112.112 0 01-.126.066l-.024-.007a1.304 1.304 0 00-.258-.078 1.395 1.395 0 00-.794.078.115.115 0 01-.067.007l-.005-.001z", fill: "#1F3C49" }),
            React.createElement("path", { d: "M34.366 26.339c-.239-.05-.317.32-.076.37.24.049.318-.321.076-.37zm5.151-.483c-.24-.049-.318.32-.078.37.24.05.318-.32.078-.37z", fill: "#fff" }),
            React.createElement("path", { d: "M40.014 29.784a3.372 3.372 0 01-.312 1.82c-.415.858-1.19 1.468-2.127 1.562-.96.096-1.862-.372-2.44-1.162a3.382 3.382 0 01-.635-1.667c-.003-.023-.001-.046-.004-.07l5.508-.552c.003.024.008.046.01.07z", fill: "#D55454" }),
            React.createElement("path", { d: "M39.737 29.715a3.498 3.498 0 01-2.423 1.308 3.513 3.513 0 01-2.637-.801l5.06-.507z", fill: "#fff" }),
            React.createElement("path", { d: "M39.598 31.614c-.415.857-1.19 1.467-2.127 1.561-.962.096-1.863-.372-2.44-1.162a10.195 10.195 0 014.567-.4z", fill: "#F37C7C" }),
            React.createElement("path", { d: "M34.209 29.003c.033.62-.63 1.16-1.48 1.204-.852.045-1.569-.421-1.602-1.041-.033-.62.63-1.16 1.48-1.204.852-.045 1.569.422 1.602 1.041zm7.166-1.041c.023.419-.425.783-1 .813-.575.03-1.058-.285-1.081-.703-.023-.42.425-.783 1-.813.575-.03 1.058.285 1.081.703z", fill: "#F9BFBB" }),
            React.createElement("path", { d: "M41.552 23.867c-1.51-.435-3.037-1.405-4.27-2.838a9.294 9.294 0 01-.459-.575c-.938 1.958-2.854 3.758-5.403 4.825-2.058.861-4.174 1.096-6.004.79-.304-1.898-.174-3.835.602-5.643.35-.822.84-1.583 1.444-2.246 2.542-2.8 6.915-3.284 10.022-1.109 1.62 1.134 2.827 2.84 3.472 4.683a17.795 17.795 0 01.596 2.113z", fill: "#603813" }),
            React.createElement("path", { d: "M29.238 31.142c-1.03.71-2.448.835-3.58.298a3.547 3.547 0 01-1.909-2.386 3.228 3.228 0 01-.076-.566 2.533 2.533 0 01.136-.99c.289-.776 1.087-1.324 1.92-1.378.836-.053 1.673.365 2.175 1.027l.513 1.536.82 2.459z", fill: "#FFCFCA" }),
            React.createElement("path", { d: "M37.024 39.983c-.126 1.078-.288 2.22-1.15 2.991-.758.677-1.817.968-2.827.96a5.243 5.243 0 01-2.979-.958c-.97-.692-1.265-1.854-1.298-2.981-.014-.527-.855-.53-.84 0 .037 1.304.392 2.677 1.477 3.515a6.093 6.093 0 003.64 1.245c1.231.012 2.505-.375 3.42-1.2 1.034-.931 1.247-2.273 1.398-3.572.06-.524-.781-.52-.841 0z", fill: "#F15A24" }),
            React.createElement("path", { d: "M18.465 94.649c.047.224.101.45.097.677-.021 1.064-1.205.788-1.653.156a1.69 1.69 0 01-.145-.255 2.216 2.216 0 01-.133-.37c-.07-.256-.108-.523-.159-.777a2.986 2.986 0 00-.276-.852.897.897 0 00-.134-.216.404.404 0 00-.131-.094.213.213 0 00-.04-.014.168.168 0 00-.043-.01.532.532 0 00-.215.024.59.59 0 00-.08.027 1.185 1.185 0 00-.35.233c-.11.11-.189.24-.247.383a1.736 1.736 0 00-.082.266c-.043.18-.062.368-.077.551-.041.477-.051.952-.082 1.43l-.007.173c-.02.501.019.982.171 1.454l.002.001c.03.092.063.183.103.274.27.627.672 1.183 1.041 1.755.335.523.8 1.195.793 1.852a1.262 1.262 0 01-.048.336.474.474 0 01-.349.326.617.617 0 01-.495-.077c-.157-.104-.266-.263-.373-.418-.682-.989-1.383-1.966-1.916-3.044-.503-1.017-1.142-3.604-1.36-4.708-.316-1.634-1.584-10.565-1.94-14.23-.726-7.502-.165-12.51.603-16.591.221-1.18.715-3.355 1.205-5.455 2.134.513 5.545 1.244 7.115 1.619l-1.85 7.38s-.736 9.72-1.04 13.144c-.274 3.092-1.039 6.662-1.112 7.581.434.458.893 1.097 1.614 2.402.337.61.676 1.225.943 1.871.421 1.014.422 2.13.65 3.196z", fill: "#FFCFCA" }),
            React.createElement("path", { d: "M23.422 49.774c-.02.205-.503 4.91-.923 6.737-.262 1.143-.668 2.327-1.034 3.28a273.021 273.021 0 01-.816-3.051s1.272-2.515 1.966-4.458c.649-1.822.792-2.438.807-2.508zm23.689 9.266a60.563 60.563 0 01-1.746-2.087l-.002-.001s.116-3.772 0-5.35c-.075-1.033-.898-4.485-.923-4.592.059.105 1.948 3.487 2.425 5.712.473 2.2.394 4.654.246 6.318z", fill: "#F7A037" }))));
}
WomenIcon1.defaultProps = {
    fill: "#C4C4C4",
    height: 64,
    width: 64,
};

function PlaceHolderAvatar(_a) {
    var placeholderGender = _a.placeholderGender, height = _a.height, width = _a.width, borderRadius = _a.borderRadius, containerStyles = _a.containerStyles, containerClassName = _a.containerClassName, imageClassName = _a.imageClassName, type = _a.type;
    var getBackground = function () {
        if (type === types.team)
            return { background: "#155e75" };
        return { background: "" };
    };
    var renderIconBasedOnGender = function () {
        if (type === types.team)
            return React__default.createElement(TeamIcon, { className: imageClassName });
        switch (placeholderGender) {
            case placeholderGenders.female:
                return (React__default.createElement(WomenIcon1, { className: imageClassName, height: height, width: width }));
            case placeholderGenders.male:
                return (React__default.createElement(ManIcon1, { className: imageClassName, height: height, width: width }));
            default:
                return (React__default.createElement(NeutralAvatarIcon, { className: imageClassName, height: height, width: width }));
        }
    };
    return (React__default.createElement("div", { style: __assign(__assign({ height: height, width: width, borderRadius: borderRadius }, getBackground()), containerStyles), className: cn("flex justify-center items-center avatar-container ", containerClassName) }, renderIconBasedOnGender()));
}

function Avatar(props) {
    var getBorderRadius = function () {
        var shape = props.shape;
        switch (shape) {
            case shapes.circle:
                return "50%";
            default:
                return "6px";
        }
    };
    var renderAvatar = function () {
        var url = props.url, name = props.name;
        var extendedProps = __assign(__assign({}, props), { borderRadius: getBorderRadius() });
        if (url) {
            return React__default.createElement(BaseAvatar, __assign({}, extendedProps));
        }
        else if (name)
            return React__default.createElement(InitialsAvatar, __assign({}, extendedProps));
        else
            return React__default.createElement(PlaceHolderAvatar, __assign({}, extendedProps));
    };
    var getNotificationBg = function () {
        var notificationType = props.notificationType, notificationColor = props.notificationColor;
        if (notificationColor)
            return { backgroundColor: notificationColor };
        return {
            backgroundColor: notificationColors[notificationType || notificationTypes.neutral],
        };
    };
    var getNotificationPositionStyles = function () {
        var shape = props.shape, notificationPosition = props.notificationPosition;
        switch (shape) {
            case shapes.rounded: {
                if (notificationPosition === notificationPositions.top)
                    return {
                        top: "-3px",
                        right: "-3px",
                    };
                return {
                    bottom: "-4px",
                    right: "-4px",
                };
            }
            default: {
                if (notificationPosition === notificationPositions.top)
                    return {
                        top: "0",
                        right: "0",
                    };
                return {
                    bottom: "0",
                    right: "0",
                };
            }
        }
    };
    var renderNotificationIcon = function () {
        var notificationIconClassName = props.notificationIconClassName;
        return (React__default.createElement("div", { style: __assign(__assign({ height: "14px", width: "14px", borderRadius: "50%", border: "2.5px solid white" }, getNotificationBg()), getNotificationPositionStyles()), className: cn("absolute", notificationIconClassName) }));
    };
    var wrapperClassName = props.wrapperClassName, shouldHideNotification = props.shouldHideNotification, wrapperStyles = props.wrapperStyles;
    return (React__default.createElement("div", { className: cn("relative flex justify-center items-center", "" + wrapperClassName), style: __assign({}, wrapperStyles) },
        shouldHideNotification ? null : renderNotificationIcon(),
        renderAvatar()));
}
Avatar.defaultProps = {
    width: 48,
    height: 48,
};
Avatar.types = types;
Avatar.placeholderGenders = placeholderGenders;
Avatar.notificationPositions = notificationPositions;
Avatar.shapes = shapes;
Avatar.notificationTypes = notificationTypes;
Avatar.colors = colorConstants;

function CloseIcon(_a) {
    var height = _a.height, width = _a.width, fill = _a.fill, rest = __rest(_a, ["height", "width", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("path", { d: "M4 12l8-8M4 4l8 8", stroke: fill, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" })));
}
CloseIcon.defaultProps = {
    fill: "#94A3B8",
    height: 16,
    width: 16,
};

var bottomSheetStates$1 = {
    close: "Close",
    open: "Open",
};

var close$1 = bottomSheetStates$1.close;
function BottomSheet(props) {
    var isOpen = props.isOpen, hideCloseIcon = props.hideCloseIcon, renderCloseIcon = props.renderCloseIcon, hasHeader = props.hasHeader, renderHeader = props.renderHeader, onStateChange = props.onStateChange, onRequestClose = props.onRequestClose, overlayClassName = props.overlayClassName, containerClassName = props.containerClassName, headerClassName = props.headerClassName, closeIconClassName = props.closeIconClassName, children = props.children, enablePreventScroll = props.enablePreventScroll;
    var viewport = useViewportSize();
    var _a = __read(React__default.useState(viewport.height), 2), height = _a[0], setHeight = _a[1];
    var timeoutRef = React__default.useRef();
    usePreventScroll({ isDisabled: !(enablePreventScroll && isOpen) });
    React__default.useEffect(function () {
        clearTimeout(timeoutRef.current);
        if (viewport.height < height && viewport.height < window.innerHeight) {
            timeoutRef.current = setTimeout(function () {
                setHeight(viewport.height);
            }, 500);
        }
        else {
            setHeight(viewport.height);
        }
    }, [height, viewport.height]);
    var renderBottomSheetCloseIcon = function () {
        return hideCloseIcon ? null : (React__default.createElement("div", { onClick: function () { return onStateChange && onStateChange(close$1); }, className: cn("flex items-center justify-center w-6 h-6 close-icon-wrapper cursor-pointer", closeIconClassName) }, renderCloseIcon()));
    };
    var renderBottomSheetHeader = function () {
        return hasHeader && renderHeader ? (React__default.createElement("div", { className: cn("mb-1 border-b-default border-blue-gray-100 pb-3 flex justify-between ", headerClassName) },
            renderHeader(),
            renderBottomSheetCloseIcon())) : (React__default.createElement("div", { className: cn("flex justify-end", headerClassName) }, renderBottomSheetCloseIcon()));
    };
    var renderBottomSheet = function () {
        return (React__default.createElement("div", { onClick: function (e) {
                e.stopPropagation();
            }, className: cn("p-20px bottom-sheet-animation bg-base-white rounded-t-24px max-h-80vh overflow-y-auto", containerClassName) },
            renderBottomSheetHeader(),
            children));
    };
    return isOpen ? (React__default.createElement("div", { onClick: onRequestClose, className: cn("fixed bottom-0 flex flex-col-reverse h-screen w-screen right-0 left-0 top-0 bg-blue-gray-600 bg-opacity-80 z-l10", overlayClassName), style: { height: height } }, renderBottomSheet())) : null;
}
var renderCloseIcon$1 = function () {
    return React__default.createElement(CloseIcon, null);
};
BottomSheet.defaultProps = {
    isOpen: false,
    hideCloseIcon: false,
    renderCloseIcon: renderCloseIcon$1,
    hasHeader: false,
};

var bottomSheetStates = {
    close: "Close",
    open: "Open",
};

var close = bottomSheetStates.close, open = bottomSheetStates.open;
function BottomSheetWithTrigger(props) {
    var isOpen = props.isOpen, onStateChange = props.onStateChange, isTriggerDisabled = props.isTriggerDisabled, renderTrigger = props.renderTrigger, onRequestClose = props.onRequestClose, shouldCloseOnRequestClose = props.shouldCloseOnRequestClose, rest = __rest(props, ["isOpen", "onStateChange", "isTriggerDisabled", "renderTrigger", "onRequestClose", "shouldCloseOnRequestClose"]);
    var isOpenInitially = isOpen !== undefined ? isOpen : false;
    var _a = __read(useState(isOpenInitially), 2), isBottomSheetOpen = _a[0], changeIsBottomSheetOpen = _a[1];
    useEffect(function () {
        changeIsBottomSheetOpen(isOpen);
        document.body.style.overflowY = isOpen ? "hidden" : "auto";
    }, [isOpen]);
    var onClickTrigger = function () {
        if (!isTriggerDisabled) {
            document.body.style.overflowY = "hidden";
            changeIsBottomSheetOpen(true);
            onStateChange && onStateChange(open);
        }
    };
    var onClickRequestClose = function () {
        if (shouldCloseOnRequestClose) {
            document.body.style.overflowY = "auto";
            changeIsBottomSheetOpen(false);
            onStateChange && onStateChange(close);
        }
        onRequestClose && onRequestClose();
    };
    var onBottomSheetOpenStateChange = function (state) {
        if (state === close) {
            onClickRequestClose();
        }
        onStateChange && onStateChange(state);
    };
    var renderBottomSheet = function () {
        return isBottomSheetOpen ? (React__default.createElement(BottomSheet, __assign({}, rest, { onStateChange: onBottomSheetOpenStateChange, isOpen: isBottomSheetOpen, onRequestClose: onClickRequestClose }))) : null;
    };
    return (React__default.createElement(React__default.Fragment, null,
        renderTrigger(onClickTrigger),
        renderBottomSheet()));
}
var renderCloseIcon = function () {
    return React__default.createElement(CloseIcon, null);
};
BottomSheetWithTrigger.defaultProps = {
    isOpen: false,
    hideCloseIcon: false,
    renderCloseIcon: renderCloseIcon,
    hasHeader: false,
    renderHeader: function () { return React__default.createElement("p", null, "header"); },
    isTriggerDisabled: false,
    shouldCloseOnRequestClose: true,
};

var getDataMaskAttribute = function (enableDataMask) {
    if (enableDataMask) {
        return {
            "data-clarity-mask": "True",
        };
    }
    return {};
};

function Label(_a) {
    var id = _a.id, children = _a.children, labelProps = _a.labelProps, labelClassName = _a.labelClassName, isRequired = _a.isRequired;
    return (React__default.createElement("label", __assign({}, labelProps, { id: id, "lens-role": "label", className: cn("text-blue-gray-700 dark:text-gray-100 flex items-center", "font-medium text-sm leading-5", "flex-grow-0 text-left tracking-normal not-italic", labelClassName) }),
        React__default.createElement("p", null,
            children,
            isRequired ? (React__default.createElement("span", { className: cn("text-red-500 ml-4px pt-4px") }, "*")) : null)));
}

function Hint(_a) {
    var id = _a.id, hint = _a.hint, errorText = _a.errorText, hintClassName = _a.hintClassName, renderHintLeftIcon = _a.renderHintLeftIcon;
    var renderHint = function () {
        return (React__default.createElement(React__default.Fragment, null,
            renderHintLeftIcon && renderHintLeftIcon(),
            hint));
    };
    if ((errorText && errorText !== "") || (hint && hint !== "")) {
        return (React__default.createElement("div", { id: id, className: cn("mt-1", "text-xs", "flex", hintClassName, {
                "text-red-600 dark:text-red-600": !!errorText,
                "text-blue-gray-700 dark:text-gray-400": !!hint,
            }, "animate-slide-top", "font-normal", "leading-4 tracking-normal text-left flex-grow-0 not-italic") }, errorText || renderHint()));
    }
    else {
        return React__default.createElement("div", { className: cn("h-20px", hintClassName) });
    }
}

var colors$1 = {
    indigo: "indigo",
    blue: "blue",
    cyan: "cyan",
};

function focusStateRingColor$1(color) {
    switch (color) {
        case colors$1.indigo:
            return "ring-indigo-200";
        case colors$1.cyan:
            return "ring-cyan-200";
        default:
            return "ring-blue-200";
    }
}
function focusStateBorderColor(color) {
    switch (color) {
        case colors$1.indigo:
            return "border-indigo-600";
        case colors$1.cyan:
            return "border-cyan-600";
        default:
            return "border-blue-600";
    }
}
var TextFieldBase = forwardRef(function (props, forwardedRef) {
    var _a;
    var _b = props.autoFocus, autoFocus = _b === void 0 ? false : _b, errorMessage = props.errorMessage, hint = props.hint, _c = props.isDisabled, isDisabled = _c === void 0 ? false : _c, label = props.label, LeftElement = props.leftElement, RightElement = props.rightElement, _d = props.color, color = _d === void 0 ? colors$1.blue : _d, _e = props.containerClassName, containerClassName = _e === void 0 ? "w-full" : _e, _f = props.leftElementContainerClassName, leftElementContainerClassName = _f === void 0 ? "" : _f, _g = props.rightElementContainerClassName, rightElementContainerClassName = _g === void 0 ? "" : _g, _h = props.inputContainerClassName, inputContainerClassName = _h === void 0 ? "" : _h, _j = props.inputClassName, inputClassName = _j === void 0 ? "" : _j, _k = props.hintClassName, hintClassName = _k === void 0 ? "text-input-hint" : _k, _l = props.labelClassName, labelClassName = _l === void 0 ? "" : _l, isRequired = props.isRequired, _m = props.focusRingClassName, focusRingClassName = _m === void 0 ? "" : _m, _o = props.shouldShowHint, shouldShowHint = _o === void 0 ? true : _o, inputProps = props.inputProps, labelProps = props.labelProps; props.onFocus; props.onBlur; var _p = props.enableDataMask, enableDataMask = _p === void 0 ? false : _p;
    var inputRef = forwardedRef;
    var _q = __read(React__default.useState(false), 2), isFocusWithin = _q[0], setFocusWithin = _q[1];
    var focusWithinProps = useFocusWithin({
        isDisabled: isDisabled,
        onFocusWithinChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusWithinProps;
    var focusProps = useFocus({
        onBlur: function () {
            // Validation is disabled until the user touches / focuses the field at least once
        },
        onFocusChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusProps;
    var hintId = useId();
    return (React__default.createElement("div", { className: cn("flex flex-col", containerClassName) },
        label ? (React__default.createElement(Label, { labelProps: labelProps, labelClassName: labelClassName, isRequired: isRequired }, label)) : null,
        React__default.createElement("section", { className: cn("table-cell w-full mt-1") },
            React__default.createElement(FocusRing, { autoFocus: autoFocus, within: true, focusClassProps: "ring-offset-0 " + (errorMessage && !isFocusWithin
                    ? "ring-0 border-1 border-red-400"
                    : isFocusWithin
                        ? focusRingClassName
                            ? focusRingClassName
                            : "ring " + focusStateRingColor$1(color)
                        : "") },
                React__default.createElement("div", __assign({}, focusWithinProps, { className: cn("flex flex-grow items-center", "border-1 border-blue-gray-300 dark:border-gray-700 bg-base-white rounded-lg", "text-sm", "overflow-hidden", inputContainerClassName, (_a = {
                            "bg-blue-gray-100 dark:bg-blue-gray-100": isDisabled,
                            "bg-white dark:bg-gray-900 shadow-small1": !isDisabled,
                            "cursor-not-allowed": isDisabled,
                            "border-1 border-red-400 dark:border-red-400 rounded-md bg-base-white": !!errorMessage && !isFocusWithin
                        },
                        _a["border-1 " + focusStateBorderColor(color) + " rounded-md"] = isFocusWithin,
                        _a)) }),
                    LeftElement ? (React__default.createElement("div", { className: cn("py-2 pl-14px pr-2", "bg-base-white", "select-none", "flex flex-col justify-center	rounded-md", "text-blue-gray-500 dark:text-gray-100 outline-none", { "bg-blue-gray-100": isDisabled }, leftElementContainerClassName) },
                        React__default.createElement(LeftElement, { color: errorMessage && !isFocusWithin ? "#f87171" : null }))) : null,
                    React__default.createElement("input", __assign({ ref: inputRef, "lens-role": "text-field" }, mergeProps(focusProps, inputProps), { className: cn("flex-grow-default input rounded-lg", "px-14px py-2", "focus:border-0 focus:rounded-md focus:outline-none", "text-blue-gray-900 text-sm leading-5 tracking-normal not-italic", "placeholder-blue-gray-500", {
                            disabled: isDisabled,
                        }, {
                            "placeholder-blue-gray-400": isDisabled,
                        }, {
                            "border-none shadow-none rounded-l-none pl-0": LeftElement,
                        }, {
                            "border-none rounded-none shadow-none pr-0": RightElement,
                        }, {
                            "border-none rounded-none shadow-none text-red-800": !!errorMessage && !isFocusWithin,
                        }, inputClassName), required: isRequired }, getDataMaskAttribute(enableDataMask))),
                    RightElement ? (React__default.createElement("button", { className: cn("py-2 pr-14px pl-2", "bg-base-white", "select-none", "flex flex-col justify-center	rounded-md", "text-blue-gray-500 dark:text-gray-100 outline-none", { "bg-blue-gray-100": isDisabled }, rightElementContainerClassName) },
                        React__default.createElement(RightElement, { color: errorMessage && !isFocusWithin ? "#f87171" : null }))) : null)),
            shouldShowHint && (React__default.createElement(Hint, { id: hintId, hint: hint, errorText: errorMessage && !isFocusWithin ? errorMessage : "", hintClassName: hintClassName })))));
});

var TextField = forwardRef(function (props, forwardedRef) {
    var _a = props.autoFocus, autoFocus = _a === void 0 ? false : _a, errorMessage = props.errorMessage, hint = props.hint, _b = props.isDisabled, isDisabled = _b === void 0 ? false : _b, _c = props.type, type = _c === void 0 ? "text" : _c;
    var _inputRef = useRef(null);
    var inputRef = forwardedRef || _inputRef;
    var hintId = useId();
    var _d = useTextField(__assign(__assign({}, props), { "aria-describedby": errorMessage || hint ? hintId : undefined, autoFocus: autoFocus, isDisabled: isDisabled, isReadOnly: isDisabled, type: type, validationState: !!errorMessage ? "invalid" : "valid", description: hint }), inputRef), labelProps = _d.labelProps, inputProps = _d.inputProps;
    return (React__default.createElement(TextFieldBase, __assign({}, props, { inputProps: inputProps, labelProps: labelProps, ref: inputRef })));
});

var colors = {
    baseBlack: "#000000",
    blueGray50: "#f8fafc",
    baseWhite: "#ffffff",
    blueGray500: "#64748b",
    blueGray600: "#475569",
    blueGray200: "#e2e8f0",
    blueGray700: "#334155",
    blueGray800: "#1e293b",
    blueGray400: "#94a3b8",
    blueGray900: "#0f172a",
    blueGray300: "#cbd5e1",
    blueGray100: "#f1f5f9",
    red50: "#fef2f2",
    red100: "#fee2e2",
    red200: "#fecaca",
    red300: "#fca5a5",
    red500: "#ef4444",
    red400: "#f87171",
    red600: "#dc2626",
    red700: "#b91c1c",
    red800: "#991b1b",
    red900: "#7f1d1d",
    pink50: "#fdf2f8",
    pink100: "#fce7f3",
    pink200: "#fbcfe8",
    pink300: "#f9a8d4",
    pink400: "#f472b6",
    pink500: "#ec4899",
    pink700: "#be185d",
    pink600: "#db2777",
    pink800: "#9d174d",
    pink900: "#831843",
    fuchsia50: "#fdf4ff",
    fuchsia100: "#fae8ff",
    fuchsia200: "#f5d0fe",
    fuchsia300: "#f0abfc",
    fuchsia400: "#e879f9",
    fuchsia500: "#d946ef",
    fuchsia600: "#c026d3",
    fuchsia700: "#a21caf",
    fuchsia800: "#86198f",
    fuchsia900: "#701a75",
    purple50: "#faf5ff",
    purple100: "#f3e8ff",
    purple200: "#e9d5ff",
    purple300: "#d8b4fe",
    purple400: "#c084fc",
    purple500: "#a855f7",
    purple600: "#9333ea",
    purple700: "#7e22ce",
    purple800: "#6b21a8",
    purple900: "#581c87",
    violet50: "#f5f3ff",
    violet100: "#ede9fe",
    violet200: "#ddd6fe",
    violet300: "#c4b5fd",
    violet400: "#a78bfa",
    violet500: "#8b5cf6",
    violet600: "#7c3aed",
    violet700: "#6d28d9",
    violet800: "#5b21b6",
    violet900: "#4c1d95",
    indigo50: "#eef2ff",
    indigo100: "#e0e7ff",
    indigo200: "#c7d2fe",
    indigo300: "#a5b4fc",
    indigo400: "#818cf8",
    indigo500: "#6366f1",
    indigo600: "#4f46e5",
    indigo700: "#4338ca",
    indigo800: "#3730a3",
    indigo900: "#312e81",
    blue50: "#eff6ff",
    blue100: "#dbeafe",
    blue200: "#bfdbfe",
    blue300: "#93c5fd",
    blue400: "#60a5fa",
    blue500: "#3b82f6",
    blue600: "#2563eb",
    blue700: "#1d4ed8",
    blue800: "#1e40af",
    blue900: "#1e3a8a",
    cyan50: "#ecfeff",
    cyan100: "#cffafe",
    cyan200: "#a5f3fc",
    cyan300: "#67e8f9",
    cyan400: "#22d3ee",
    cyan500: "#06b6d4",
    cyan600: "#0891b2",
    cyan700: "#0e7490",
    teal300: "#5eead4",
    teal50: "#f0fdfa",
    cyan800: "#155e75",
    teal200: "#99f6e4",
    teal100: "#ccfbf1",
    cyan900: "#164e63",
    teal500: "#14b8a6",
    teal600: "#0d9488",
    teal400: "#2dd4bf",
    teal800: "#115e59",
    teal700: "#0f766e",
    teal900: "#134e4a",
    emerald200: "#a7f3d0",
    emerald50: "#ecfdf5",
    emerald100: "#d1fae5",
    emerald500: "#10b981",
    emerald400: "#34d399",
    emerald300: "#6ee7b7",
    emerald600: "#059669",
    emerald700: "#047857",
    emerald800: "#065f46",
    emerald900: "#064e3b",
    green50: "#f0fdf4",
    green100: "#dcfce7",
    green200: "#bbf7d0",
    green300: "#86efac",
    green400: "#4ade80",
    green500: "#22c55e",
    green600: "#16a34a",
    green700: "#15803d",
    green800: "#166534",
    green900: "#14532d",
    lime50: "#f7fee7",
    lime100: "#ecfccb",
    lime200: "#d9f99d",
    lime300: "#bef264",
    lime400: "#a3e635",
    lime500: "#84cc16",
    lime700: "#4d7c0f",
    lime600: "#65a30d",
    lime800: "#3f6212",
    lime900: "#365314",
    yellow50: "#fefce8",
    yellow100: "#fef9c3",
    yellow200: "#fef08a",
    yellow300: "#fde047",
    yellow400: "#facc15",
    yellow500: "#eab308",
    yellow600: "#ca8a04",
    yellow700: "#a16207",
    yellow800: "#854d0e",
    yellow900: "#713f12",
    amber50: "#fffbeb",
    amber100: "#fef3c7",
    amber200: "#fde68a",
    amber300: "#fcd34d",
    amber400: "#fbbf24",
    amber500: "#f59e0b",
    amber600: "#d97706",
    amber700: "#b45309",
    amber800: "#92400e",
    orange50: "#fff7ed",
    amber900: "#78350f",
    orange100: "#ffedd5",
    orange200: "#fed7aa",
    orange300: "#fdba74",
    orange400: "#fb923c",
    orange500: "#f97316",
    orange600: "#ea580c",
    orange700: "#c2410c",
    orange800: "#9a3412",
    orange900: "#7c2d12",
    rose50: "#fff1f2",
    rose100: "#ffe4e6",
    rose200: "#fecdd3",
    rose300: "#fda4af",
    rose400: "#fb7185",
    rose500: "#f43f5e",
    rose600: "#e11d48",
    rose800: "#9f1239",
    rose900: "#881337",
    rose700: "#be123c",
    glassWhite: "rgba(255, 255, 255, 0.24)",
    glassBlack: "rgba(0, 0, 0, 0.24)",
};

function SvgComponent$1(_a) {
    var _b = _a.width, width = _b === void 0 ? 16 : _b, _c = _a.height, height = _c === void 0 ? 16 : _c, _d = _a.fill, fill = _d === void 0 ? colors.blue600 : _d;
    return (React.createElement("svg", { width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("circle", { cx: 8, cy: 8, r: 5.5, stroke: fill, strokeWidth: 5 })));
}

function SvgComponent(_a) {
    var _b = _a.width, width = _b === void 0 ? 16 : _b, _c = _a.height, height = _c === void 0 ? 16 : _c, _d = _a.fill, fill = _d === void 0 ? colors.blueGray300 : _d;
    return (React.createElement("svg", { width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("circle", { cx: 8, cy: 8, r: 7.5, stroke: fill })));
}

// RadioGroup is the same as in the previous example
var RadioContext = React__default.createContext(null);
function RadioGroup(props) {
    var children = props.children, label = props.label, labelClassName = props.labelClassName, className = props.className, hint = props.hint, errorMessage = props.errorMessage, hintClassName = props.hintClassName, isRequired = props.isRequired, containerClassName = props.containerClassName, other = __rest(props, ["children", "label", "labelClassName", "className", "hint", "errorMessage", "hintClassName", "isRequired", "containerClassName"]);
    var hintId = useId();
    var state = useRadioGroupState(props);
    var _a = useRadioGroup(props, state), radioGroupProps = _a.radioGroupProps, labelProps = _a.labelProps;
    return (React__default.createElement("div", { className: cn(containerClassName) },
        React__default.createElement("div", __assign({}, radioGroupProps, { className: className }),
            React__default.createElement(Label, __assign({}, labelProps, { labelClassName: labelClassName + " mb-4", isRequired: isRequired }), label),
            React__default.createElement(RadioContext.Provider, { value: { state: state, parentProps: other } }, children)),
        React__default.createElement(Hint, { id: hintId, hint: hint, errorText: errorMessage ? errorMessage : "", hintClassName: hintClassName })));
}
function Radio(props) {
    var children = props.children, isDisabled = props.isDisabled, autoFocus = props.autoFocus, className = props.className, radioButtonClassName = props.radioButtonClassName;
    var contextValues = React__default.useContext(RadioContext);
    var state = contextValues.state;
    var parentProps = contextValues.parentProps;
    var ref = React__default.useRef(null);
    var inputProps = useRadio(props, state, ref).inputProps;
    var _a = __read(React__default.useState(false), 2); _a[0]; var setFocusWithin = _a[1];
    var focusWithinProps = useFocusWithin({
        isDisabled: isDisabled,
        onFocusWithinChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusWithinProps;
    var focusProps = useFocus({
        onBlur: function () {
            // Validation is disabled until the user touches / focuses the field at least once
        },
        onFocusChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusProps;
    var _b = useHover({ isDisabled: isDisabled }), hoverProps = _b.hoverProps, isHovered = _b.isHovered;
    var isSelected = state.selectedValue === props.value;
    var type = parentProps.type, hideRadioButtons = parentProps.hideRadioButtons, radioButtonPosition = parentProps.radioButtonPosition;
    var getRingColor = function () {
        if (isHovered && isSelected)
            return colors.blue700;
        if (isHovered)
            return colors.blueGray400;
        if (isDisabled)
            return colors.blueGray200;
        if (isSelected)
            return colors.blue600;
        return colors.blueGray300;
    };
    var renderRadioButton = function () {
        var radioIconColor = getRingColor();
        return hideRadioButtons ? null : (React__default.createElement(FocusRing, { autoFocus: autoFocus, focusClassProps: "ring-2 ring-offset-2 ring-blue-400" },
            React__default.createElement("div", __assign({}, focusProps, focusWithinProps, hoverProps, { className: cn("flex mr-4px", radioButtonClassName, { "cursor-not-allowed": isDisabled }, { "cursor-pointer": !isDisabled }) }), isSelected ? (React__default.createElement(SvgComponent$1, { fill: radioIconColor })) : (React__default.createElement(SvgComponent, { fill: radioIconColor })))));
    };
    var renderRadioWithChild = function () {
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(VisuallyHidden, null,
                React__default.createElement("input", __assign({}, inputProps, { ref: ref }))),
            radioButtonPosition === "RIGHT" ? null : renderRadioButton(),
            React__default.createElement("span", { className: cn("text-sm leading-5 font-normal", { "text-blue-gray-400": isDisabled }, { "text-blue-gray-700": !isDisabled }) }, children),
            radioButtonPosition === "RIGHT" ? renderRadioButton() : null));
    };
    var renderSimpleRadioOption = function () {
        return (React__default.createElement("label", { className: cn("flex items-center", { "cursor-not-allowed": isDisabled }, { "cursor-pointer": !isDisabled }, className) }, renderRadioWithChild()));
    };
    var getBorderStyles = function () {
        return isSelected
            ? "border-2 border-blue-600"
            : "border-1 border-blue-gray-300";
    };
    var renderAdvancedRadio = function () {
        return (React__default.createElement("label", { className: cn("flex items-center p-16px rounded-12px", { "cursor-not-allowed": isDisabled }, { "cursor-pointer": !isDisabled }, getBorderStyles(), className) }, renderRadioWithChild()));
    };
    return type === "ADVANCED" ? renderAdvancedRadio() : renderSimpleRadioOption();
}

var positionConstants = {
    bottom: "bottom",
    bottomLeft: "bottom left",
    bottomRight: "bottom right",
    left: "left",
    leftBottom: "left bottom",
    leftTop: "left top",
    right: "right",
    top: "top",
    topLeft: "top left",
    topRight: "top right",
    bottomStart: "bottom start",
    bottomEnd: "bottom end",
};

var CrossIcon = function (_a) {
    var _b = _a.width, width = _b === void 0 ? 16 : _b, _c = _a.height, height = _c === void 0 ? 16 : _c, _d = _a.fill, fill = _d === void 0 ? "#475569" : _d;
    return (React.createElement("svg", { width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { d: "m4 12 8-8M4 4l8 8", stroke: fill, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" })));
};

var index$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  default: CrossIcon
});

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
// @ts-ignore
var ListBoxContext = React__default.createContext(null);

function TickIcon(props) {
    var _a = props.width, width = _a === void 0 ? 20 : _a, _b = props.height, height = _b === void 0 ? 20 : _b, _c = props.fill, fill = _c === void 0 ? colors["blue600"] : _c, rest = __rest(props, ["width", "height", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z", fill: fill })));
}

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  default: TickIcon
});

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
/** @private */
function ListBoxOption(props) {
    var item = props.item, shouldSelectOnPressUp = props.shouldSelectOnPressUp, shouldFocusOnHover = props.shouldFocusOnHover, shouldUseVirtualFocus = props.shouldUseVirtualFocus, CustomOption = props.customOption;
    var rendered = item.rendered, key = item.key;
    var state = useContext(ListBoxContext);
    var ref = useRef();
    var _a = useOption({
        "aria-label": item["aria-label"],
        key: key,
        shouldSelectOnPressUp: shouldSelectOnPressUp,
        shouldFocusOnHover: shouldFocusOnHover,
        isVirtualized: true,
        shouldUseVirtualFocus: shouldUseVirtualFocus,
    }, state, ref), optionProps = _a.optionProps, labelProps = _a.labelProps, descriptionProps = _a.descriptionProps, isSelected = _a.isSelected, isDisabled = _a.isDisabled, isFocused = _a.isFocused;
    var _b = useHover(__assign(__assign({}, props), { isDisabled: isDisabled })), hoverProps = _b.hoverProps, isHovered = _b.isHovered;
    var contents = typeof rendered === "string" ? React__default.createElement(Text, null, rendered) : rendered;
    var isKeyboardModality = isFocusVisible();
    return (React__default.createElement(FocusRing$1, { focusRingClass: cn("focus-ring") }, CustomOption ? (React__default.createElement(CustomOption, { optionProps: __assign({}, mergeProps(optionProps, shouldFocusOnHover ? {} : hoverProps)), optionRef: ref, item: item, labelProps: labelProps, descriptionProps: descriptionProps, isSelected: isSelected, isFocused: isFocused, isDisabled: isDisabled })) : (React__default.createElement("div", __assign({}, mergeProps(optionProps, shouldFocusOnHover ? {} : hoverProps), { ref: ref, className: cn("mx-8px pb-4px outline-none") }),
        React__default.createElement("div", { className: cn("bg-base-white font-normal text-14px text-blue-gray-700", "flex items-center justify-between", "py-6px px-12px rounded-12px border-0 cursor-pointer", {
                // If using virtual focus, apply focused styles to the item when the user is interacting with keyboard modality
                "outline-none bg-blue-50 text-light-blue-gray-700 font-semibold border-0": shouldUseVirtualFocus || (isFocused && isKeyboardModality),
                "is-disabled": isDisabled,
                " bg-blue-50  text-light-blue-gray-700 font-semibold border-0 outline-none": isSelected,
                "border-0 outline-none": state.selectionManager.selectionMode !== "none",
                // When shouldFocusOnHover is false, apply hover styles both when hovered with the mouse.
                // Otherwise, apply hover styles when focused using non-keyboard modality.
                "bg-blue-50 border-0 outline-none": (isHovered && !shouldFocusOnHover) ||
                    (isFocused && !isKeyboardModality),
            }) },
            React__default.createElement(ClearSlots, null,
                React__default.createElement(SlotProvider, { slots: {
                        text: __assign({}, labelProps),
                        icon: {
                            size: "S",
                        },
                        description: __assign({}, descriptionProps),
                    } },
                    contents,
                    isSelected && React__default.createElement(TickIcon, { className: "flex-shrink-0" }))))))));
}

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
function useVirtualizerItem(options) {
    var _a = options.reusableView, layoutInfo = _a.layoutInfo, virtualizer = _a.virtualizer, ref = options.ref;
    var updateSize = useCallback(function () {
        var size = getSize(ref === null || ref === void 0 ? void 0 : ref.current);
        virtualizer.updateItemSize(layoutInfo === null || layoutInfo === void 0 ? void 0 : layoutInfo.key, size);
    }, [virtualizer, layoutInfo === null || layoutInfo === void 0 ? void 0 : layoutInfo.key, ref]);
    useLayoutEffect(function () {
        if (layoutInfo === null || layoutInfo === void 0 ? void 0 : layoutInfo.estimatedSize) {
            updateSize();
        }
    });
    return { updateSize: updateSize };
}
function getSize(node) {
    // Reset height before measuring so we get the intrinsic size
    var height = node.style.height;
    node.style.height = "";
    var size = new Size(node.scrollWidth, node.scrollHeight);
    node.style.height = height;
    return size;
}

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
/** @private */
function ListBoxSection(props) {
    var children = props.children, reusableView = props.reusableView, header = props.header;
    var item = reusableView.content;
    var _a = useListBoxSection({
        heading: item.rendered,
        "aria-label": item["aria-label"],
    }), headingProps = _a.headingProps, groupProps = _a.groupProps;
    var separatorProps = useSeparator({
        elementType: "li",
    }).separatorProps;
    var headerRef = useRef();
    useVirtualizerItem({
        reusableView: header,
        ref: headerRef,
    });
    var direction = useLocale().direction;
    var state = useContext(ListBoxContext);
    return (React__default.createElement(Fragment, null,
        React__default.createElement("div", { role: "presentation", ref: headerRef, style: layoutInfoToStyle(header.layoutInfo, direction) },
            item.key !== state.collection.getFirstKey() && (React__default.createElement("div", __assign({}, separatorProps, { className: cn("spectrum-Menu-divider") }))),
            item.rendered && (React__default.createElement("div", __assign({}, headingProps, { className: cn("spectrum-Menu-sectionHeading") }), item.rendered))),
        React__default.createElement("div", __assign({}, groupProps, { style: layoutInfoToStyle(reusableView.layoutInfo, direction), className: cn("spectrum-Menu") }), children)));
}

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
/** @private */
function useListBoxLayout(state, options) {
    if (options === void 0) { options = {}; }
    var collator = useCollator({ usage: "search", sensitivity: "base" });
    var layout = useMemo(function () {
        return new ListLayout(__assign({ estimatedRowHeight: 37, estimatedHeadingHeight: 33, padding: 5, loaderHeight: 40, placeholderHeight: 160, collator: collator }, options));
    }, __spreadArray([collator], __read(Object.values(options)), false));
    layout.collection = state.collection;
    layout.disabledKeys = state.disabledKeys;
    return layout;
}
/** @private */
function ListBoxBase(props, ref) {
    var layout = props.layout, state = props.state, shouldSelectOnPressUp = props.shouldSelectOnPressUp, focusOnPointerEnter = props.focusOnPointerEnter, shouldUseVirtualFocus = props.shouldUseVirtualFocus, _a = props.domProps, domProps = _a === void 0 ? {} : _a, _b = props.transitionDuration, transitionDuration = _b === void 0 ? 0 : _b, onScroll = props.onScroll, customOption = props.customOption;
    var listBoxProps = useListBox(__assign(__assign({}, props), { keyboardDelegate: layout, isVirtualized: true, autoFocus: false }), state, ref).listBoxProps;
    // Sync loading state into the layout.
    layout.isLoading = props.isLoading ? props.isLoading : false;
    var renderWrapper = function (parent, reusableView, children, renderChildren) {
        if (reusableView.viewType === "section") {
            return (React__default.createElement(ListBoxSection, { key: reusableView.key, reusableView: reusableView, 
                //@ts-ignore
                header: children.find(function (c) { return c.viewType === "header"; }) }, renderChildren(children.filter(function (c) { return c.viewType === "item"; }))));
        }
        return (React__default.createElement(VirtualizerItem, { key: reusableView.key, reusableView: reusableView, parent: parent }));
    };
    return (React__default.createElement(ListBoxContext.Provider, { value: state },
        React__default.createElement(Virtualizer, __assign({}, mergeProps(listBoxProps, domProps), { ref: ref, focusedKey: state.selectionManager.focusedKey, sizeToFit: "height", scrollDirection: "vertical", className: cn("spectrum-Menu"), layout: layout, collection: state.collection, 
            //@ts-ignore
            renderWrapper: renderWrapper, transitionDuration: transitionDuration, isLoading: props.isLoading, onLoadMore: props.onLoadMore, shouldUseVirtualFocus: shouldUseVirtualFocus, onScroll: onScroll }), function (type, item) {
            if (type === "item") {
                return (React__default.createElement(ListBoxOption, { item: item, shouldSelectOnPressUp: shouldSelectOnPressUp, shouldFocusOnHover: focusOnPointerEnter, shouldUseVirtualFocus: shouldUseVirtualFocus, customOption: customOption }));
            }
            else if (type === "loader") {
                return (
                // aria-selected isn't needed here since this option is not selectable.
                // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                React__default.createElement("div", { role: "option", style: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                    } },
                    React__default.createElement(ProgressCircle, { isIndeterminate: true, size: "S", "aria-label": state.collection.size > 0 ? "loadingMore" : "loading", UNSAFE_className: cn("spectrum-Dropdown-progressCircle") })));
            }
            else if (type === "placeholder") {
                var emptyState = props.renderEmptyState
                    ? props.renderEmptyState()
                    : null;
                if (emptyState == null) {
                    return null;
                }
                return (React__default.createElement("div", { 
                    // aria-selected isn't needed here since this option is not selectable.
                    // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                    role: "option", style: { height: "200px" } }, emptyState));
            }
        })));
}
// forwardRef doesn't support generic parameters, so cast the result to the correct type
// https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref
//@ts-ignore
var _ListBoxBase = React__default.forwardRef(ListBoxBase);

var OPEN_STATES = {
    entering: false,
    entered: true,
};
function OpenTransition(props) {
    return (React__default.createElement(Transition, __assign({ timeout: { enter: 0, exit: 350 } }, props), function (state) {
        return React__default.Children.map(props.children, function (child) {
            return child && React__default.cloneElement(child, { isOpen: !!OPEN_STATES[state] });
        });
    }));
}

function useColorScheme(theme, defaultColorScheme) {
    var matchesDark = useMediaQuery("(prefers-color-scheme: dark)");
    var matchesLight = useMediaQuery("(prefers-color-scheme: light)");
    if (theme && theme.dark && matchesDark) {
        return "dark";
    }
    if (theme && theme.light && matchesLight) {
        return "light";
    }
    if (theme && theme.dark && defaultColorScheme === "dark") {
        return "dark";
    }
    if (theme && theme.light && defaultColorScheme === "light") {
        return "light";
    }
    if (theme && !theme.dark) {
        return "light";
    }
    if (theme && !theme.light) {
        return "dark";
    }
    return "light";
}
function useScale(theme) {
    var matchesFine = useMediaQuery("(any-pointer: fine)");
    if (matchesFine && theme && theme.medium) {
        return "medium";
    }
    if (theme && theme.large) {
        return "large";
    }
    return "medium";
}

var Context = React__default.createContext(null);
Context.displayName = "ProviderContext";
var DEFAULT_BREAKPOINTS = { S: 640, M: 768, L: 1024, XL: 1280, XXL: 1536 };
function Provider(props, ref) {
    var prevContext = useProvider();
    var prevBreakpoints = prevContext && prevContext.breakpoints;
    var _a = props.theme, theme = _a === void 0 ? prevContext && prevContext.theme : _a, defaultColorScheme = props.defaultColorScheme;
    var autoColorScheme = useColorScheme(theme, defaultColorScheme);
    var autoScale = useScale(theme);
    useLocale().locale;
    var _b = props.colorScheme, colorScheme = _b === void 0 ? autoColorScheme : _b, _c = props.scale, scale = _c === void 0 ? prevContext ? prevContext.scale : autoScale : _c; props.locale; var _e = props.breakpoints, breakpoints = _e === void 0 ? prevContext ? prevBreakpoints : DEFAULT_BREAKPOINTS : _e, children = props.children, isQuiet = props.isQuiet, isEmphasized = props.isEmphasized, isDisabled = props.isDisabled, isRequired = props.isRequired, isReadOnly = props.isReadOnly, validationState = props.validationState, otherProps = __rest(props, ["colorScheme", "scale", "locale", "breakpoints", "children", "isQuiet", "isEmphasized", "isDisabled", "isRequired", "isReadOnly", "validationState"]);
    var currentProps = {
        theme: theme,
        breakpoints: breakpoints,
        colorScheme: colorScheme,
        scale: scale,
        isQuiet: isQuiet,
        isEmphasized: isEmphasized,
        isDisabled: isDisabled,
        isRequired: isRequired,
        isReadOnly: isReadOnly,
        validationState: validationState,
    };
    var filteredProps = {};
    Object.entries(currentProps).forEach(function (_a) {
        var _b = __read(_a, 2), key = _b[0], value = _b[1];
        return value !== undefined && (filteredProps[key] = value);
    });
    var context = Object.assign({}, prevContext, filteredProps);
    var contents = children;
    var domProps = filterDOMProps(otherProps);
    var styleProps = useStyleProps(otherProps, undefined).styleProps;
    if (!prevContext ||
        props.locale ||
        theme !== prevContext.theme ||
        colorScheme !== prevContext.colorScheme ||
        scale !== prevContext.scale ||
        Object.keys(domProps).length > 0 ||
        otherProps.UNSAFE_className ||
        Object.keys(styleProps.style).length >
            0) {
        contents = (React__default.createElement(ProviderWrapper, __assign({}, props, { UNSAFE_style: __assign({ isolation: !prevContext ? "isolate" : undefined }, styleProps.style), ref: ref }), contents));
    }
    return (React__default.createElement(Context.Provider, { value: context },
        React__default.createElement(ModalProvider, null, contents)));
}
var _Provider = React__default.forwardRef(Provider);
var ProviderWrapper = React__default.forwardRef(function ProviderWrapper(props, ref) {
    var _a;
    var _b, _c;
    var children = props.children, otherProps = __rest(props, ["children"]);
    var _d = useLocale(), locale = _d.locale, direction = _d.direction;
    var _e = useProvider(), theme = _e.theme, colorScheme = _e.colorScheme, scale = _e.scale;
    var modalProviderProps = useModalProvider().modalProviderProps;
    var styleProps = useStyleProps(otherProps).styleProps;
    var domRef = useDOMRef(ref);
    var themeKey = Object.keys(theme[colorScheme])[0];
    var scaleKey = Object.keys(theme[scale])[0];
    var className = clsx(styleProps.className, theme[colorScheme][themeKey], theme[scale][scaleKey], theme.global ? Object.values(theme.global) : null, (_a = {
            "react-spectrum-provider": shouldKeepSpectrumClassNames,
            spectrum: shouldKeepSpectrumClassNames
        },
        _a[themeKey] = shouldKeepSpectrumClassNames,
        _a[scaleKey] = shouldKeepSpectrumClassNames,
        _a));
    var style = __assign(__assign({}, styleProps.style), { colorScheme: (_c = (_b = props.colorScheme) !== null && _b !== void 0 ? _b : colorScheme) !== null && _c !== void 0 ? _c : Object.keys(theme)
            .filter(function (k) { return k === "light" || k === "dark"; })
            .join(" ") });
    var hasWarned = useRef(false);
    useEffect(function () {
        if (direction && domRef.current) {
            var closestDir = domRef.current.parentElement &&
                domRef.current.parentElement.closest("[dir]");
            var dir = closestDir && closestDir.getAttribute("dir");
            if (dir && dir !== direction && !hasWarned.current) {
                console.warn("Language directions cannot be nested. " + direction + " inside " + dir + ".");
                hasWarned.current = true;
            }
        }
    }, [direction, domRef, hasWarned]);
    return (React__default.createElement("div", __assign({}, filterDOMProps(otherProps), styleProps, modalProviderProps, { className: className, style: style, lang: locale, dir: direction, ref: domRef }), children));
});
function useProvider() {
    return useContext(Context);
}

function Overlay(props, ref) {
    var children = props.children, isOpen = props.isOpen, container = props.container, onEnter = props.onEnter, onEntering = props.onEntering, onEntered = props.onEntered, onExit = props.onExit, onExiting = props.onExiting, onExited = props.onExited;
    var _a = __read(useState(!isOpen), 2), exited = _a[0], setExited = _a[1];
    var handleEntered = useCallback(function () {
        setExited(false);
        if (onEntered) {
            onEntered();
        }
    }, [onEntered]);
    var handleExited = useCallback(function () {
        setExited(true);
        if (onExited) {
            onExited();
        }
    }, [onExited]);
    var mountOverlay = isOpen || !exited;
    if (!mountOverlay) {
        return null;
    }
    var contents = (React__default.createElement(_Provider, { ref: ref, UNSAFE_style: { background: "transparent", isolation: "isolate" }, theme: theme },
        React__default.createElement(OpenTransition, { in: isOpen, appear: true, onExit: onExit, onExiting: onExiting, onExited: handleExited, onEnter: onEnter, onEntering: onEntering, onEntered: handleEntered }, children)));
    return ReactDOM.createPortal(contents, container || document.body);
}
var _Overlay = React__default.forwardRef(Overlay);

function Popover$1(props, ref) {
    var domRef = useDOMRef(ref);
    var onClose = props.onClose, children = props.children, popOverStyles = props.popOverStyles, others = __rest(props, ["onClose", "children", "popOverStyles"]);
    return (React.createElement(_Overlay, __assign({}, others),
        React.createElement(PopoverWrapper, __assign({}, others, { ref: domRef, onClose: onClose, isNonModal: false, popOverStyles: popOverStyles }), children)));
}
var PopoverWrapper = forwardRef(function (props, ref) {
    var children = props.children, _a = props.placement, placement = _a === void 0 ? "bottom" : _a; props.arrowProps; var isOpen = props.isOpen; props.hideArrow; props.shouldCloseOnBlur; props.isKeyboardDismissDisabled; var isNonModal = props.isNonModal; props.isDismissable; var popOverStyles = props.popOverStyles, onClose = props.onClose, otherProps = __rest(props, ["children", "placement", "arrowProps", "isOpen", "hideArrow", "shouldCloseOnBlur", "isKeyboardDismissDisabled", "isNonModal", "isDismissable", "popOverStyles", "onClose"]);
    var overlayProps = useOverlay(__assign(__assign({}, props), { isDismissable: isOpen }), ref).overlayProps;
    var modalProps = useModal({
        isDisabled: isNonModal,
    }).modalProps;
    return (React.createElement("div", __assign({}, mergeProps(otherProps, overlayProps, modalProps), { className: cn("bg-base-white rounded-lg absolute left-0 border-blue-gray-200 border-1 shadow-xl overflow-y-auto ", popOverStyles, "popover-animation", "popover-animation--" + placement, {
            "is-open": isOpen,
        }), ref: ref }),
        children,
        React.createElement(DismissButton, { onDismiss: onClose })));
});
var _Popover = forwardRef(Popover$1);

function Underlay(_a) {
    _a.isOpen;
    return React__default.createElement("div", null);
}

function Tray(props, ref) {
    var children = props.children, onClose = props.onClose, isFixedHeight = props.isFixedHeight, isNonModal = props.isNonModal, label = props.label, isSearchable = props.isSearchable, trayChildrenWrapperClassName = props.trayChildrenWrapperClassName, trayContainerClassName = props.trayContainerClassName, otherProps = __rest(props, ["children", "onClose", "isFixedHeight", "isNonModal", "label", "isSearchable", "trayChildrenWrapperClassName", "trayContainerClassName"]);
    var domRef = useDOMRef(ref);
    var styleProps = useStyleProps(props).styleProps;
    var _a = useOverlay(__assign(__assign({}, props), { isDismissable: true }), domRef), overlayProps = _a.overlayProps, underlayProps = _a.underlayProps;
    return (React__default.createElement(_Overlay, __assign({}, otherProps),
        React__default.createElement(Underlay, __assign({}, underlayProps)),
        React__default.createElement(TrayWrapper, __assign({}, styleProps, { onClose: onClose, isSearchable: isSearchable, ref: domRef, overlayProps: overlayProps, isFixedHeight: isFixedHeight, isNonModal: isNonModal, label: label, trayChildrenWrapperClassName: trayChildrenWrapperClassName, trayContainerClassName: trayContainerClassName }), children)));
}
var TrayWrapper = forwardRef(function (props, ref) {
    var children = props.children, isOpen = props.isOpen; props.isFixedHeight; var isNonModal = props.isNonModal, overlayProps = props.overlayProps; props.label; var _a = props.isSearchable, isSearchable = _a === void 0 ? true : _a, trayContainerClassName = props.trayContainerClassName, trayChildrenWrapperClassName = props.trayChildrenWrapperClassName, otherProps = __rest(props, ["children", "isOpen", "isFixedHeight", "isNonModal", "overlayProps", "label", "isSearchable", "trayContainerClassName", "trayChildrenWrapperClassName"]);
    usePreventScroll();
    var modalProps = useModal({
        isDisabled: isNonModal,
    }).modalProps;
    var viewport = useViewportSize();
    var _b = __read(useState(viewport.height), 2), height = _b[0], setHeight = _b[1];
    var timeoutRef = useRef();
    useEffect(function () {
        clearTimeout(timeoutRef.current);
        if (viewport.height < height && viewport.height < window.innerHeight) {
            timeoutRef.current = setTimeout(function () {
                setHeight(viewport.height);
            }, 500);
        }
        else {
            setHeight(viewport.height);
        }
        var trayContainer = document.getElementById("tray-container");
        trayContainer === null || trayContainer === void 0 ? void 0 : trayContainer.style.setProperty("--viewport-height", height + "px");
    }, [height, viewport.height]);
    var domProps = mergeProps(otherProps, overlayProps);
    return (React__default.createElement("div", { style: { height: height }, className: cn("fixed bottom-0 flex flex-col-reverse right-0 left-0 top-0 bg-blue-gray-600 bg-opacity-80 z-l10 w-full", { "fade-in-class": isOpen }, { "fade-out-class": !isOpen }, trayContainerClassName), id: "tray-container" },
        React__default.createElement("div", __assign({}, domProps, modalProps, { ref: ref, "data-testid": "tray", className: cn("bg-base-white relative w-full flex flex-col rounded-t-16px spectrum-tray", isSearchable
                ? "searchable-spectrum-tray-height"
                : "default-spectrum-tray-height", { "is-open": isOpen }, trayChildrenWrapperClassName) }), children)));
});
var _Tray = forwardRef(Tray);

var PopoverStyles = {
    secondary: "secondary",
};

var getWrapperWithPopOverStyles = function (isFocusWithin, error, isDisabled, wrapperWithPopOverStyles, label) {
    return cn(label ? "flex flex-col flex-grow mt-1" : "flex flex-col flex-grow", "border-1 border-blue-gray-300 dark:border-gray-700 bg-base-white rounded-lg", "text-sm  shadow-small1", "overflow-hidden", "" + (isFocusWithin ? "border-blue-600 border-1" : ""), "" + (error && "border-red-400 border-1"), "" + (isDisabled && "pointer-events-none bg-blue-gray-100 shadow-none"), wrapperWithPopOverStyles);
};
var getPickerInputFieldStyles = function (state, error, inputFieldStyles) {
    return cn("truncate text-sm leading-5 tracking-normal not-italic", "" + (state.selectedItem ? "text-blue-gray-900" : "text-blue-gray-500"), "" + (error && "text-red-800"), inputFieldStyles);
};
var isOverlayOpen = function (_a) {
    var state = _a.state, isMobileDevice = _a.isMobileDevice, isMobile = _a.isMobile, isDesktop = _a.isDesktop;
    if (isMobileDevice) {
        if (isDesktop) {
            return state.isOpen;
        }
        return false;
    }
    else {
        if (isMobile) {
            return false;
        }
        return state.isOpen;
    }
};

var DropDownIcon$1 = function (props) {
    var _a = props.fill, fill = _a === void 0 ? colors.blueGray500 : _a, rest = __rest(props, ["fill"]);
    return (React.createElement("svg", __assign({ width: 12, height: 8, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("path", { d: "M10.667 1 6 5.667 1.333 1", stroke: fill, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" })));
};

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  default: DropDownIcon$1
});

var PickerButton = function (props, ref) {
    props = useSlotProps(props, "button");
    var buttonStyles = props.buttonStyles, state = props.state, triggerProps = props.triggerProps, renderValueContainer = props.renderValueContainer, valueProps = props.valueProps, inputFieldStyles = props.inputFieldStyles, error = props.error, getCustomLabelForSelectedOption = props.getCustomLabelForSelectedOption, placeholder = props.placeholder, hoverProps = props.hoverProps, focusProps = props.focusProps;
    var domRef = useFocusableRef(ref);
    var buttonProps = useButton(__assign(__assign({}, triggerProps), { onPress: function (e) {
            setTimeout(function () {
                triggerProps.onPress && triggerProps.onPress(e);
            }, 100);
        } }), domRef).buttonProps;
    return (React__default.createElement("button", __assign({ className: cn("flex items-center justify-between focus:outline-none py-2 pl-3 pr-2 w-full", buttonStyles) }, mergeProps(buttonProps, hoverProps, focusProps), { ref: domRef }),
        state.selectedItem && renderValueContainer ? (renderValueContainer(state.selectedItem.value, valueProps)) : (React__default.createElement("span", __assign({ className: getPickerInputFieldStyles(state, error, inputFieldStyles) }, valueProps), state.selectedItem
            ? getCustomLabelForSelectedOption
                ? getCustomLabelForSelectedOption(state.selectedItem)
                : state.selectedItem.value.label
            : placeholder)),
        state.isOpen ? (React__default.createElement(DropDownIcon$1, { style: { transform: "rotate(180deg)", transition: "transform 0.5s" } })) : (React__default.createElement(DropDownIcon$1, { style: { transition: "transform 0.5s" } }))));
};
var _PickerButton = React__default.forwardRef(PickerButton);

var getPopoverPositionStylesFn$1 = function (positionProps, triggerRef) {
    var _a, _b, _c;
    return __assign(__assign({}, positionProps.style), { maxHeight: typeof ((_a = positionProps.style) === null || _a === void 0 ? void 0 : _a.maxHeight) === "string"
            ? parseInt(positionProps.style.maxHeight) + 6 //NOTE: Added this to remove the scroll in listbox
            : ((_b = positionProps.style) === null || _b === void 0 ? void 0 : _b.maxHeight)
                ? positionProps.style.maxHeight + 6
                : undefined, paddingTop: "2px", paddingBottom: "2px", width: triggerRef ? (_c = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth : 0 });
};
function PickerComponent(props, _ref) {
    var _a;
    var error = props.error, fieldContainerStyles = props.fieldContainerStyles, labelStyles = props.labelStyles, wrapperWithPopOverStyles = props.wrapperWithPopOverStyles, inputGroupStyles = props.inputGroupStyles, inputFieldStyles = props.inputFieldStyles, buttonStyles = props.buttonStyles; props.errorMessageStyles; var DropDownIcon = props.dropDownIcon, popOverStyles = props.popOverStyles; props.listBoxStyles; props.listItemStyles; props.selectedListItemStyles; props.itemContentStyles; var placeholder = props.placeholder, _b = props.showPickerLabelInDesktopView, showPickerLabelInDesktopView = _b === void 0 ? true : _b, popOverStyle = props.popOverStyle, _c = props.autoFocus, autoFocus = _c === void 0 ? false : _c, isDisabled = props.isDisabled, getCustomLabelForSelectedOption = props.getCustomLabelForSelectedOption, isMobile = props.isMobile, wrapperComponent = props.wrapperComponent, isDesktop = props.isDesktop, handleFocus = props.handleFocus, renderValueContainer = props.renderValueContainer, isRequired = props.isRequired, helperTextStyles = props.helperTextStyles; props.isSearchable; props.noOptionsMessage; props.searchInputPlaceholder; var helperText = props.helperText, shouldShowHint = props.shouldShowHint, items = props.items, CustomTrigger = props.customTrigger, _e = props.overlayOptions, overlayOptions = _e === void 0 ? {} : _e, _f = props.getPopoverPositionStyles, getPopoverPositionStyles = _f === void 0 ? getPopoverPositionStylesFn$1 : _f, label = props.label, CustomHeader = props.customHeader, _g = props.bottomSheetHeaderStyles, bottomSheetHeaderStyles = _g === void 0 ? "" : _g, trayChildrenWrapperClassName = props.trayChildrenWrapperClassName, trayContainerClassName = props.trayContainerClassName, customOption = props.customOption, _h = props.listboxLayoutOptions, listboxLayoutOptions = _h === void 0 ? {} : _h, name = props.name, _j = props.enableDataMask, enableDataMask = _j === void 0 ? false : _j;
    var popoverRef = useRef(null);
    var unwrappedPopoverRef = useUnwrapDOMRef(popoverRef);
    var triggerRef = useRef(null);
    var unwrappedTriggerRef = useUnwrapDOMRef(triggerRef);
    var listboxRef = useRef(null);
    var state = useSelectState(props);
    var layout = useListBoxLayout(state, listboxLayoutOptions);
    var _k = useSelect(__assign(__assign({}, props), { keyboardDelegate: layout }), state, unwrappedTriggerRef), triggerProps = _k.triggerProps, valueProps = _k.valueProps, menuProps = _k.menuProps;
    var isMobileDevice = useMediaQuery$1({
        query: "(max-width: 768px)",
    });
    var _l = useOverlayPosition(__assign({ targetRef: unwrappedTriggerRef, overlayRef: unwrappedPopoverRef, scrollRef: listboxRef, offset: 0, isOpen: isOverlayOpen({ isMobileDevice: isMobileDevice, state: state, isMobile: isMobile, isDesktop: isDesktop }), placement: positionConstants.bottomStart, onClose: state.close }, overlayOptions)), positionProps = _l.overlayProps, placement = _l.placement;
    layout.collection = state.collection;
    layout.disabledKeys = state.disabledKeys;
    var _m = __read(React__default.useState(false), 2), isFocusWithin = _m[0], setFocusWithin = _m[1];
    var focusProps = useFocus({
        onBlur: function () { },
        onFocusChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
        onFocus: function (e) { return handleFocus && handleFocus(e); },
    }).focusProps;
    function focusStateRingColor(color) {
        switch (color) {
            case colors$1.indigo:
                return "ring-indigo-200";
            default:
                return "ring-blue-200";
        }
    }
    var getPopoverStyles = function () {
        var secondary = PopoverStyles.secondary;
        var defaultStyles = "flex flex-col justify-between";
        switch (popOverStyle) {
            case secondary:
                return "static " + defaultStyles;
            default:
                return "relative " + defaultStyles;
        }
    };
    var onClickRequestClose = function () {
        document.body.style.overflowY = "auto";
        state.close();
    };
    var hoverProps = useHover({ isDisabled: isDisabled }).hoverProps;
    var WrapperComponent = wrapperComponent;
    var styles = getPopoverPositionStyles(positionProps, unwrappedTriggerRef);
    var listBox = (React__default.createElement(FocusScope, { restoreFocus: true, autoFocus: true },
        React__default.createElement(_ListBoxBase, __assign({}, menuProps, { items: items, state: state, ref: listboxRef, layout: layout, shouldSelectOnPressUp: true, focusOnPointerEnter: true, UNSAFE_style: { maxHeight: (_a = positionProps.style) === null || _a === void 0 ? void 0 : _a.maxHeight }, UNSAFE_className: cn("outline-none"), customOption: customOption }))));
    var popover = (React__default.createElement(_Popover, { isOpen: state.isOpen, onClose: state.close, popOverStyles: popOverStyles, ref: popoverRef, style: styles, placement: placement, shouldCloseOnBlur: true }, wrapperComponent ? (React__default.createElement(WrapperComponent, __assign({}, menuProps, { state: state }))) : (listBox)));
    var header = (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: cn("px-12px py-6px flex items-center justify-between mx-8px mt-8px", bottomSheetHeaderStyles) },
            React__default.createElement("p", { className: cn("uppercase text-blue-gray-500 font-semibold text-xs") }, label),
            React__default.createElement("div", { className: cn("cursor-pointer"), onClick: state.close },
                React__default.createElement(CrossIcon, { fill: "#94A3B8" }))),
        React__default.createElement("div", { className: cn("bg-blue-gray-200 h-px m-8px") })));
    var tray = (React__default.createElement(_Tray, { isOpen: state.isOpen, onClose: onClickRequestClose, label: props.label, isSearchable: false, trayChildrenWrapperClassName: trayChildrenWrapperClassName, trayContainerClassName: trayContainerClassName }, wrapperComponent ? (React__default.createElement(WrapperComponent, __assign({}, menuProps, { state: state }))) : (React__default.createElement(React__default.Fragment, null,
        label && CustomHeader ? (React__default.createElement(CustomHeader, { onClose: state.close })) : (header),
        listBox))));
    var returnOverlay = function () {
        if (isMobile) {
            return tray;
        }
        else if (isDesktop) {
            return popover;
        }
        else {
            return (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(MediaQuery, { minWidth: 769 }, popover),
                React__default.createElement(MediaQuery, { maxWidth: 768 }, tray)));
        }
    };
    var hintId = useId();
    return (React__default.createElement("div", { className: cn(getPopoverStyles(), fieldContainerStyles, "flex-col") },
        showPickerLabelInDesktopView && (React__default.createElement(Label, { labelClassName: labelStyles, isRequired: isRequired }, props.label)),
        React__default.createElement(FocusRing, { autoFocus: autoFocus, within: true, focusClassProps: "ring-offset-0 " + (error
                ? "ring-0 border-1 border-red-400"
                : isFocusWithin
                    ? "ring " + focusStateRingColor(colors$1.blue)
                    : "") },
            React__default.createElement("div", { className: getWrapperWithPopOverStyles(isFocusWithin, error, isDisabled, wrapperWithPopOverStyles) },
                React__default.createElement("div", __assign({ className: cn("flex", inputGroupStyles) }, getDataMaskAttribute(enableDataMask)),
                    React__default.createElement(HiddenSelect, { state: state, triggerRef: unwrappedTriggerRef, label: props.label, name: name }),
                    React__default.createElement(PressResponder, __assign({}, mergeProps(hoverProps, triggerProps)), CustomTrigger ? (React__default.createElement(CustomTrigger, { buttonStyles: buttonStyles, state: state, triggerProps: triggerProps, ref: triggerRef, renderValueContainer: renderValueContainer, valueProps: valueProps, inputFieldStyles: inputFieldStyles, error: error, getCustomLabelForSelectedOption: getCustomLabelForSelectedOption, placeholder: placeholder, hoverProps: hoverProps, focusProps: focusProps })) : (React__default.createElement(_PickerButton, { buttonStyles: buttonStyles, state: state, triggerProps: triggerProps, ref: triggerRef, renderValueContainer: renderValueContainer, valueProps: valueProps, inputFieldStyles: inputFieldStyles, error: error, getCustomLabelForSelectedOption: getCustomLabelForSelectedOption, placeholder: placeholder, hoverProps: hoverProps, focusProps: focusProps },
                        state.selectedItem && renderValueContainer ? (renderValueContainer(state.selectedItem.value, valueProps)) : (React__default.createElement("span", __assign({ className: getPickerInputFieldStyles(state, error, inputFieldStyles) }, valueProps), state.selectedItem
                            ? getCustomLabelForSelectedOption
                                ? getCustomLabelForSelectedOption(state.selectedItem)
                                : //@ts-ignore
                                    state.selectedItem.value.label
                            : placeholder)),
                        React__default.createElement(MediaQuery, { minWidth: 769 }, state.isOpen ? (React__default.createElement(DropDownIcon, { style: {
                                transform: "rotate(180deg)",
                                transition: "transform 0.5s",
                            } })) : (React__default.createElement(DropDownIcon, { style: {
                                transition: "transform 0.5s",
                            } }))),
                        React__default.createElement(MediaQuery, { maxWidth: 768 },
                            React__default.createElement(DropDownIcon, null)))))))),
        shouldShowHint && (React__default.createElement(Hint, { id: hintId, hint: helperText, errorText: error ? error : "", hintClassName: helperTextStyles })),
        returnOverlay()));
}
var _Picker = React__default.forwardRef(PickerComponent);

var ListElement = function (props) {
    var label = props.item.label;
    return React__default.createElement("span", null, label);
};

// NOTE: extended errors do not gave message or stack trace when using babel
var InternationalizationNotSupportedError = /** @class */ (function (_super) {
    __extends(InternationalizationNotSupportedError, _super);
    function InternationalizationNotSupportedError(message) {
        var _newTarget = this.constructor;
        var _this = this;
        var trueProto = _newTarget.prototype;
        _this = _super.call(this, message) || this;
        _this.__proto__ = trueProto;
        _this.name = "InternationalizationNotSupportedError";
        return _this;
    }
    return InternationalizationNotSupportedError;
}(Error));

var Picker = function (props) {
    //NOTE: react-aria supports Internalization (uses Intl.PluralRules) which is only supported in iOS >=13
    if (typeof Intl.PluralRules !== "function") {
        throw new InternationalizationNotSupportedError("Internationalization is not supported");
    }
    var label = props.label, options = props.options, placeholder = props.placeholder, onBlur = props.onBlur, error = props.error, getListItemKey = props.getListItemKey, getListItemValue = props.getListItemValue, RenderListView = props.renderListView, _a = props.dropDownIcon, dropDownIcon = _a === void 0 ? DropDownIcon$1 : _a, _b = props.selectedOptionIcon, selectedOptionIcon = _b === void 0 ? TickIcon : _b, isDisabled = props.isDisabled, getCustomLabelForSelectedOption = props.getCustomLabelForSelectedOption, customOption = props.customOption, isMobile = props.isMobile, headerClassName = props.headerClassName, containerClassName = props.containerClassName, wrapperComponent = props.wrapperComponent, hasHeader = props.hasHeader, hideCloseIcon = props.hideCloseIcon, isDesktop = props.isDesktop, mobileListBoxStyles = props.mobileListBoxStyles, handleFocus = props.handleFocus, isRequired = props.isRequired, helperText = props.helperText, helperTextStyles = props.helperTextStyles, isSearchable = props.isSearchable, noOptionsMessage = props.noOptionsMessage, searchInputPlaceholder = props.searchInputPlaceholder, _c = props.shouldShowHint, shouldShowHint = _c === void 0 ? true : _c, listboxLayoutOptions = props.listboxLayoutOptions, getPopoverPositionStyles = props.getPopoverPositionStyles; props.name; var others = __rest(props, ["label", "options", "placeholder", "onBlur", "error", "getListItemKey", "getListItemValue", "renderListView", "dropDownIcon", "selectedOptionIcon", "isDisabled", "getCustomLabelForSelectedOption", "customOption", "isMobile", "headerClassName", "containerClassName", "wrapperComponent", "hasHeader", "hideCloseIcon", "isDesktop", "mobileListBoxStyles", "handleFocus", "isRequired", "helperText", "helperTextStyles", "isSearchable", "noOptionsMessage", "searchInputPlaceholder", "shouldShowHint", "listboxLayoutOptions", "getPopoverPositionStyles", "name"]);
    return (React__default.createElement(_Picker, __assign({ label: label, placeholder: placeholder, onBlur: onBlur, items: options, error: error, dropDownIcon: dropDownIcon, selectedOptionIcon: selectedOptionIcon, getCustomLabelForSelectedOption: getCustomLabelForSelectedOption, isDisabled: isDisabled, customOption: customOption, headerClassName: headerClassName, containerClassName: containerClassName, isMobile: isMobile, wrapperComponent: wrapperComponent, hasHeader: hasHeader, hideCloseIcon: hideCloseIcon, isDesktop: isDesktop, mobileListBoxStyles: mobileListBoxStyles, handleFocus: handleFocus, isRequired: isRequired, helperText: helperText, helperTextStyles: helperTextStyles, isSearchable: isSearchable, noOptionsMessage: noOptionsMessage, searchInputPlaceholder: searchInputPlaceholder, shouldShowHint: shouldShowHint, listboxLayoutOptions: listboxLayoutOptions, getPopoverPositionStyles: getPopoverPositionStyles }, others), function (eachItem) { return (React__default.createElement(Item, { key: getListItemKey(eachItem), textValue: getListItemValue(eachItem) }, RenderListView ? (React__default.createElement(RenderListView, { item: eachItem })) : (React__default.createElement(ListElement, { item: eachItem })))); }));
};

var TextArea = forwardRef(function (props, forwardedRef) {
    var _a;
    var label = props.label, _b = props.containerClassName, containerClassName = _b === void 0 ? "" : _b, _c = props.labelClassName, labelClassName = _c === void 0 ? "" : _c, _d = props.autoFocus, autoFocus = _d === void 0 ? false : _d, _e = props.isDisabled, isDisabled = _e === void 0 ? false : _e, _f = props.color, color = _f === void 0 ? colors$1.blue : _f, _g = props.inputContainerClassName, inputContainerClassName = _g === void 0 ? "" : _g, handleChange = props.handleChange, handleBlur = props.handleBlur, handleFocus = props.handleFocus, rows = props.rows, cols = props.cols, _h = props.type, type = _h === void 0 ? "textarea" : _h, _j = props.inputClassName, inputClassName = _j === void 0 ? "" : _j, hint = props.hint, errorMessage = props.errorMessage, hintClassName = props.hintClassName, isRequired = props.isRequired, _k = props.enableDataMask, enableDataMask = _k === void 0 ? false : _k;
    var hintId = useId();
    var _inputRef = useRef(null);
    var inputRef = forwardedRef || _inputRef;
    var _l = useTextField(__assign(__assign({}, props), { autoFocus: autoFocus, isDisabled: isDisabled, isReadOnly: isDisabled, type: type, validationState: !!errorMessage ? "invalid" : "valid" }), inputRef), labelProps = _l.labelProps, inputProps = _l.inputProps;
    var _m = __read(React__default.useState(false), 2), isFocusWithin = _m[0], setFocusWithin = _m[1];
    var focusWithinProps = useFocusWithin({
        isDisabled: isDisabled,
        onFocusWithinChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusWithinProps;
    var focusProps = useFocus({
        onBlur: function () { },
        onFocusChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusProps;
    function focusStateRingColor(color) {
        switch (color) {
            case colors$1.indigo:
                return "ring-indigo-200";
            default:
                return "ring-blue-200";
        }
    }
    function focusStateBorderColor(color) {
        switch (color) {
            case colors$1.indigo:
                return "border-indigo-600";
            default:
                return "border-blue-600";
        }
    }
    return (React__default.createElement("div", { className: cn("flex flex-col w-full", containerClassName) },
        label ? (React__default.createElement(Label, { labelProps: labelProps, labelClassName: labelClassName, isRequired: isRequired }, label)) : null,
        React__default.createElement(FocusRing, { autoFocus: autoFocus, within: true, focusClassProps: "ring-offset-0 " + (errorMessage
                ? "ring-0 border-1 border-red-400"
                : isFocusWithin
                    ? "ring " + focusStateRingColor(color)
                    : "") },
            React__default.createElement("div", __assign({}, focusWithinProps, { className: cn("flex flex-grow items-center", "border-1 border-blue-gray-300 dark:border-gray-700 bg-base-white rounded-lg", "text-sm", "overflow-hidden", inputContainerClassName, (_a = {
                        "bg-blue-gray-100 dark:bg-blue-gray-100": isDisabled,
                        "bg-white dark:bg-gray-900 shadow-small1": !isDisabled,
                        "cursor-not-allowed": isDisabled,
                        "border-1 border-red-400 dark:border-red-400 rounded-md bg-base-white": !!errorMessage
                    },
                    _a["border-1 " + focusStateBorderColor(color) + " rounded-md"] = isFocusWithin,
                    _a)) }),
                React__default.createElement("textarea", __assign({ ref: inputRef, "lens-role": "text-area-field" }, mergeProps(focusProps, inputProps), { className: cn("flex-grow-default input rounded-lg resize-none", "px-14px py-2", "focus:border-0 focus:rounded-md focus:outline-none", "text-blue-gray-900 text-sm leading-5 tracking-normal not-italic", "placeholder-blue-gray-500", {
                        disabled: isDisabled,
                    }, {
                        "placeholder-blue-gray-400": isDisabled,
                    }, {
                        "border-none rounded-none shadow-none text-red-800": !!errorMessage,
                    }, inputClassName), rows: rows, cols: cols, onBlur: function (e) {
                        setFocusWithin(false);
                        handleBlur && handleBlur(e);
                    }, onChange: function (e) { return handleChange && handleChange(e); }, onFocus: function (e) {
                        setFocusWithin(true);
                        handleFocus && handleFocus(e);
                    } }, getDataMaskAttribute(enableDataMask))))),
        React__default.createElement(Hint, { id: hintId, hint: hint, errorText: errorMessage ? errorMessage : "", hintClassName: hintClassName })));
});

var CountriesList = [
    ["Afghanistan (‫افغانستان‬‎)", "af", "93"],
    ["Albania (Shqipëri)", "al", "355"],
    ["Algeria (‫الجزائر‬‎)", "dz", "213"],
    ["American Samoa", "as", "1684"],
    ["Andorra", "ad", "376"],
    ["Angola", "ao", "244"],
    ["Anguilla", "ai", "1264"],
    ["Antigua and Barbuda", "ag", "1268"],
    ["Argentina", "ar", "54"],
    ["Armenia (Հայաստան)", "am", "374"],
    ["Aruba", "aw", "297"],
    ["Australia", "au", "61", 0],
    ["Austria (Österreich)", "at", "43"],
    ["Azerbaijan (Azərbaycan)", "az", "994"],
    ["Bahamas", "bs", "1242"],
    ["Bahrain (‫البحرين‬‎)", "bh", "973"],
    ["Bangladesh (বাংলাদেশ)", "bd", "880"],
    ["Barbados", "bb", "1246"],
    ["Belarus (Беларусь)", "by", "375"],
    ["Belgium (België)", "be", "32"],
    ["Belize", "bz", "501"],
    ["Benin (Bénin)", "bj", "229"],
    ["Bermuda", "bm", "1441"],
    ["Bhutan (འབྲུག)", "bt", "975"],
    ["Bolivia", "bo", "591"],
    ["Bosnia and Herzegovina (Босна и Херцеговина)", "ba", "387"],
    ["Botswana", "bw", "267"],
    ["Brazil (Brasil)", "br", "55"],
    ["British Indian Ocean Territory", "io", "246"],
    ["British Virgin Islands", "vg", "1284"],
    ["Brunei", "bn", "673"],
    ["Bulgaria (България)", "bg", "359"],
    ["Burkina Faso", "bf", "226"],
    ["Burundi (Uburundi)", "bi", "257"],
    ["Cambodia (កម្ពុជា)", "kh", "855"],
    ["Cameroon (Cameroun)", "cm", "237"],
    ["Canada", "ca", "1"],
    ["Cape Verde (Kabu Verdi)", "cv", "238"],
    ["Caribbean Netherlands", "bq", "599", 1],
    ["Cayman Islands", "ky", "1345"],
    ["Central African Republic (République centrafricaine)", "cf", "236"],
    ["Chad (Tchad)", "td", "235"],
    ["Chile", "cl", "56"],
    ["China (中国)", "cn", "86"],
    ["Christmas Island", "cx", "61", 2],
    ["Cocos (Keeling) Islands", "cc", "61", 1],
    ["Colombia", "co", "57"],
    ["Comoros (‫جزر القمر‬‎)", "km", "269"],
    ["Congo (DRC) (Jamhuri ya Kidemokrasia ya Kongo)", "cd", "243"],
    ["Congo (Republic) (Congo-Brazzaville)", "cg", "242"],
    ["Cook Islands", "ck", "682"],
    ["Costa Rica", "cr", "506"],
    ["Côte d’Ivoire", "ci", "225"],
    ["Croatia (Hrvatska)", "hr", "385"],
    ["Cuba", "cu", "53"],
    ["Curaçao", "cw", "599", 0],
    ["Cyprus (Κύπρος)", "cy", "357"],
    ["Czech Republic (Česká republika)", "cz", "420"],
    ["Denmark (Danmark)", "dk", "45"],
    ["Djibouti", "dj", "253"],
    ["Dominica", "dm", "1767"],
    [
        "Dominican Republic (República Dominicana)",
        "do",
        "1",
        2,
        ["809", "829", "849"],
    ],
    ["Ecuador", "ec", "593"],
    ["Egypt (‫مصر‬‎)", "eg", "20"],
    ["El Salvador", "sv", "503"],
    ["Equatorial Guinea (Guinea Ecuatorial)", "gq", "240"],
    ["Eritrea", "er", "291"],
    ["Estonia (Eesti)", "ee", "372"],
    ["Ethiopia", "et", "251"],
    ["Falkland Islands (Islas Malvinas)", "fk", "500"],
    ["Faroe Islands (Føroyar)", "fo", "298"],
    ["Fiji", "fj", "679"],
    ["Finland (Suomi)", "fi", "358", 0],
    ["France", "fr", "33"],
    ["French Guiana (Guyane française)", "gf", "594"],
    ["French Polynesia (Polynésie française)", "pf", "689"],
    ["Gabon", "ga", "241"],
    ["Gambia", "gm", "220"],
    ["Georgia (საქართველო)", "ge", "995"],
    ["Germany (Deutschland)", "de", "49"],
    ["Ghana (Gaana)", "gh", "233"],
    ["Gibraltar", "gi", "350"],
    ["Greece (Ελλάδα)", "gr", "30"],
    ["Greenland (Kalaallit Nunaat)", "gl", "299"],
    ["Grenada", "gd", "1473"],
    ["Guadeloupe", "gp", "590", 0],
    ["Guam", "gu", "1671"],
    ["Guatemala", "gt", "502"],
    ["Guernsey", "gg", "44", 1],
    ["Guinea (Guinée)", "gn", "224"],
    ["Guinea-Bissau (Guiné Bissau)", "gw", "245"],
    ["Guyana", "gy", "592"],
    ["Haiti", "ht", "509"],
    ["Honduras", "hn", "504"],
    ["Hong Kong (香港)", "hk", "852"],
    ["Hungary (Magyarország)", "hu", "36"],
    ["Iceland (Ísland)", "is", "354"],
    ["India (भारत)", "in", "91"],
    ["Indonesia", "id", "62"],
    ["Iran (‫ایران‬‎)", "ir", "98"],
    ["Iraq (‫العراق‬‎)", "iq", "964"],
    ["Ireland", "ie", "353"],
    ["Isle of Man", "im", "44", 2],
    ["Israel (‫ישראל‬‎)", "il", "972"],
    ["Italy (Italia)", "it", "39", 0],
    ["Jamaica", "jm", "1876"],
    ["Japan (日本)", "jp", "81"],
    ["Jersey", "je", "44", 3],
    ["Jordan (‫الأردن‬‎)", "jo", "962"],
    ["Kazakhstan (Казахстан)", "kz", "7", 1],
    ["Kenya", "ke", "254"],
    ["Kiribati", "ki", "686"],
    ["Kosovo", "xk", "383"],
    ["Kuwait (‫الكويت‬‎)", "kw", "965"],
    ["Kyrgyzstan (Кыргызстан)", "kg", "996"],
    ["Laos (ລາວ)", "la", "856"],
    ["Latvia (Latvija)", "lv", "371"],
    ["Lebanon (‫لبنان‬‎)", "lb", "961"],
    ["Lesotho", "ls", "266"],
    ["Liberia", "lr", "231"],
    ["Libya (‫ليبيا‬‎)", "ly", "218"],
    ["Liechtenstein", "li", "423"],
    ["Lithuania (Lietuva)", "lt", "370"],
    ["Luxembourg", "lu", "352"],
    ["Macau (澳門)", "mo", "853"],
    ["Macedonia (FYROM) (Македонија)", "mk", "389"],
    ["Madagascar (Madagasikara)", "mg", "261"],
    ["Malawi", "mw", "265"],
    ["Malaysia", "my", "60"],
    ["Maldives", "mv", "960"],
    ["Mali", "ml", "223"],
    ["Malta", "mt", "356"],
    ["Marshall Islands", "mh", "692"],
    ["Martinique", "mq", "596"],
    ["Mauritania (‫موريتانيا‬‎)", "mr", "222"],
    ["Mauritius (Moris)", "mu", "230"],
    ["Mayotte", "yt", "262", 1],
    ["Mexico (México)", "mx", "52"],
    ["Micronesia", "fm", "691"],
    ["Moldova (Republica Moldova)", "md", "373"],
    ["Monaco", "mc", "377"],
    ["Mongolia (Монгол)", "mn", "976"],
    ["Montenegro (Crna Gora)", "me", "382"],
    ["Montserrat", "ms", "1664"],
    ["Morocco (‫المغرب‬‎)", "ma", "212", 0],
    ["Mozambique (Moçambique)", "mz", "258"],
    ["Myanmar (Burma) (မြန်မာ)", "mm", "95"],
    ["Namibia (Namibië)", "na", "264"],
    ["Nauru", "nr", "674"],
    ["Nepal (नेपाल)", "np", "977"],
    ["Netherlands (Nederland)", "nl", "31"],
    ["New Caledonia (Nouvelle-Calédonie)", "nc", "687"],
    ["New Zealand", "nz", "64"],
    ["Nicaragua", "ni", "505"],
    ["Niger (Nijar)", "ne", "227"],
    ["Nigeria", "ng", "234"],
    ["Niue", "nu", "683"],
    ["Norfolk Island", "nf", "672"],
    ["North Korea (조선 민주주의 인민 공화국)", "kp", "850"],
    ["Northern Mariana Islands", "mp", "1670"],
    ["Norway (Norge)", "no", "47", 0],
    ["Oman (‫عُمان‬‎)", "om", "968"],
    ["Pakistan (‫پاکستان‬‎)", "pk", "92"],
    ["Palau", "pw", "680"],
    ["Palestine (‫فلسطين‬‎)", "ps", "970"],
    ["Panama (Panamá)", "pa", "507"],
    ["Papua New Guinea", "pg", "675"],
    ["Paraguay", "py", "595"],
    ["Peru (Perú)", "pe", "51"],
    ["Philippines", "ph", "63"],
    ["Poland (Polska)", "pl", "48"],
    ["Portugal", "pt", "351"],
    ["Puerto Rico", "pr", "1", 3, ["787", "939"]],
    ["Qatar (‫قطر‬‎)", "qa", "974"],
    ["Réunion (La Réunion)", "re", "262", 0],
    ["Romania (România)", "ro", "40"],
    ["Russia (Россия)", "ru", "7", 0],
    ["Rwanda", "rw", "250"],
    ["Saint Barthélemy (Saint-Barthélemy)", "bl", "590", 1],
    ["Saint Helena", "sh", "290"],
    ["Saint Kitts and Nevis", "kn", "1869"],
    ["Saint Lucia", "lc", "1758"],
    ["Saint Martin (Saint-Martin (partie française))", "mf", "590", 2],
    ["Saint Pierre and Miquelon (Saint-Pierre-et-Miquelon)", "pm", "508"],
    ["Saint Vincent and the Grenadines", "vc", "1784"],
    ["Samoa", "ws", "685"],
    ["San Marino", "sm", "378"],
    ["São Tomé and Príncipe (São Tomé e Príncipe)", "st", "239"],
    ["Saudi Arabia (‫المملكة العربية السعودية‬‎)", "sa", "966"],
    ["Senegal (Sénégal)", "sn", "221"],
    ["Serbia (Србија)", "rs", "381"],
    ["Seychelles", "sc", "248"],
    ["Sierra Leone", "sl", "232"],
    ["Singapore", "sg", "65"],
    ["Sint Maarten", "sx", "1721"],
    ["Slovakia (Slovensko)", "sk", "421"],
    ["Slovenia (Slovenija)", "si", "386"],
    ["Solomon Islands", "sb", "677"],
    ["Somalia (Soomaaliya)", "so", "252"],
    ["South Africa", "za", "27"],
    ["South Korea (대한민국)", "kr", "82"],
    ["South Sudan (‫جنوب السودان‬‎)", "ss", "211"],
    ["Spain (España)", "es", "34"],
    ["Sri Lanka (ශ්‍රී ලංකාව)", "lk", "94"],
    ["Sudan (‫السودان‬‎)", "sd", "249"],
    ["Suriname", "sr", "597"],
    ["Svalbard and Jan Mayen", "sj", "47", 1],
    ["Swaziland", "sz", "268"],
    ["Sweden (Sverige)", "se", "46"],
    ["Switzerland (Schweiz)", "ch", "41"],
    ["Syria (‫سوريا‬‎)", "sy", "963"],
    ["Taiwan (台灣)", "tw", "886"],
    ["Tajikistan", "tj", "992"],
    ["Tanzania", "tz", "255"],
    ["Thailand (ไทย)", "th", "66"],
    ["Timor-Leste", "tl", "670"],
    ["Togo", "tg", "228"],
    ["Tokelau", "tk", "690"],
    ["Tonga", "to", "676"],
    ["Trinidad and Tobago", "tt", "1868"],
    ["Tunisia (‫تونس‬‎)", "tn", "216"],
    ["Turkey (Türkiye)", "tr", "90"],
    ["Turkmenistan", "tm", "993"],
    ["Turks and Caicos Islands", "tc", "1649"],
    ["Tuvalu", "tv", "688"],
    ["U.S. Virgin Islands", "vi", "1340"],
    ["Uganda", "ug", "256"],
    ["Ukraine (Україна)", "ua", "380"],
    ["United Arab Emirates (‫الإمارات العربية المتحدة‬‎)", "ae", "971"],
    ["United Kingdom", "gb", "44", 0],
    ["United States", "us", "1", 0],
    ["Uruguay", "uy", "598"],
    ["Uzbekistan (Oʻzbekiston)", "uz", "998"],
    ["Vanuatu", "vu", "678"],
    ["Vatican City (Città del Vaticano)", "va", "39", 1],
    ["Venezuela", "ve", "58"],
    ["Vietnam (Việt Nam)", "vn", "84"],
    ["Wallis and Futuna", "wf", "681"],
    ["Western Sahara (‫الصحراء الغربية‬‎)", "eh", "212", 1],
    ["Yemen (‫اليمن‬‎)", "ye", "967"],
    ["Zambia", "zm", "260"],
    ["Zimbabwe", "zw", "263"],
    ["Åland Islands", "ax", "358", 1],
];

var GetCountriesPhoneDropDownValues = function (countriesList) {
    return countriesList.map(function (eachCountry) { return ({
        label: eachCountry[0] + " +" + eachCountry[2],
        key: eachCountry[1].toUpperCase(),
        value: eachCountry[1].toUpperCase(),
    }); });
};
var getDialCode = function (countryCode) {
    var countryArray = CountriesList.find(function (countryArray) { return countryArray[1] === countryCode.toLowerCase(); });
    return countryArray ? "+" + countryArray[2] : "";
};
var getAllowedCountriesList = function (validCountries) {
    var allowedCountriesList = CountriesList.filter(function (eachCountryArray) {
        return validCountries.includes(eachCountryArray[1].toString().toUpperCase());
    });
    return allowedCountriesList;
};

function getBorderColorBasedOnColor(color) {
    switch (color) {
        case colors$1.indigo:
            return "border-indigo-600 border-1";
        case colors$1.cyan:
            return "border-cyan-600 border-1";
        default:
            return "border-blue-600 border-1";
    }
}
var getIntlPhoneNumberFieldStyles = function (isFocussed, isNotValid, isDisabled, intlPhoneNumberFieldStyles, color, label) {
    if (color === void 0) { color = colors$1.blue; }
    return cn(label ? "flex items-center mt-1" : "flex items-center", "border-1 border-blue-gray-300 dark:border-gray-700 bg-base-white rounded-lg", "text-sm shadow-small1 h-38px", "overflow-hidden", "" + (isFocussed ? getBorderColorBasedOnColor(color) : ""), "" + (isNotValid && "border-red-400 border-1"), "" + (isDisabled && "pointer-events-none bg-blue-gray-100"), intlPhoneNumberFieldStyles);
};

/**
 * Provides the behavior and accessibility implementation for a combo box component.
 * A combo box combines a text input with a listbox, allowing users to filter a list of options to items matching a query.
 * @param props - Props for the combo box.
 * @param state - State for the select, as returned by `useComboBoxState`.
 */
function useCountryCodeComboBox(props, state) {
    var _a, _b;
    var buttonRef = props.buttonRef, popoverRef = props.popoverRef, inputRef = props.inputRef, listBoxRef = props.listBoxRef, keyboardDelegate = props.keyboardDelegate, 
    // completionMode = 'suggest',
    shouldFocusWrap = props.shouldFocusWrap, isReadOnly = props.isReadOnly, isDisabled = props.isDisabled;
    var _c = useMenuTrigger({
        type: "listbox",
        isDisabled: isDisabled || isReadOnly,
    }, state, buttonRef), menuTriggerProps = _c.menuTriggerProps, menuProps = _c.menuProps;
    // Set listbox id so it can be used when calling getItemId later
    listData.set(state, { id: menuProps.id });
    // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
    // When virtualized, the layout object will be passed in as a prop and override this.
    var delegate = useMemo(function () {
        return keyboardDelegate ||
            new ListKeyboardDelegate(state.collection, state.disabledKeys, listBoxRef);
    }, [keyboardDelegate, state.collection, state.disabledKeys, listBoxRef]);
    // Use useSelectableCollection to get the keyboard handlers to apply to the textfield
    var collectionProps = useSelectableCollection({
        selectionManager: state.selectionManager,
        keyboardDelegate: delegate,
        disallowTypeAhead: true,
        disallowEmptySelection: true,
        shouldFocusWrap: shouldFocusWrap,
        ref: inputRef,
        // Prevent item scroll behavior from being applied here, should be handled in the user's Popover + ListBox component
        isVirtualized: true,
    }).collectionProps;
    // For textfield specific keydown operations
    var onKeyDown = function (e) {
        switch (e.key) {
            case "Enter":
            case "Tab":
                // Prevent form submission if menu is open since we may be selecting a option
                if (state.isOpen && e.key === "Enter") {
                    e.preventDefault();
                }
                state.commit();
                break;
            case "Escape":
                state.revert();
                break;
            case "ArrowDown":
                state.open("first", "manual");
                break;
            case "ArrowUp":
                state.open("last", "manual");
                break;
            case "ArrowLeft":
            case "ArrowRight":
                state.selectionManager.setFocusedKey(null);
                break;
        }
    };
    var onBlur = function (e) {
        var _a;
        // Ignore blur if focused moved to the button or into the popover.
        if (e.relatedTarget === (buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) ||
            ((_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))) {
            return;
        }
        if (props.onBlur) {
            props.onBlur(e);
        }
        state.setFocused(false);
    };
    var onFocus = function (e) {
        if (state.isFocused) {
            return;
        }
        if (props.onFocus) {
            props.onFocus(e);
        }
        state.setFocused(true);
    };
    var _d = useTextField(__assign(__assign({}, props), { onChange: state.setInputValue, onKeyDown: !isReadOnly &&
            chain(state.isOpen && collectionProps.onKeyDown, onKeyDown, props.onKeyDown), onBlur: onBlur, value: state.inputValue, onFocus: onFocus, autoComplete: "off" }), inputRef), labelProps = _d.labelProps, inputProps = _d.inputProps, descriptionProps = _d.descriptionProps, errorMessageProps = _d.errorMessageProps;
    // Press handlers for the ComboBox button
    var onPress = function (e) {
        if (e.pointerType === "touch") {
            // Focus the input field in case it isn't focused yet
            inputRef.current.focus();
            state.toggle(null, "manual");
        }
    };
    var onPressStart = function (e) {
        if (e.pointerType !== "touch" && inputRef.current) {
            inputRef.current.focus();
        }
        state.toggle(e.pointerType === "keyboard" || e.pointerType === "virtual"
            ? "first"
            : null, "manual");
    };
    var triggerLabelProps = useLabels({
        id: menuTriggerProps.id,
        "aria-label": " Show suggestions",
        "aria-labelledby": props["aria-labelledby"] || labelProps.id,
    });
    var listBoxProps = useLabels({
        id: menuProps.id,
        "aria-label": "Suggestions",
        "aria-labelledby": "Suggestions" ,
    });
    // If a touch happens on direct center of ComboBox input, might be virtual click from iPad so open ComboBox menu
    var lastEventTime = useRef(0);
    var onTouchEnd = function (e) {
        if (isDisabled || isReadOnly) {
            return;
        }
        // Sometimes VoiceOver on iOS fires two touchend events in quick succession. Ignore the second one.
        if (e.timeStamp - lastEventTime.current < 500) {
            e.preventDefault();
            inputRef.current.focus();
            return;
        }
        var rect = e.target.getBoundingClientRect();
        var touch = e.changedTouches[0];
        var centerX = Math.ceil(rect.left + 0.5 * rect.width);
        var centerY = Math.ceil(rect.top + 0.5 * rect.height);
        if (touch.clientX === centerX && touch.clientY === centerY) {
            e.preventDefault();
            inputRef.current.focus();
            state.toggle(null, "manual");
            lastEventTime.current = e.timeStamp;
        }
    };
    // VoiceOver has issues with announcing aria-activedescendant properly on change
    // (especially on iOS). We use a live region announcer to announce focus changes
    // manually. In addition, section titles are announced when navigating into a new section.
    var focusedItem = state.selectionManager.focusedKey != null && state.isOpen
        ? state.collection.getItem(state.selectionManager.focusedKey)
        : undefined;
    var sectionKey = (_a = focusedItem === null || focusedItem === void 0 ? void 0 : focusedItem.parentKey) !== null && _a !== void 0 ? _a : null;
    var itemKey = (_b = state.selectionManager.focusedKey) !== null && _b !== void 0 ? _b : null;
    var lastSection = useRef(sectionKey);
    var lastItem = useRef(itemKey);
    useEffect(function () {
        if (isAppleDevice() &&
            focusedItem != null &&
            itemKey !== lastItem.current) {
            state.selectionManager.isSelected(itemKey);
            var section = sectionKey != null ? state.collection.getItem(sectionKey) : null;
            (section === null || section === void 0 ? void 0 : section["aria-label"]) ||
                (typeof (section === null || section === void 0 ? void 0 : section.rendered) === "string" ? section.rendered : "") ||
                "";
            //TODO: Need to add this
            // let announcement = formatMessage("focusAnnouncement", {
            //   isGroupChange: section && sectionKey !== lastSection.current,
            //   groupTitle: sectionTitle,
            //   groupCount: section ? [...section.childNodes].length : 0,
            //   optionText: focusedItem["aria-label"] || focusedItem.textValue || "",
            //   isSelected,
            // })
            // announce(announcement)
        }
        lastSection.current = sectionKey;
        lastItem.current = itemKey;
    });
    // Announce the number of available suggestions when it changes
    var optionCount = getItemCount(state.collection);
    var lastSize = useRef(optionCount);
    var lastOpen = useRef(state.isOpen);
    useEffect(function () {
        // Only announce the number of options available when the menu opens if there is no
        // focused item, otherwise screen readers will typically read e.g. "1 of 6".
        // The exception is VoiceOver since this isn't included in the message above.
        state.isOpen !== lastOpen.current &&
            (state.selectionManager.focusedKey == null || isAppleDevice());
        // if (
        //   state.isOpen &&
        //   (didOpenWithoutFocusedItem || optionCount !== lastSize.current)
        // ) {
        //   let announcement = formatMessage("countAnnouncement", { optionCount })
        //   announce(announcement)
        // }
        lastSize.current = optionCount;
        lastOpen.current = state.isOpen;
    });
    // Announce when a selection occurs for VoiceOver. Other screen readers typically do this automatically.
    var lastSelectedKey = useRef(state.selectedKey);
    useEffect(function () {
        if (isAppleDevice() &&
            state.isFocused &&
            state.selectedItem &&
            state.selectedKey !== lastSelectedKey.current) {
            state.selectedItem["aria-label"] || state.selectedItem.textValue || "";
            // let announcement = formatMessage("selectedAnnouncement", { optionText })
            // announce(announcement)
        }
        lastSelectedKey.current = state.selectedKey;
    });
    useEffect(function () {
        if (state.isOpen) {
            return ariaHideOutside([inputRef.current, popoverRef.current]);
        }
    }, [state.isOpen, inputRef, popoverRef]);
    return {
        labelProps: labelProps,
        buttonProps: __assign(__assign(__assign({}, menuTriggerProps), triggerLabelProps), { excludeFromTabOrder: true, onPress: onPress, onPressStart: onPressStart, isDisabled: isDisabled || isReadOnly }),
        inputProps: mergeProps(inputProps, {
            role: "combobox",
            "aria-expanded": menuTriggerProps["aria-expanded"],
            "aria-controls": state.isOpen ? menuProps.id : undefined,
            // TODO: readd proper logic for completionMode = complete (aria-autocomplete: both)
            "aria-autocomplete": "list",
            "aria-activedescendant": focusedItem
                ? getItemId(state, focusedItem.key)
                : undefined,
            onTouchEnd: onTouchEnd,
            // This disable's iOS's autocorrect suggestions, since the combo box provides its own suggestions.
            autoCorrect: "off",
            // This disable's the macOS Safari spell check auto corrections.
            spellCheck: "false",
        }),
        listBoxProps: mergeProps(menuProps, listBoxProps, {
            autoFocus: state.focusStrategy,
            shouldUseVirtualFocus: true,
            shouldSelectOnPressUp: true,
            shouldFocusOnHover: true,
        }),
        descriptionProps: descriptionProps,
        errorMessageProps: errorMessageProps,
    };
}

var SearchIcon = function (props) {
    var _a = props.width, width = _a === void 0 ? 20 : _a, _b = props.height, height = _b === void 0 ? 20 : _b, _c = props.fill, fill = _c === void 0 ? "#64748B" : _c;
    return (React.createElement("svg", { width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { d: "M9.583 18.125c-4.709 0-8.542-3.833-8.542-8.542 0-4.708 3.833-8.541 8.542-8.541 4.708 0 8.541 3.833 8.541 8.541 0 4.709-3.833 8.542-8.541 8.542zm0-15.833c-4.025 0-7.292 3.275-7.292 7.291 0 4.017 3.267 7.292 7.292 7.292s7.291-3.275 7.291-7.292c0-4.016-3.266-7.291-7.291-7.291zm8.75 16.666a.618.618 0 0 1-.442-.183l-1.667-1.667a.629.629 0 0 1 0-.883.629.629 0 0 1 .884 0l1.666 1.667a.629.629 0 0 1 0 .883.618.618 0 0 1-.441.183z", fill: fill })));
};

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  default: SearchIcon
});

var NoResultsFoundIcon = function () { return (React.createElement("svg", { width: 106, height: 106, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("circle", { cx: 49.534, cy: 53.924, r: 37.895, fill: "#EAEEF9" }),
    React.createElement("path", { d: "M92.14 38.286a3.246 3.246 0 1 0 0-6.493 3.246 3.246 0 0 0 0 6.493ZM96.89 25.617a2.217 2.217 0 1 0 0-4.434 2.217 2.217 0 0 0 0 4.434ZM16.17 22.014a4.117 4.117 0 1 0 0-8.234 4.117 4.117 0 0 0 0 8.234Z", fill: "#EAEEF9" }),
    React.createElement("path", { d: "M64.185 33.277H43.998v-7.66h26.608c4.134.097 7.49 3.512 7.49 7.66h-8.755", fill: "#CCD4E2" }),
    React.createElement("g", { filter: "url(#a)" },
        React.createElement("path", { d: "M71.192 25.616c-1.557.098-3.016.732-4.038 1.806a2.12 2.12 0 0 0-.292.341c-.924 1.074-1.605 2.489-1.605 4.001V77.34A7.63 7.63 0 0 1 57.62 85H20.458V31.423c.097-1.562.68-2.977 1.702-4.001a6.053 6.053 0 0 1 4.33-1.806H71.191Z", fill: "url(#b)" })),
    React.createElement("path", { d: "M57.862 36.059H46.82a.75.75 0 0 1-.73-.732c0-.39.34-.732.73-.732h11.042c.389 0 .73.341.73.732 0 .39-.292.732-.73.732ZM41.618 36.059H27.22a.75.75 0 0 1-.73-.732c0-.39.34-.732.73-.732h14.447c.389 0 .73.341.73.732 0 .39-.341.732-.779.732ZM50.857 43.28h-14.35a.75.75 0 0 1-.729-.732c0-.39.34-.732.73-.732h14.35c.388 0 .729.342.729.732.048.39-.292.732-.73.732Z", fill: "#B3BFD3" }),
    React.createElement("path", { d: "M31.89 43.28h-4.67a.75.75 0 0 1-.73-.732c0-.39.34-.732.73-.732h4.67c.389 0 .73.342.73.732a.75.75 0 0 1-.73.732ZM57.866 50.502h-8.804a.75.75 0 0 1-.73-.732c0-.39.34-.732.73-.732h8.804c.389 0 .73.342.73.732 0 .39-.292.732-.73.732Z", fill: "#D4DBEC" }),
    React.createElement("path", { d: "M44.828 50.502H27.22a.75.75 0 0 1-.73-.732c0-.39.34-.732.73-.732h17.608c.39 0 .73.342.73.732 0 .39-.292.732-.73.732ZM50.864 57.723h-9.68a.75.75 0 0 1-.73-.732c0-.39.34-.732.73-.732h9.68c.389 0 .73.342.73.732.048.39-.293.732-.73.732Z", fill: "#B3BFD3" }),
    React.createElement("path", { d: "M36.948 57.723H27.22a.75.75 0 0 1-.73-.732c0-.39.34-.732.73-.732h9.777c.39 0 .73.342.73.732 0 .39-.34.732-.779.732Z", fill: "#D4DBEC" }),
    React.createElement("path", { d: "M57.865 64.945h-6.762a.75.75 0 0 1-.73-.732c0-.39.341-.732.73-.732h6.762c.389 0 .73.342.73.732 0 .39-.292.732-.73.732Z", fill: "#B3BFD3" }),
    React.createElement("path", { d: "M46.724 64.945H35.39a.75.75 0 0 1-.73-.732c0-.39.341-.732.73-.732h11.334c.389 0 .73.342.73.732a.75.75 0 0 1-.73.732Z", fill: "#D4DBEC" }),
    React.createElement("path", { d: "M31.16 64.945h-3.94a.75.75 0 0 1-.73-.732c0-.39.34-.732.73-.732h3.94c.389 0 .73.342.73.732a.75.75 0 0 1-.73.732ZM50.859 72.168h-6.713a.75.75 0 0 1-.73-.732c0-.39.341-.731.73-.731h6.713c.39 0 .73.341.73.731.048.39-.292.732-.73.732Z", fill: "#B3BFD3" }),
    React.createElement("path", { d: "M39.72 72.168h-12.5a.75.75 0 0 1-.73-.732c0-.39.34-.731.73-.731h12.5c.39 0 .73.341.73.731a.75.75 0 0 1-.73.732Z", fill: "#D4DBEC" }),
    React.createElement("path", { d: "M41.276 78.95h9.826c0 1.66.681 3.172 1.751 4.245 1.07 1.123 2.578 1.757 4.232 1.757H12.87a5.984 5.984 0 0 1-5.984-6.002h29.088s5.4.049 5.303 0Z", fill: "#CCD4E2" }),
    React.createElement("path", { d: "m83.46 74.278-6.14-6.137 1.407-1.408 6.137 6.142-1.403 1.403Z", fill: "#858B9B" }),
    React.createElement("path", { d: "m85.597 76.438-4.71-4.71a1.011 1.011 0 0 1 0-1.427 1.01 1.01 0 0 1 1.428 0l4.709 4.71a1.011 1.011 0 0 1 0 1.427 1.011 1.011 0 0 1-1.427 0Z", fill: "#858B9B" }),
    React.createElement("path", { d: "m85.258 76.738-6.06-6.059a.366.366 0 0 1 0-.514l1.55-1.55c.14-.14.368-.14.514 0l6.06 6.06c.14.14.14.369 0 .515l-1.55 1.548a.366.366 0 0 1-.514 0Z", fill: "url(#c)" }),
    React.createElement("path", { d: "M70.367 48.367c-6.429 0-11.638 5.21-11.638 11.637 0 6.423 5.21 11.638 11.633 11.638 6.428 0 11.637-5.21 11.637-11.638s-5.21-11.637-11.632-11.637Zm0 21.12c-5.161 0-9.342-4.25-9.342-9.488 0-5.238 4.18-9.482 9.342-9.482 5.16 0 9.34 4.249 9.34 9.487 0 5.239-4.184 9.482-9.34 9.482Z", fill: "url(#d)" }),
    React.createElement("defs", null,
        React.createElement("linearGradient", { id: "b", x1: 45.808, y1: 24.243, x2: 45.808, y2: 85.64, gradientUnits: "userSpaceOnUse" },
            React.createElement("stop", { stopColor: "#FDFEFF" }),
            React.createElement("stop", { offset: 0.996, stopColor: "#ECF0F5" })),
        React.createElement("linearGradient", { id: "c", x1: 79.089, y1: 72.678, x2: 87.428, y2: 72.678, gradientUnits: "userSpaceOnUse" },
            React.createElement("stop", { stopColor: "#B0BACC" }),
            React.createElement("stop", { offset: 1, stopColor: "#969EAE" })),
        React.createElement("linearGradient", { id: "d", x1: 58.718, y1: 60.007, x2: 82.003, y2: 60.007, gradientUnits: "userSpaceOnUse" },
            React.createElement("stop", { stopColor: "#B0BACC" }),
            React.createElement("stop", { offset: 1, stopColor: "#969EAE" })),
        React.createElement("filter", { id: "a", x: 8.195, y: 19.485, width: 75.26, height: 83.91, filterUnits: "userSpaceOnUse", colorInterpolationFilters: "sRGB" },
            React.createElement("feFlood", { floodOpacity: 0, result: "BackgroundImageFix" }),
            React.createElement("feColorMatrix", { in: "SourceAlpha", values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0", result: "hardAlpha" }),
            React.createElement("feOffset", { dy: 6.132 }),
            React.createElement("feGaussianBlur", { stdDeviation: 6.132 }),
            React.createElement("feColorMatrix", { values: "0 0 0 0 0.397708 0 0 0 0 0.47749 0 0 0 0 0.575 0 0 0 0.27 0" }),
            React.createElement("feBlend", { in2: "BackgroundImageFix", result: "effect1_dropShadow_8684_2289" }),
            React.createElement("feBlend", { in: "SourceGraphic", in2: "effect1_dropShadow_8684_2289", result: "shape" }))))); };

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  default: NoResultsFoundIcon
});

var ComboBoxButton = React__default.forwardRef(function ComboBoxButton(props, ref) {
    props = useSlotProps(props, "button");
    var buttonStyles = props.buttonStyles, state = props.state, triggerProps = props.triggerProps, hoverProps = props.hoverProps; props.focusProps; var children = props.children, isDisabled = props.isDisabled, onFocus = props.onFocus, onBlur = props.onBlur, className = props.className, otherProps = __rest(props, ["buttonStyles", "state", "triggerProps", "hoverProps", "focusProps", "children", "isDisabled", "onFocus", "onBlur", "className"]);
    var domRef = useFocusableRef(ref);
    var buttonProps = useButton(__assign(__assign(__assign({}, triggerProps), otherProps), { isDisabled: isDisabled, onFocus: onFocus, onBlur: onBlur }), domRef).buttonProps;
    return (React__default.createElement("button", __assign({}, mergeProps(buttonProps, hoverProps), { className: cn("flex items-center justify-between focus:outline-none py-2 pr-2 cursor-pointer", className, buttonStyles), ref: domRef }),
        children,
        state.isOpen ? (React__default.createElement(DropDownIcon$1, { style: { transform: "rotate(180deg)", transition: "transform 0.5s" } })) : (React__default.createElement(DropDownIcon$1, { style: { transition: "transform 0.5s" } }))));
});

var getComboBoxWrapperWithPopOverStyles = function (isFocus, error, isDisabled, wrapperWithPopOverStyles) {
    return cn("flex flex-grow items-center pl-13px py-2", "border-1 border-blue-gray-300 dark:border-gray-700 bg-base-white rounded-lg", "text-sm shadow-small1", "overflow-hidden", "" + (isFocus ? "border-blue-600 border-1" : ""), "" + (error && "border-red-400 border-1"), "" + (isDisabled && "pointer-events-none bg-blue-gray-100"), wrapperWithPopOverStyles);
};
var getMobileComboboxWrapperStyles = function (isFocus, error, isDisabled, comboboxButtonClassname) {
    return cn("flex items-center justify-between focus:outline-none pl-3 pr-2 w-full border-1 border-solid border-blue-gray-300 rounded-8px", "" + (isFocus ? "border-blue-600 border-1" : ""), "" + (error && "border-red-400 border-1"), "" + (isDisabled && "pointer-events-none bg-blue-gray-100"), comboboxButtonClassname);
};
var getMobileComboboxButtonStyles = function (state) {
    return cn("text-sm leading-5 font-normal mr-1", {
        "text-blue-gray-900": state.selectedItem,
    }, {
        "text-blue-gray-500": !state.selectedItem,
    });
};

var LOADING = "loading";

var MobileComboBox = function MobileComboBox(props, ref) {
    props = useProviderProps(props);
    var contains = useFilter({ sensitivity: "base" }).contains;
    var state = useComboBoxState(__assign(__assign({}, props), { defaultFilter: contains, allowsEmptyCollection: true, shouldCloseOnBlur: false }));
    var triggerRef = useRef(null);
    var unwrappedTriggerRef = useFocusableRef(triggerRef);
    var _a = useOverlayTrigger({ type: "listbox" }, state, triggerRef), triggerProps = _a.triggerProps, overlayProps = _a.overlayProps;
    var helperText = props.helperText, error = props.error, isDisabled = props.isDisabled, isSearchable = props.isSearchable, trayChildrenWrapperClassName = props.trayChildrenWrapperClassName, trayContainerClassName = props.trayContainerClassName, label = props.label, comboboxButtonClassname = props.comboboxButtonClassname, autoFocus = props.autoFocus;
    var hoverProps = useHover({ isDisabled: isDisabled }).hoverProps;
    var _b = __read(useState(false), 2); _b[1];
    var onClose = function () { return state.commit(); };
    var _c = __read(useState(false), 2), isFocus = _c[0], setIsFocus = _c[1];
    var onFocus = function () {
        setIsFocus(true);
    };
    var onBlur = function () {
        setIsFocus(false);
    };
    return (React__default.createElement("div", { className: cn("flex flex-col justify-center") },
        React__default.createElement(Label, { labelClassName: cn("mb-1") }, label),
        React__default.createElement(FocusRing, { autoFocus: autoFocus, within: true, focusClassProps: "ring-offset-0 " + (error
                ? "ring-0 border-1 border-red-400"
                : isFocus
                    ? "ring ring-blue-200"
                    : "") },
            React__default.createElement(ComboBoxButton, { buttonStyles: getMobileComboboxWrapperStyles(isFocus, error, isDisabled, comboboxButtonClassname), state: state, triggerProps: triggerProps, ref: triggerRef, hoverProps: hoverProps, onPress: function () { return state.open(null, "manual"); }, isDisabled: isDisabled, onFocus: onFocus, onBlur: onBlur },
                React__default.createElement("span", { className: getMobileComboboxButtonStyles(state) }, state.selectedItem ? state.selectedItem.value.value : "Select"))),
        React__default.createElement(Hint, { id: "combobox-hint", errorText: error, hint: helperText }),
        React__default.createElement(_Tray, __assign({ isOpen: state.isOpen, onClose: onClose, label: label, isSearchable: isSearchable, isFixedHeight: true, isNonModal: true, trayChildrenWrapperClassName: trayChildrenWrapperClassName, trayContainerClassName: trayContainerClassName }, overlayProps),
            React__default.createElement(CombobboxTray, __assign({}, props, { state: state, onClose: onClose, overlayProps: overlayProps, unwrappedTriggerRef: unwrappedTriggerRef })))));
};
var CombobboxTray = function (props) {
    var state = props.state, apiStatus = props.apiStatus, unwrappedTriggerRef = props.unwrappedTriggerRef; props.overlayProps; var onClose = props.onClose, _a = props.isSearchable, isSearchable = _a === void 0 ? true : _a, loadingState = props.loadingState, onLoadMore = props.onLoadMore, cancelButtonClassname = props.cancelButtonClassname, otherProps = __rest(props, ["state", "apiStatus", "unwrappedTriggerRef", "overlayProps", "onClose", "isSearchable", "loadingState", "onLoadMore", "cancelButtonClassname"]);
    var isAsync = loadingState != null;
    var inputRef = React__default.useRef(null);
    var popoverRef = useRef(null);
    var listBoxRef = useRef(null);
    var timerRef = useRef(null);
    var layout = useListBoxLayout(state);
    var _b = useCountryCodeComboBox(__assign(__assign({}, otherProps), { inputRef: inputRef, buttonRef: unwrappedTriggerRef, listBoxRef: listBoxRef, popoverRef: popoverRef, keyboardDelegate: layout }), state), inputProps = _b.inputProps, labelProps = _b.labelProps;
    useEffect(function () {
        if (state.isOpen && inputRef.current) {
            timerRef.current = setTimeout(function () {
                var _a;
                //FIXME: This is a hack to fix the issue of the input not being focused when the popover is open
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100);
        }
        return function () {
            timerRef.current && clearTimeout(timerRef.current);
        };
    }, [state.isOpen]);
    var onClickClearIcon = function (e) {
        var _a;
        e.stopPropagation();
        state.setInputValue("");
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var ref = React__default.useRef(null);
    var menuProps = useSelect(props, state, ref).menuProps;
    //TODO: Abstract this function and use the same in both the desktop and mobile combobox
    var renderNoResults = function () { return (React__default.createElement("div", { className: cn("m-auto pb-3 w-full h-full flex items-center justify-center flex-col") },
        React__default.createElement(NoResultsFoundIcon, null),
        React__default.createElement("p", { className: cn("text-center text-blue-gray-600 font-medium") }, isAsync && loadingState === "loading"
            ? "Loading..."
            : "No Results Found"))); };
    var clearIcon = function () {
        return inputProps.value ? (React__default.createElement("div", { className: cn(" cursor-pointer focus:border-black focus:border-1 "), onClick: onClickClearIcon },
            React__default.createElement(CrossIcon, null))) : null;
    };
    var listBox = (React__default.createElement(FocusScope, { restoreFocus: true },
        React__default.createElement(_ListBoxBase, __assign({}, menuProps, { state: state, ref: listBoxRef, layout: layout, isLoading: loadingState === "loadingMore" || loadingState === "filtering", onLoadMore: onLoadMore, shouldSelectOnPressUp: true, renderEmptyState: renderNoResults, UNSAFE_className: cn("outline-none"), autoFocus: false }))));
    return (React__default.createElement("div", { className: cn("flex h-full flex-col w-full"), ref: popoverRef }, apiStatus === LOADING ? (React__default.createElement("div", { className: cn("flex justify-center items-center p-4") },
        React__default.createElement(Loader, { fill: "#64748b" }))) : (React__default.createElement(FocusScope, { restoreFocus: true, contain: true },
        React__default.createElement("div", { className: cn("justify-between items-center bg-transparent w-full mx-16px mt-16px flex-shrink-0 pr-16px", isSearchable ? "flex" : "hidden") },
            React__default.createElement(DismissButton, { onDismiss: onClose }),
            React__default.createElement(TextFieldBase, __assign({}, props, { inputProps: inputProps, labelProps: labelProps, ref: inputRef, inputContainerClassName: cn("rounded-12px"), containerClassName: cn(isSearchable ? "flex flex-1" : "hidden"), leftElement: SearchIcon, defaultValue: "", rightElement: clearIcon, hintClassName: cn("hidden") })),
            React__default.createElement("button", { onClick: state.close, className: cn("cursor-pointer flex items-center justify-center outline-none border-none shrink-0 mt-22px ml-4px", cancelButtonClassname) },
                React__default.createElement("span", { className: cn("font-medium text-blue-600") }, "Cancel")),
            React__default.createElement(DismissButton, { onDismiss: onClose })),
        listBox))));
};

var HiddenInput = function (props) {
    var name = props.name, value = props.value, isDisabled = props.isDisabled, autoComplete = props.autoComplete;
    return (React__default.createElement("input", { type: "hidden", name: name, value: value, disabled: isDisabled, autoComplete: autoComplete }));
};

function CountryListCombobox(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var inputRef = React__default.useRef(null);
    var error = props.error, isDisabled = props.isDisabled, autoFocus = props.autoFocus, fieldContainerStyles = props.fieldContainerStyles, wrapperWithPopOverStyles = props.wrapperWithPopOverStyles, buttonStyles = props.buttonStyles, popOverStyles = props.popOverStyles, parentRef = props.parentRef, _j = props.isSearchable, isSearchable = _j === void 0 ? true : _j, overlayOptions = props.overlayOptions, otherProps = __rest(props, ["error", "isDisabled", "autoFocus", "fieldContainerStyles", "wrapperWithPopOverStyles", "buttonStyles", "popOverStyles", "parentRef", "isSearchable", "overlayOptions"]);
    var popoverRef = useRef(null);
    var unwrappedPopoverRef = useUnwrapDOMRef(popoverRef);
    var triggerRef = useRef(null);
    var unwrappedTriggerRef = useUnwrapDOMRef(triggerRef);
    var listBoxRef = useRef(null);
    var contains = useFilter({ sensitivity: "base" }).contains;
    var state = useComboBoxState(__assign(__assign({ allowsEmptyCollection: true }, otherProps), { defaultFilter: contains, shouldCloseOnBlur: false }));
    var _k = __read(useState(false), 2), isFocus = _k[0]; _k[1];
    var _l = useCountryCodeComboBox(__assign(__assign({}, otherProps), { inputRef: inputRef, buttonRef: unwrappedTriggerRef, listBoxRef: listBoxRef, popoverRef: unwrappedPopoverRef }), state), triggerProps = _l.buttonProps, inputProps = _l.inputProps, labelProps = _l.labelProps;
    var timerRef = useRef(null);
    useEffect(function () {
        if (state.isOpen && inputRef.current) {
            timerRef.current = setTimeout(function () {
                var _a;
                //FIXME: This is a hack to fix the issue of the input not being focused when the popover is open
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 50);
        }
        return function () {
            timerRef.current && clearTimeout(timerRef.current);
        };
    }, [state.isOpen]);
    var layout = useListBoxLayout(state);
    var hoverProps = useHover({ isDisabled: isDisabled }).hoverProps;
    var _m = __read(React__default.useState(false), 2), setFocusWithin = _m[1];
    var focusProps = useFocus({
        onBlur: function () { },
        onFocusChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusProps;
    function focusStateRingColor(color) {
        switch (color) {
            case colors$1.indigo:
                return "ring-indigo-200";
            default:
                return "ring-blue-200";
        }
    }
    var ref = React__default.useRef(null);
    var menuProps = useSelect(otherProps, state, ref).menuProps;
    var isMobileDevice = useMediaQuery$1({
        query: "(max-width: 768px)",
    });
    var _o = useOverlayPosition(__assign({ targetRef: unwrappedTriggerRef, overlayRef: unwrappedPopoverRef, scrollRef: listBoxRef, offset: 0, isOpen: state.isOpen && !isMobileDevice, placement: "bottom left", onClose: state.close }, overlayOptions)), positionProps = _o.overlayProps, placement = _o.placement;
    var styles = __assign(__assign({}, positionProps.style), { maxHeight: typeof ((_a = positionProps.style) === null || _a === void 0 ? void 0 : _a.maxHeight) === "string"
            ? parseInt(positionProps.style.maxHeight) + 6
            : ((_b = positionProps.style) === null || _b === void 0 ? void 0 : _b.maxHeight)
                ? positionProps.style.maxHeight + 6
                : undefined, paddingTop: "2px", paddingBottom: "2px", width: parentRef
            ? (_c = parentRef === null || parentRef === void 0 ? void 0 : parentRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth
            : (_d = unwrappedTriggerRef === null || unwrappedTriggerRef === void 0 ? void 0 : unwrappedTriggerRef.current) === null || _d === void 0 ? void 0 : _d.offsetWidth });
    var onClickClearIcon = function (e) {
        var _a;
        e.stopPropagation();
        state.setInputValue("");
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var renderNoResults = function () { return (React__default.createElement("div", { className: cn("m-auto pb-3 w-full h-full flex items-center justify-center flex-col") },
        React__default.createElement(NoResultsFoundIcon, null),
        React__default.createElement("p", { className: cn("text-center text-blue-gray-600 font-medium") }, "No Results Found"))); };
    var clearIcon = function () {
        return inputProps.value ? (React__default.createElement("div", { className: cn("cursor-pointer focus:border-black focus:border-1 "), onClick: onClickClearIcon },
            React__default.createElement(CrossIcon, null))) : null;
    };
    var listBoxTop = listBoxRef.current ? (_e = listBoxRef.current) === null || _e === void 0 ? void 0 : _e.offsetTop : 0;
    var listBoxMaxHeight = typeof ((_f = positionProps.style) === null || _f === void 0 ? void 0 : _f.maxHeight) === "string"
        ? parseInt(positionProps.style.maxHeight) - listBoxTop
        : ((_g = positionProps.style) === null || _g === void 0 ? void 0 : _g.maxHeight)
            ? positionProps.style.maxHeight - listBoxTop
            : (_h = positionProps.style) === null || _h === void 0 ? void 0 : _h.maxHeight;
    var listBox = (React__default.createElement(FocusScope, { restoreFocus: true },
        React__default.createElement(_ListBoxBase, __assign({}, menuProps, { state: state, ref: listBoxRef, layout: layout, shouldSelectOnPressUp: true, focusOnPointerEnter: true, renderEmptyState: renderNoResults, UNSAFE_style: { maxHeight: listBoxMaxHeight }, UNSAFE_className: cn("outline-none") }))));
    var popover = (React__default.createElement(_Popover, { isOpen: state.isOpen, onClose: state.close, popOverStyles: popOverStyles, ref: popoverRef, style: styles, placement: placement, isDismissable: true },
        React__default.createElement(TextFieldBase, __assign({}, props, { inputProps: inputProps, labelProps: labelProps, ref: inputRef, inputContainerClassName: cn("rounded-12px"), containerClassName: cn(isSearchable ? "block mx-8px" : "hidden"), leftElement: SearchIcon, rightElement: clearIcon })),
        listBox));
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(MediaQuery, { minWidth: 769 },
            React__default.createElement("div", { className: cn("relative m-auto", fieldContainerStyles) },
                React__default.createElement(FocusRing, { autoFocus: autoFocus, within: true, focusClassProps: "ring-offset-0 " + (error
                        ? "ring-0 border-1 border-red-400"
                        : isFocus
                            ? "ring " + focusStateRingColor(colors$1.blue)
                            : "") },
                    React__default.createElement("div", { className: (getComboBoxWrapperWithPopOverStyles(isFocus, error, isDisabled, wrapperWithPopOverStyles),
                            "pl-0px") },
                        React__default.createElement(HiddenSelect, { name: "countryCode", state: state, triggerRef: unwrappedTriggerRef, label: props.label }),
                        React__default.createElement(HiddenInput, { name: "dialCode", value: getDialCode(state.selectedKey) }),
                        React__default.createElement(PressResponder, __assign({}, mergeProps(hoverProps, triggerProps)),
                            React__default.createElement(ComboBoxButton, { buttonStyles: cn(buttonStyles, "pl-3"), state: state, triggerProps: triggerProps, ref: triggerRef, hoverProps: hoverProps, focusProps: focusProps, onPress: function () { return state.open(null, "manual"); } },
                                React__default.createElement("span", { className: cn("mr-1") }, state.selectedItem ? state.selectedItem.value.value : "IN"))))),
                popover)),
        React__default.createElement(MediaQuery, { maxWidth: 768 },
            React__default.createElement(MobileComboBox, __assign({}, props, { comboboxButtonClassname: cn("border-none pl-3 mt-16px h-32px ml-2px rounded-4px"), cancelButtonClassname: cn("mb-4") })))));
}

var InternationalMobileNumber = function (props) {
    //NOTE: react-aria supports Internalization (uses Intl.PluralRules) which is only supported in iOS >=13
    if (typeof Intl.PluralRules !== "function") {
        throw new InternationalizationNotSupportedError("Internationalization is not supported");
    }
    var labelStyles = props.labelStyles, label = props.label, helperText = props.helperText; props.invalidPhoneNumberErrorMessage; props.requiredErrorMessage; var onChangePhoneNumber = props.onChangePhoneNumber, onChangeDialCode = props.onChangeDialCode, onChangeCountryCode = props.onChangeCountryCode, allowedCountriesList = props.allowedCountriesList, placeholder = props.placeholder; props.errorMessageStyles; var helperTextStyles = props.helperTextStyles, intlPhoneNumberContainerStyles = props.intlPhoneNumberContainerStyles, intlPhoneNumberFieldStyles = props.intlPhoneNumberFieldStyles, phoneNumberInputStyles = props.phoneNumberInputStyles, errorMessage = props.errorMessage, isDisabled = props.isDisabled, onBlur = props.onBlur; props.getCustomLabelForSelectedOption; var _c = props.autoFocus, autoFocus = _c === void 0 ? false : _c, phoneNumber = props.phoneNumber, _d = props.countryCode, countryCode = _d === void 0 ? "IN" : _d; props.dialCode; props.isMobile; props.isDesktop; var _h = props.color, color = _h === void 0 ? colors$1.blue : _h, isCountryCodeSelectionDisabled = props.isCountryCodeSelectionDisabled, isPhoneNumberFieldDisabled = props.isPhoneNumberFieldDisabled, hideDialCode = props.hideDialCode; props.mobileListBoxStyles; var handleFocus = props.handleFocus, isRequired = props.isRequired; props.isSearchable; props.noOptionsMessage; props.searchInputPlaceholder; var overlayOptions = props.overlayOptions, renderRightElement = props.renderRightElement, renderHintLeftIcon = props.renderHintLeftIcon, name = props.name, others = __rest(props, ["labelStyles", "label", "helperText", "invalidPhoneNumberErrorMessage", "requiredErrorMessage", "onChangePhoneNumber", "onChangeDialCode", "onChangeCountryCode", "allowedCountriesList", "placeholder", "errorMessageStyles", "helperTextStyles", "intlPhoneNumberContainerStyles", "intlPhoneNumberFieldStyles", "phoneNumberInputStyles", "errorMessage", "isDisabled", "onBlur", "getCustomLabelForSelectedOption", "autoFocus", "phoneNumber", "countryCode", "dialCode", "isMobile", "isDesktop", "color", "isCountryCodeSelectionDisabled", "isPhoneNumberFieldDisabled", "hideDialCode", "mobileListBoxStyles", "handleFocus", "isRequired", "isSearchable", "noOptionsMessage", "searchInputPlaceholder", "overlayOptions", "renderRightElement", "renderHintLeftIcon", "name"]);
    var _k = __read(useState(""), 2); _k[0]; var setError = _k[1];
    var _l = __read(useState(false), 2), isFocussed = _l[0], setIsFocussed = _l[1];
    var phoneNumberFieldRef = useRef(null);
    var getListItemKey = function (item) { return item.key; };
    var handleMobileNumberChange = function (value) {
        onChangePhoneNumber && onChangePhoneNumber(value);
    };
    var handleMobileNumberBlur = function () {
        setIsFocussed(false);
    };
    var handleMobileNumberFocus = function (e) {
        setIsFocussed(true);
        setError("");
        handleFocus && handleFocus(e);
    };
    var handleCountryCodeChange = function (countryCode) {
        if (countryCode) {
            var dialCode_1 = getDialCode(countryCode);
            onChangeCountryCode && onChangeCountryCode(countryCode);
            onChangeDialCode && onChangeDialCode(dialCode_1);
        }
    };
    var isNotValid = errorMessage && !isFocussed;
    function focusStateRingColor(color) {
        switch (color) {
            case colors$1.indigo:
                return "ring-indigo-200";
            case colors$1.cyan:
                return "ring-cyan-200";
            default:
                return "ring-blue-200";
        }
    }
    var hintId = useId();
    var countriesList = allowedCountriesList
        ? GetCountriesPhoneDropDownValues(getAllowedCountriesList(allowedCountriesList))
        : GetCountriesPhoneDropDownValues(CountriesList);
    var selectedCountry = (allowedCountriesList === null || allowedCountriesList === void 0 ? void 0 : allowedCountriesList.length)
        ? countriesList[0].key
        : countryCode;
    return (React__default.createElement("div", { className: cn("flex flex-col relative w-full", intlPhoneNumberContainerStyles), ref: phoneNumberFieldRef },
        React__default.createElement(Label, { labelClassName: labelStyles, isRequired: isRequired }, label),
        React__default.createElement(FocusRing, { autoFocus: autoFocus, within: true, focusClassProps: "ring-offset-0 " + (isNotValid
                ? "ring-0 border-1 border-red-400"
                : isFocussed
                    ? "ring " + focusStateRingColor(color)
                    : "") },
            React__default.createElement("div", { className: getIntlPhoneNumberFieldStyles(isFocussed, isNotValid, isDisabled, intlPhoneNumberFieldStyles, color, label) },
                React__default.createElement(CountryListCombobox, __assign({ label: "", defaultItems: countriesList, placeholder: "", onSelectionChange: function (key) {
                        handleCountryCodeChange(key);
                    }, error: "", selectedKey: selectedCountry, isDisabled: isDisabled || isCountryCodeSelectionDisabled, parentRef: phoneNumberFieldRef, isSearchable: allowedCountriesList ? (allowedCountriesList === null || allowedCountriesList === void 0 ? void 0 : allowedCountriesList.length) > 1 : true, overlayOptions: overlayOptions }, others), function (eachItem) { return (React__default.createElement(Item, { key: getListItemKey(eachItem), textValue: eachItem.label },
                    React__default.createElement(ListElement, { item: eachItem }))); }),
                !hideDialCode && (React__default.createElement("span", { className: cn("mr-1 text-14px text-blue-gray-500") }, getDialCode(countryCode))),
                React__default.createElement("input", { type: "text", inputMode: "numeric", pattern: "[0-9]*", placeholder: placeholder ? placeholder : "", onChange: function (e) { return handleMobileNumberChange(e.target.value); }, onBlur: function (e) {
                        onBlur && onBlur(e);
                        handleMobileNumberBlur();
                    }, onFocus: function (e) { return handleMobileNumberFocus(e); }, className: cn("w-full h-full", "focus: outline-none", "" + (isNotValid && !isFocussed && "text-red-800"), phoneNumberInputStyles), disabled: isDisabled || isPhoneNumberFieldDisabled, value: phoneNumber, name: name }),
                renderRightElement && renderRightElement())),
        React__default.createElement(Hint, { id: hintId, hint: helperText, errorText: errorMessage && !isFocussed ? errorMessage : "", hintClassName: helperTextStyles, renderHintLeftIcon: renderHintLeftIcon })));
};

function BaseCheckBoxNormalIcon(_a) {
    var height = _a.height, width = _a.width, rest = __rest(_a, ["height", "width"]);
    return (React.createElement("svg", __assign({ width: width, height: height, viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("rect", { x: 0.5, y: 0.5, width: 15, height: 15, rx: 3.5, stroke: "#CBD5E1" })));
}
BaseCheckBoxNormalIcon.defaultProps = {
    height: 16,
    width: 16,
};

function BaseCheckBoxSelectedIcon(_a) {
    var height = _a.height, width = _a.width, rest = __rest(_a, ["height", "width"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("rect", { width: 16, height: 16, rx: 4, fill: "#2563EB" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12.64 4.232a1 1 0 0 1 .128 1.408l-5 6a1 1 0 0 1-1.475.067l-3-3a1 1 0 0 1 1.414-1.414l2.226 2.226 4.299-5.16a1 1 0 0 1 1.408-.127z", fill: "#fff" })));
}
BaseCheckBoxSelectedIcon.defaultProps = {
    height: 16,
    width: 16,
};

function CheckBoxDisabled(_a) {
    var height = _a.height, width = _a.width; _a.fill; var rest = __rest(_a, ["height", "width", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("rect", { x: 0.5, y: 0.5, width: 15, height: 15, rx: 3.5, fill: "#F8FAFC", stroke: "#E2E8F0" })));
}
CheckBoxDisabled.defaultProps = {
    height: 16,
    width: 16,
};

function CheckBoxSelectedDisabledIcon(_a) {
    var height = _a.height, width = _a.width; _a.fill; var rest = __rest(_a, ["height", "width", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("rect", { width: 16, height: 16, rx: 4, fill: "#E2E8F0" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12.64 4.232a1 1 0 0 1 .128 1.408l-5 6a1 1 0 0 1-1.475.067l-3-3a1 1 0 0 1 1.414-1.414l2.226 2.226 4.299-5.16a1 1 0 0 1 1.408-.127z", fill: "#fff" })));
}
CheckBoxSelectedDisabledIcon.defaultProps = {
    height: 16,
    width: 16,
};

function IntermediateCheckBoxIcon(_a) {
    var _b = _a.height, height = _b === void 0 ? 16 : _b, _c = _a.width, width = _c === void 0 ? 16 : _c, _d = _a.fill, fill = _d === void 0 ? "#2563EB" : _d, rest = __rest(_a, ["height", "width", "fill"]);
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, rest),
        React.createElement("rect", { width: width, height: height, rx: 4, fill: fill }),
        React.createElement("path", { d: "M11.371 8.8H4.633C4.287 8.8 4 8.437 4 8c0-.437.286-.8.632-.8h6.738c.346 0 .632.363.632.8 0 .437-.286.8-.632.8z", fill: "#fff" })));
}

function Checkbox(props) {
    var children = props.children, _a = props.className, className = _a === void 0 ? "" : _a, _b = props.labelClassName, labelClassName = _b === void 0 ? "" : _b, isDisabled = props.isDisabled, _c = props.isIndeterminate, isIndeterminate = _c === void 0 ? false : _c, _d = props.isDefaultOnly, isDefaultOnly = _d === void 0 ? false : _d, _e = props.checkboxClassName, checkboxClassName = _e === void 0 ? "" : _e, errorMessage = props.errorMessage, hint = props.hint, _f = props.hintClassName, hintClassName = _f === void 0 ? "" : _f;
    var inputRef = React__default.useRef(null);
    var state = useToggleState(props);
    var inputProps = useCheckbox(props, state, inputRef).inputProps;
    var renderDesignCheckbox = function () {
        if (state.isSelected) {
            if (isDisabled) {
                return (React__default.createElement("div", { className: cn("mr-2", checkboxClassName) },
                    React__default.createElement(CheckBoxSelectedDisabledIcon, null)));
            }
            return (React__default.createElement("div", { className: cn("mr-2 cursor-pointer", checkboxClassName) },
                React__default.createElement(BaseCheckBoxSelectedIcon, null)));
        }
        if (isDisabled) {
            return (React__default.createElement("div", { className: cn("mr-2", checkboxClassName) },
                React__default.createElement(CheckBoxDisabled, null)));
        }
        return (React__default.createElement("div", { className: cn("mr-2 cursor-pointer", checkboxClassName) },
            React__default.createElement(BaseCheckBoxNormalIcon, null)));
    };
    var renderCheckbox = function () { return (React__default.createElement("input", __assign({ className: cn("border-blue-gray-300 border-solid border-default h-4 w-4 outline-none", { "mr-2": children !== undefined }, className), style: {} }, inputProps, { ref: inputRef }))); };
    var renderConfigurableCheckbox = function () {
        if (isDefaultOnly) {
            return renderCheckbox();
        }
        else {
            return (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(VisuallyHidden, null, renderCheckbox()),
                renderDesignCheckbox()));
        }
    };
    var renderIndeterminateCheckBox = function () { return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(VisuallyHidden, null, renderCheckbox()),
        React__default.createElement(IntermediateCheckBoxIcon, { className: "mr-2" }))); };
    var hintId = useId();
    return (React__default.createElement("div", { className: cn("flex flex-col") },
        React__default.createElement("label", { className: cn("text-small leading-5 font-normal text-blue-gray-700 items-center flex", {
                "text-light-blue-grey-400 cursor-not-allowed text-blue-gray-400": isDisabled,
            }, labelClassName) },
            isIndeterminate
                ? renderIndeterminateCheckBox()
                : renderConfigurableCheckbox(),
            children),
        React__default.createElement(Hint, { id: hintId, hint: hint, errorText: errorMessage ? errorMessage : "", hintClassName: hintClassName })));
}

var CheckboxGroupContext = React__default.createContext(null);
function CheckboxGroup(props) {
    var state = useCheckboxGroupState(props);
    var _a = useCheckboxGroup(props, state), groupProps = _a.groupProps, labelProps = _a.labelProps;
    var label = props.label, _b = props.containerClassName, containerClassName = _b === void 0 ? "" : _b, _c = props.labelClassName, labelClassName = _c === void 0 ? "" : _c, errorMessage = props.errorMessage, hint = props.hint, _d = props.hintClassName, hintClassName = _d === void 0 ? "" : _d, isRequired = props.isRequired, children = props.children;
    var hintId = useId();
    return (React__default.createElement("div", __assign({}, groupProps, { className: cn("checkbox-group-container", containerClassName) }),
        React__default.createElement(Label, __assign({}, labelProps, { labelClassName: cn("checkbox-group-label mb-1", labelClassName), isRequired: isRequired }), label),
        React__default.createElement(CheckboxGroupContext.Provider, { value: state }, children),
        React__default.createElement(Hint, { id: hintId, hint: hint, errorText: errorMessage ? errorMessage : "", hintClassName: cn("checkbox-group-hint", hintClassName) })));
}
function CheckboxGroupItem(props) {
    var state = React__default.useContext(CheckboxGroupContext);
    var ref = React__default.useRef(null);
    var inputProps = useCheckboxGroupItem(props, state, ref).inputProps;
    var isDisabled = state.isDisabled || props.isDisabled;
    var isSelected = state.isSelected(props.value);
    var children = props.children, _a = props.inputClassName, inputClassName = _a === void 0 ? "" : _a, _b = props.labelClassName, labelClassName = _b === void 0 ? "" : _b, _c = props.isIndeterminate, isIndeterminate = _c === void 0 ? false : _c, _d = props.isDefaultOnly, isDefaultOnly = _d === void 0 ? false : _d, _e = props.checkboxClassName, checkboxClassName = _e === void 0 ? "" : _e;
    var renderDesignCheckbox = function () {
        if (isSelected) {
            if (isDisabled) {
                return (React__default.createElement("div", { className: cn("mr-2", "checkbox-icon-container", checkboxClassName) },
                    React__default.createElement(CheckBoxSelectedDisabledIcon, null)));
            }
            return (React__default.createElement("div", { className: cn("mr-2 cursor-pointer", "checkbox-icon-container", checkboxClassName) },
                React__default.createElement(BaseCheckBoxSelectedIcon, null)));
        }
        if (isDisabled) {
            return (React__default.createElement("div", { className: cn("mr-2", "checkbox-icon-container", checkboxClassName) },
                React__default.createElement(CheckBoxDisabled, null)));
        }
        return (React__default.createElement("div", { className: cn("mr-2 cursor-pointer", "checkbox-icon-container", checkboxClassName) },
            React__default.createElement(BaseCheckBoxNormalIcon, null)));
    };
    var renderCheckbox = function () { return (React__default.createElement("input", __assign({ className: cn("border-blue-gray-300 border-solid border-default h-4 w-4 outline-none", { "mr-2": children !== undefined }, "checkbox-group-item-input", inputClassName) }, inputProps))); };
    var renderIndeterminateCheckBox = function () { return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(VisuallyHidden, null, renderCheckbox()),
        React__default.createElement(IntermediateCheckBoxIcon, { className: "mr-2" }))); };
    var renderConfigurableCheckbox = function () {
        if (isDefaultOnly) {
            return renderCheckbox();
        }
        else {
            return (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(VisuallyHidden, null, renderCheckbox()),
                renderDesignCheckbox()));
        }
    };
    return (React__default.createElement("label", { className: cn("text-small leading-5 font-normal text-blue-gray-700 items-center flex", {
            "text-light-blue-grey-400 cursor-not-allowed text-blue-gray-400": isDisabled,
        }, "checkbox-group-item-label", labelClassName) },
        isIndeterminate
            ? renderIndeterminateCheckBox()
            : renderConfigurableCheckbox(),
        children));
}

var getBadgePaddings = function (size, className) {
    switch (size) {
        case "Basic":
            return cn("py-2px px-8px");
        case "Large":
            return cn("py-4px px-12px");
        case "Custom":
            return cn(className);
    }
};
var getBadgeVariantStyles = function (variant, customVariant) {
    switch (variant) {
        case "Neutral":
            return cn("bg-blue-gray-100 text-blue-gray-800");
        case "Success":
            return cn("bg-emerald-100 text-emerald-800");
        case "Warning":
            return cn("bg-yellow-100 text-yellow-800");
        case "Information":
            return cn("bg-blue-100 text-blue-800");
        case "Critical":
            return cn("bg-red-100 text-red-800");
        case "Custom":
            return cn(customVariant);
    }
};

function Badge(_a) {
    var children = _a.children, className = _a.className, _b = _a.rightEnhancer, rightEnhancer = _b === void 0 ? function () { return React__default.createElement("div", null); } : _b, _c = _a.leftEnhancer, leftEnhancer = _c === void 0 ? function () { return React__default.createElement("div", null); } : _c, _d = _a.variant, variant = _d === void 0 ? "Neutral" : _d, _e = _a.size, size = _e === void 0 ? "Basic" : _e, title = _a.title, customSizeClassName = _a.customSizeClassName, customVariantClassName = _a.customVariantClassName, isRounded = _a.isRounded;
    return (React__default.createElement("div", { className: cn("flex items-center text-xs leading-4 font-medium", getBadgeVariantStyles(variant, customVariantClassName !== null && customVariantClassName !== void 0 ? customVariantClassName : ""), getBadgePaddings(size, customSizeClassName !== null && customSizeClassName !== void 0 ? customSizeClassName : ""), className, "" + (isRounded ? "rounded-6px" : "rounded-16px")), title: title },
        leftEnhancer(),
        children,
        rightEnhancer()));
}

var TABLET_MIN_WIDTH = 768;

var isMobileDevice = typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    window.innerWidth < TABLET_MIN_WIDTH;

var CustomOption = function (props) {
    var label = props.label, isSelected = props.isSelected, onClick = props.onClick;
    return (React__default.createElement("div", { className: cn("px-12px py-6px rounded-12px cursor-pointer w-full hover:bg-blue-50", isSelected ? "bg-blue-50" : ""), onClick: onClick },
        React__default.createElement("div", { className: cn("flex items-center justify-between cursor-pointer") },
            React__default.createElement("label", { className: cn("text-14px text-blue-gray-700 cursor-pointer", isSelected ? "font-semibold" : "") }, label),
            isSelected && React__default.createElement(TickIcon, null))));
};

var RemoveIcon = function () { return (React.createElement("svg", { width: 12, height: 12, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "m3 9 6-6M3 3l6 6", stroke: "#2563EB", strokeWidth: 0.875, strokeLinecap: "round", strokeLinejoin: "round" }))); };

var Value = function (props) {
    var onRemove = props.onRemove, value = props.value;
    return (React__default.createElement("div", { className: cn("flex items-center justify-between bg-blue-100 rounded-4px px-12px py-4px w-auto mb-8px mr-8px", "selected-option-container") },
        React__default.createElement("span", { className: cn("text-blue-800 text-12px font-medium mr-4px") }, value),
        React__default.createElement("div", { onClick: onRemove, className: cn("cursor-pointer") },
            React__default.createElement(RemoveIcon, null))));
};

var customStyles = {
    option: function () { return ({
        padding: 8,
        width: "100%",
    }); },
    menuList: function () { return ({}); },
};
function focusStateRingColor(color) {
    switch (color) {
        case colors$1.indigo:
            return "ring-indigo-200";
        default:
            return "ring-blue-200";
    }
}
var ValueContainer = function (props) {
    var isMulti = props.isMulti;
    return (React__default.createElement(components.ValueContainer, __assign({}, props, { className: cn("flex items-center") }),
        isMulti && (React__default.createElement("div", { className: cn("mr-8px search-icon") },
            React__default.createElement(SearchIcon, null))),
        props.children));
};
function MultiSelect(props) {
    var _a = __read(React__default.useState(false), 2), isFocusWithin = _a[0], setFocusWithin = _a[1];
    var _b = __read(React__default.useState(false), 2), isBottomSheetOpen = _b[0], setBottomSheetOpen = _b[1];
    var errorMessage = props.errorMessage, hint = props.hint, _c = props.isDisabled, isDisabled = _c === void 0 ? false : _c, label = props.label, _d = props.containerClassName, containerClassName = _d === void 0 ? "" : _d, _e = props.hintClassName, hintClassName = _e === void 0 ? "text-input-hint" : _e, _f = props.labelClassName, labelClassName = _f === void 0 ? "" : _f, options = props.options, _g = props.color, color = _g === void 0 ? colors$1.blue : _g, OptionComponent = props.optionComponent, OptionBadgeComponent = props.optionBadgeComponent, ValueContainerComponent = props.valueContainer; props.selectedValueComponent; var MultiValueComponent = props.multiValueComponent, bottomSheetHeaderStyles = props.bottomSheetHeaderStyles, onChange = props.onChange, placeholder = props.placeholder, _h = props.closeOnSelect, closeOnSelect = _h === void 0 ? false : _h, _j = props.hideSelectedOptions, hideSelectedOptions = _j === void 0 ? false : _j; props.leftElement; props.rightElement; var maxValuesToShow = props.maxValuesToShow, _k = props.selectedOptions, selectedOptions = _k === void 0 ? [] : _k, _l = props.controlShouldRenderValue, controlShouldRenderValue = _l === void 0 ? false : _l, isRequired = props.isRequired, _m = props.isClearable, isClearable = _m === void 0 ? false : _m; props.className; props.ref; var _o = props.shouldRenderValue, shouldRenderValue = _o === void 0 ? true : _o, otherProps = __rest(props, ["errorMessage", "hint", "isDisabled", "label", "containerClassName", "hintClassName", "labelClassName", "options", "color", "optionComponent", "optionBadgeComponent", "valueContainer", "selectedValueComponent", "multiValueComponent", "bottomSheetHeaderStyles", "onChange", "placeholder", "closeOnSelect", "hideSelectedOptions", "leftElement", "rightElement", "maxValuesToShow", "selectedOptions", "controlShouldRenderValue", "isRequired", "isClearable", "className", "ref", "shouldRenderValue"]);
    var selectedOptionBadges = maxValuesToShow && selectedOptions.length > maxValuesToShow
        ? selectedOptions.slice(0, maxValuesToShow)
        : selectedOptions;
    var noOfRemainingOptions = maxValuesToShow && selectedOptions.length > maxValuesToShow
        ? selectedOptions.length - maxValuesToShow
        : 0;
    //NOTE: Options are generics, so keeping it as any
    var Option = function (props) {
        return (React__default.createElement(components.Option, __assign({}, props), OptionComponent ? (React__default.createElement(OptionComponent, __assign({}, props))) : (React__default.createElement(CustomOption, __assign({ isDefaultOnly: false, isSelected: props.isSelected }, props)))));
    };
    var MultiValue = function (props) { return (React__default.createElement(components.MultiValue, __assign({}, props), MultiValueComponent && React__default.createElement(MultiValueComponent, __assign({}, props)))); };
    var customComponents = {
        Option: Option,
    };
    customComponents.ValueContainer = ValueContainerComponent !== null && ValueContainerComponent !== void 0 ? ValueContainerComponent : ValueContainer;
    MultiValueComponent && (customComponents.MultiValue = MultiValue);
    isMobileDevice && (customComponents.Menu = function () { return null; });
    var focusWithinProps = useFocusWithin({
        isDisabled: false,
        onFocusWithinChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusWithinProps;
    useFocus({
        onBlur: function () {
            // Validation is disabled until the user touches / focuses the field at least once
        },
        onFocusChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusProps;
    var handleChange = function (selectedOptions) {
        onChange && onChange(selectedOptions);
    };
    var hintId = useId();
    var closeBottomSheet = function () { return setBottomSheetOpen(false); };
    var openBottomSheet = function () { return setBottomSheetOpen(true); };
    var handleChangeSelectedOptions = function (selectedOption, isSelected) {
        var updatedOptions = [];
        if (isSelected) {
            updatedOptions = __spreadArray(__spreadArray([], __read(selectedOptions), false), [selectedOption], false);
        }
        else {
            updatedOptions = selectedOptions.filter(function (option) { return option.value !== selectedOption.value; });
        }
        onChange && onChange(updatedOptions);
    };
    var isSelectedOption = function (option) {
        return selectedOptions.some(function (eachOption) { return eachOption.value === option.value; });
    };
    var renderSelectedOptionBadges = function () {
        var shouldRenderSelectedOptionBadges = selectedOptionBadges.length > 0;
        return shouldRenderSelectedOptionBadges ? (React__default.createElement("div", { className: cn("w-full flex flex-wrap items-center mb-12px") },
            selectedOptionBadges.map(function (option) {
                return OptionBadgeComponent ? (React__default.createElement(OptionBadgeComponent, { key: option.value, option: option })) : (React__default.createElement(Value, { value: option.label, key: option.value, onRemove: function () { return handleChangeSelectedOptions(option, false); } }));
            }),
            noOfRemainingOptions > 0 && (React__default.createElement("span", { className: cn("text-blue-800 text-12px font-medium px-12px py-4px mt-8px bg-blue-100") },
                "+ ",
                noOfRemainingOptions)))) : null;
    };
    var renderSearchBar = function () { return (React__default.createElement(React__default.Fragment, null,
        shouldRenderValue
            ? controlShouldRenderValue
                ? null
                : renderSelectedOptionBadges()
            : null,
        React__default.createElement(FocusRing, { autoFocus: true, within: true, focusClassProps: "ring-offset-0 " + (errorMessage && !isFocusWithin
                ? "ring-0 border-1 border-red-400"
                : isFocusWithin
                    ? "ring " + focusStateRingColor(color)
                    : "") },
            React__default.createElement(Select, __assign({ defaultValue: selectedOptions, onChange: handleChange, options: options, isMulti: true, styles: customStyles, components: customComponents, closeMenuOnSelect: closeOnSelect, hideSelectedOptions: hideSelectedOptions, isDisabled: isDisabled, value: selectedOptions, onFocus: openBottomSheet, isClearable: isClearable, placeholder: placeholder, controlShouldRenderValue: controlShouldRenderValue && shouldRenderValue, classNamePrefix: "multi-select", backspaceRemovesValue: controlShouldRenderValue && shouldRenderValue }, otherProps))))); };
    var renderMobileView = function () {
        return (React__default.createElement(MediaQuery, { maxWidth: 767 },
            React__default.createElement(BottomSheet, { isOpen: isBottomSheetOpen, hasHeader: true, hideCloseIcon: false, renderHeader: function () { return (React__default.createElement("span", { className: cn("text-blue-gray-500 font-semibold text-xs left-0 mb-0", bottomSheetHeaderStyles) }, props.label)); }, onRequestClose: closeBottomSheet, onStateChange: closeBottomSheet, headerClassName: cn("fixed w-full bg-base-white rounded-t-16px left-0 px-4 pt-4"), containerClassName: cn("pt-0 px-0 mb-0 bottom-sheet-container", containerClassName) },
                React__default.createElement("div", { className: cn("p-8px w-full", "mobile-options-container") }, options.map(function (option) { return (React__default.createElement("div", { key: option.value, className: cn("py-1", "mobile-option") }, OptionComponent ? (React__default.createElement(OptionComponent, { key: option.value, onChange: function (isSelected) {
                        return handleChangeSelectedOptions(option, isSelected);
                    }, option: option, isSelected: isSelectedOption(option) })) : (React__default.createElement(CustomOption, { key: option.value, isSelected: isSelectedOption(option), label: option.label, onClick: function () {
                        return handleChangeSelectedOptions(option, !isSelectedOption(option));
                    } })))); })))));
    };
    return (React__default.createElement("div", __assign({}, focusWithinProps, { className: cn("flex flex-col w-full", containerClassName) }),
        label ? (React__default.createElement(Label, { labelClassName: "mb-4px " + labelClassName, isRequired: isRequired }, label)) : null,
        renderSearchBar(),
        renderMobileView(),
        React__default.createElement(Hint, { id: hintId, hint: hint, errorText: errorMessage && !isFocusWithin ? errorMessage : "", hintClassName: hintClassName })));
}

var MOBILE_MAX_WIDTH = 767;
var DESKTOP_MIN_WIDTH = 768;

function ChevronLeftArrowIcon(props) {
    var isDisabled = props.isDisabled;
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", fill: "none", viewBox: "0 0 20 20" },
        React__default.createElement("path", { fill: isDisabled ? "#cbd5e1" : "#475569", fillRule: "evenodd", d: "M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z", clipRule: "evenodd" })));
}
ChevronLeftArrowIcon.defaultProps = {
    isDisabled: false,
};

function ChevronRightArrowIcon(props) {
    var isDisabled = props.isDisabled;
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", fill: "none", viewBox: "0 0 20 20" },
        React__default.createElement("path", { fill: isDisabled ? "#cbd5e1" : "#475569", fillRule: "evenodd", d: "M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z", clipRule: "evenodd" })));
}
ChevronRightArrowIcon.defaultProps = {
    isDisabled: false,
};

var CustomHeader = function (props) {
    var params = props.params, filters = props.filters, onClickFilter = props.onClickFilter, headerDisplayFormat = props.headerDisplayFormat, selectedFilterValue = props.selectedFilterValue;
    var date = params.date, decreaseMonth = params.decreaseMonth, increaseMonth = params.increaseMonth, prevMonthButtonDisabled = params.prevMonthButtonDisabled, nextMonthButtonDisabled = params.nextMonthButtonDisabled;
    var onClickPrevMonth = function () {
        if (!prevMonthButtonDisabled) {
            decreaseMonth();
        }
    };
    var onClickNextMonth = function () {
        if (!nextMonthButtonDisabled) {
            increaseMonth();
        }
    };
    return (React__default.createElement("div", null,
        filters && onClickFilter ? (React__default.createElement("ul", { className: cn("flex flex-col mr-16px") }, filters.map(function (eachFilter) {
            return (React__default.createElement("li", { key: eachFilter.value, className: cn(" flex mb-8px px-8px py-12px cursor-pointer", "" + (eachFilter.value === selectedFilterValue ? "bg-blue-50" : "")), onClick: function () { return onClickFilter(eachFilter.value); } },
                React__default.createElement("p", { className: cn("text-sm text-blue-gray-700") }, eachFilter.label)));
        }))) : null,
        React__default.createElement("div", { className: cn("flex items-center") },
            React__default.createElement(Button, { variant: "Plain", size: "Small", onClick: onClickPrevMonth, isDisabled: prevMonthButtonDisabled, className: cn("navigation-buttons") },
                React__default.createElement(ChevronLeftArrowIcon, { isDisabled: prevMonthButtonDisabled })),
            React__default.createElement("p", { className: cn("text-sm leading-5 font-semibold mx-4px text-blue-gray-700") }, format(date, headerDisplayFormat !== null && headerDisplayFormat !== void 0 ? headerDisplayFormat : "MMMM YYYY")),
            React__default.createElement(Button, { variant: "Plain", size: "Small", onClick: onClickNextMonth, isDisabled: nextMonthButtonDisabled, className: cn("navigation-buttons") },
                React__default.createElement(ChevronRightArrowIcon, { isDisabled: nextMonthButtonDisabled })))));
};

function CalendarIcon(_a) {
    var _b = _a.fill, fill = _b === void 0 ? colors.blueGray600 : _b;
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", fill: "none" },
        React__default.createElement("path", { fill: fill, d: "M6.667 4.792a.63.63 0 01-.625-.625v-2.5a.63.63 0 01.625-.625.63.63 0 01.625.625v2.5a.63.63 0 01-.625.625zm6.666 0a.63.63 0 01-.625-.625v-2.5a.63.63 0 01.625-.625.63.63 0 01.625.625v2.5a.63.63 0 01-.625.625zm-6.25 7.291a.829.829 0 01-.316-.066.86.86 0 01-.275-.175.86.86 0 01-.242-.592.83.83 0 01.067-.317c.041-.1.1-.191.175-.275a.86.86 0 01.275-.175.851.851 0 01.908.175c.15.159.242.375.242.592 0 .05-.009.108-.017.167a.53.53 0 01-.05.15.63.63 0 01-.075.15c-.025.041-.067.083-.1.125a.876.876 0 01-.592.241zm2.917 0a.83.83 0 01-.317-.066.86.86 0 01-.275-.175.86.86 0 01-.241-.592.83.83 0 01.066-.317c.042-.1.1-.191.175-.275a.86.86 0 01.275-.175.835.835 0 01.909.175c.15.159.241.375.241.592 0 .05-.008.108-.016.167a.529.529 0 01-.05.15.626.626 0 01-.075.15c-.025.041-.067.083-.1.125a.876.876 0 01-.592.241zm2.917 0a.831.831 0 01-.317-.066.862.862 0 01-.275-.175 60.77 60.77 0 00-.1-.125.634.634 0 01-.075-.15.532.532 0 01-.05-.15c-.008-.059-.017-.117-.017-.167 0-.217.092-.433.242-.592a.862.862 0 01.275-.175.833.833 0 01.908.175c.15.159.242.375.242.592 0 .05-.008.108-.017.167a.532.532 0 01-.05.15.634.634 0 01-.075.15c-.025.041-.066.083-.1.125a.876.876 0 01-.591.241zM7.083 15a.83.83 0 01-.316-.067.963.963 0 01-.275-.175.876.876 0 01-.242-.591.83.83 0 01.067-.317c.041-.108.1-.2.175-.275.308-.308.875-.308 1.183 0 .15.158.242.375.242.592a.876.876 0 01-.242.591.876.876 0 01-.592.242zM10 15a.876.876 0 01-.592-.242.876.876 0 01-.241-.591.83.83 0 01.066-.317c.042-.108.1-.2.175-.275.309-.308.875-.308 1.184 0a.777.777 0 01.175.275.84.84 0 01.066.317.876.876 0 01-.241.591A.876.876 0 0110 15zm2.917 0a.876.876 0 01-.592-.242.779.779 0 01-.175-.275.831.831 0 01-.067-.316c0-.109.025-.217.067-.317.042-.108.1-.2.175-.275a.832.832 0 01.908-.175c.05.017.1.042.15.075.042.025.084.067.125.1.15.158.242.375.242.592a.876.876 0 01-.242.591.876.876 0 01-.591.242zm4.166-6.8H2.917a.63.63 0 01-.625-.625.63.63 0 01.625-.625h14.166a.63.63 0 01.625.625.63.63 0 01-.625.625z" }),
        React__default.createElement("path", { fill: fill, d: "M13.333 18.958H6.667c-3.042 0-4.792-1.75-4.792-4.791V7.083c0-3.041 1.75-4.791 4.792-4.791h6.666c3.042 0 4.792 1.75 4.792 4.791v7.084c0 3.041-1.75 4.791-4.792 4.791zM6.667 3.542c-2.384 0-3.542 1.158-3.542 3.541v7.084c0 2.383 1.158 3.541 3.542 3.541h6.666c2.384 0 3.542-1.158 3.542-3.541V7.083c0-2.383-1.158-3.541-3.542-3.541H6.667z" })));
}

var CustomInput = forwardRef(function (_a, ref) {
    var value = _a.value; _a.onClick; _a.onChange; var onChangeDate = _a.onChangeDate, selectsRange = _a.selectsRange, setOpen = _a.setOpen, isMobile = _a.isMobile, setBlur = _a.setBlur, isDisabled = _a.isDisabled, rest = __rest(_a, ["value", "onClick", "onChange", "onChangeDate", "selectsRange", "setOpen", "isMobile", "setBlur", "isDisabled"]);
    var _b = __read(useState(value), 2), selectedValue = _b[0], setSelectedValue = _b[1];
    var onChangeValue = function (changedValue) {
        setSelectedValue(changedValue);
        if (changedValue === "") {
            if (selectsRange) {
                onChangeDate([null, null], undefined);
            }
            onChangeDate(null, undefined);
        }
    };
    useEffect(function () {
        setSelectedValue(value);
    }, [value]);
    return (React__default.createElement(TextField, __assign({ value: selectedValue, ref: ref, rightElement: function () { return (React__default.createElement("div", { onClick: function () {
                if (!isDisabled) {
                    setOpen && setOpen(true);
                }
            }, className: cn({ "cursor-pointer": !isDisabled }) },
            React__default.createElement(CalendarIcon, null))); }, onChange: onChangeValue, onFocusChange: function (isFocused) {
            if (isMobile && isFocused) {
                setBlur();
            }
        }, focusRingClassName: cn("" + (isMobile ? "ring-0 input-class" : "")), isDisabled: isDisabled }, rest, { inputContainerClassName: "hover:border-blue-gray-400" })));
});

var isMobile = function (deviceWidth) { return deviceWidth < 768; };
var isInRange = function (date, startDate, endDate) {
    if (startDate && endDate) {
        return isWithinRange(date, startDate, endDate);
    }
};
var getFormat = function (dateFormat, showTimeSelect) {
    if (!dateFormat && showTimeSelect) {
        return "DD/MM/YYYY hh:mm a";
    }
    else if (!dateFormat && !showTimeSelect) {
        return "DD/MM/YYYY";
    }
    return dateFormat;
};
var isSelectedDate = function (date, selected) {
    if (selected) {
        return (getDate(selected) === getDate(date) &&
            getMonth(selected) === getMonth(date) &&
            getYear(selected) === getYear(date));
    }
    return false;
};
var getTimeClassName = function (date, timeClassName) {
    return cn("text-xs leading-5 font-normal w-62px md:w-64px", "" + (timeClassName && timeClassName(date)));
};
var getWeekdayClassName = function (date, showTimeSelect, weekDayClassName) {
    return cn("text-xs leading-4 font-normal text-blue-gray-700 m-0 p-8px w-39px h-32px mt-8px", "" + (showTimeSelect ? "" : "mr-8px"), "" + (weekDayClassName && weekDayClassName(date)));
};
var getDayClassName = function (date, selected, selectsRange, startDate, endDate, showTimeSelect, dayClassName) {
    if (selectsRange) {
        if (startDate && isEqual(date, startDate)) {
            return cn("text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px bg-blue-600 text-base-white m-0 px-10px md:px-12px py-8px hover:bg-blue-600", "" + (showTimeSelect ? "" : "mr-8px"), "" + (dayClassName && dayClassName(date)));
        }
        if (endDate && isEqual(date, endDate)) {
            return cn("text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px bg-blue-600 text-base-white m-0 px-10px md:px-12px py-8px hover:bg-blue-600", "" + (showTimeSelect ? "" : "mr-8px"), "" + (dayClassName && dayClassName(date)));
        }
        if (isInRange(date, startDate, endDate)) {
            return cn("text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px text-blue-gray-700 m-0 px-10px md:px-12px py-8px bg-blue-50 hover:bg-blue-50", "" + (showTimeSelect ? "" : "mr-8px"), "" + (dayClassName && dayClassName(date)));
        }
    }
    return isSelectedDate(date, selected)
        ? cn("text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px bg-blue-600 text-base-white m-0 px-10px md:px-12px py-8px hover:bg-blue-600", "" + (showTimeSelect ? "" : "mr-8px"), "" + (dayClassName && dayClassName(date)))
        : cn("text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px text-blue-gray-700 m-0 px-10px md:px-12px py-8px hover:bg-blue-50", "" + (showTimeSelect ? "" : "mr-8px"), "" + (dayClassName && dayClassName(date)));
};
var getPopperClassName = function (popperClassName) {
    return cn("bg-base-white rounded-8px border-solid border-1 border-blue-gray-200 pt-16px pl-16px pb-12px mt-8px", "popper", popperClassName);
};
function getDefaultLocale() {
    var scope = typeof window !== "undefined" ? window : global;
    return scope.__localeId__;
}
function getLocaleObject(localeSpec) {
    if (typeof localeSpec === "string") {
        // Treat it as a locale name registered by registerLocale
        var scope = typeof window !== "undefined" ? window : global;
        return scope.__localeData__ ? scope.__localeData__[localeSpec] : null;
    }
    else {
        // Treat it as a raw date-fns locale object
        return localeSpec;
    }
}
function formatDate(date, formatStr, locale) {
    if (locale === "en") {
        return format(date, formatStr);
    }
    var localeObj = getLocaleObject(locale);
    if (locale && !localeObj) {
        console.warn("A locale object was not found for the provided string [\"" + locale + "\"].");
    }
    if (!localeObj &&
        !!getDefaultLocale() &&
        !!getLocaleObject(getDefaultLocale())) {
        localeObj = getLocaleObject(getDefaultLocale());
    }
    return format(date, formatStr, {
        locale: localeObj ? localeObj : null,
    });
}
function safeDateFormat(date, _a) {
    var dateFormat = _a.dateFormat, locale = _a.locale;
    return ((date &&
        formatDate(date, Array.isArray(dateFormat) ? dateFormat[0] : dateFormat, locale)) ||
        "");
}
function safeDateRangeFormat(startDate, endDate, props) {
    if (!startDate) {
        return "";
    }
    var formattedStartDate = safeDateFormat(startDate, props);
    var formattedEndDate = endDate ? safeDateFormat(endDate, props) : "";
    return formattedStartDate + " - " + formattedEndDate;
}
var getSelectedFilterLabel = function (filters, selectedFilterValue) {
    var _a, _b;
    return (_b = (_a = filters === null || filters === void 0 ? void 0 : filters.find(function (eachFilter) { return eachFilter.value === selectedFilterValue; })) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : "";
};

var useResize = function () {
    var _a = __read(useState(window.innerWidth), 2), deviceWidth = _a[0], changeDeviceWidth = _a[1];
    useEffect(function () {
        var resizeW = function () { return changeDeviceWidth(window.innerWidth); };
        window.addEventListener("resize", resizeW);
        return function () { return window.removeEventListener("resize", resizeW); };
    }, [deviceWidth]);
    return deviceWidth;
};

var DatePicker = function (props) {
    var onChange = props.onChange, selected = props.selected, dateFormat = props.dateFormat, showTimeSelect = props.showTimeSelect, popperClassName = props.popperClassName, weekDayClassName = props.weekDayClassName, dayClassName = props.dayClassName, timeClassName = props.timeClassName, selectsRange = props.selectsRange, startDate = props.startDate, endDate = props.endDate, inputProps = props.inputProps, filters = props.filters, onClickFilter = props.onClickFilter, headerDisplayFormat = props.headerDisplayFormat, selectedFilterValue = props.selectedFilterValue, locale = props.locale, _a = props.shouldCloseOnSelect, shouldCloseOnSelect = _a === void 0 ? false : _a, _b = props.enableCustomHeader, enableCustomHeader = _b === void 0 ? false : _b, disabled = props.disabled, rest = __rest(props, ["onChange", "selected", "dateFormat", "showTimeSelect", "popperClassName", "weekDayClassName", "dayClassName", "timeClassName", "selectsRange", "startDate", "endDate", "inputProps", "filters", "onClickFilter", "headerDisplayFormat", "selectedFilterValue", "locale", "shouldCloseOnSelect", "enableCustomHeader", "disabled"]);
    var deviceWidth = useResize();
    var datePickerRef = useRef();
    var requestCalendarClose = function () {
        var _a;
        (_a = datePickerRef.current) === null || _a === void 0 ? void 0 : _a.setOpen(false);
    };
    var onChangeDate = function (date, event) {
        if (selectedFilterValue) {
            onClickFilter && onClickFilter("");
        }
        onChange(date, event);
    };
    var CalendarContainer = function (props) {
        var _a;
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(MediaQuery, { maxWidth: MOBILE_MAX_WIDTH },
                React__default.createElement(BottomSheet, { isOpen: (_a = datePickerRef.current) === null || _a === void 0 ? void 0 : _a.isCalendarOpen(), containerClassName: cn("bottom-sheet-container"), overlayClassName: cn("bg-blue-gray-600"), hideCloseIcon: true, onRequestClose: requestCalendarClose }, props.children)),
            React__default.createElement(MediaQuery, { minWidth: DESKTOP_MIN_WIDTH },
                React__default.createElement("div", { className: cn("bg-base-white md:flex") }, props.children))));
    };
    var onClickFilterItem = function (value) {
        var _a;
        if (onClickFilter) {
            onClickFilter(value);
            if (shouldCloseOnSelect) {
                (_a = datePickerRef.current) === null || _a === void 0 ? void 0 : _a.setOpen(false);
            }
            if (selected) {
                onChange(null, undefined);
            }
        }
    };
    var renderCustomHeader = function (params) { return (React__default.createElement(CustomHeader, { params: params, filters: filters, onClickFilter: onClickFilterItem, headerDisplayFormat: headerDisplayFormat, selectedFilterValue: selectedFilterValue })); };
    var getInputValue = function () {
        if (selectedFilterValue) {
            return getSelectedFilterLabel(filters, selectedFilterValue);
        }
        else if (selectsRange) {
            return safeDateRangeFormat(startDate !== null && startDate !== void 0 ? startDate : "", endDate !== null && endDate !== void 0 ? endDate : "", {
                dateFormat: getFormat(dateFormat, showTimeSelect),
                locale: locale,
            });
        }
        else if (selected) {
            return safeDateFormat(selected, {
                dateFormat: getFormat(dateFormat, showTimeSelect),
                locale: locale,
            });
        }
        return "";
    };
    var datePickerProps = {};
    if (enableCustomHeader) {
        datePickerProps.renderCustomHeader = renderCustomHeader;
    }
    if (!enableCustomHeader) {
        datePickerProps.showYearDropdown = true;
        datePickerProps.showMonthDropdown = true;
        datePickerProps.scrollableYearDropdown = true;
    }
    var onCalendarOpen = function () {
        document.body.classList.add("body-no-scroll");
    };
    var onCalendarClose = function () {
        document.body.classList.remove("body-no-scroll");
    };
    return (React__default.createElement(DatePickerComponent, __assign({ value: getInputValue(), placeholderText: "Select a Date", ref: datePickerRef, selected: selected, startDate: startDate, endDate: endDate, useWeekdaysShort: true, showTimeSelect: showTimeSelect, selectsRange: selectsRange, withPortal: isMobile(deviceWidth), dateFormat: getFormat(dateFormat, showTimeSelect), onChange: onChangeDate, customInput: React__default.createElement(CustomInput, __assign({ key: "" + selectedFilterValue + selected, value: getInputValue(), onClick: function () { }, onChangeDate: onChangeDate, selectsRange: selectsRange, setOpen: function (open) {
                var _a;
                (_a = datePickerRef.current) === null || _a === void 0 ? void 0 : _a.setOpen(open);
            }, setBlur: function () {
                var _a;
                (_a = datePickerRef.current) === null || _a === void 0 ? void 0 : _a.setBlur();
            }, isMobile: isMobile(deviceWidth), isDisabled: disabled }, inputProps)), calendarContainer: function (props) { return React__default.createElement(CalendarContainer, __assign({}, props)); }, weekDayClassName: function (date) {
            return getWeekdayClassName(date, showTimeSelect, weekDayClassName);
        }, popperClassName: getPopperClassName(popperClassName), dayClassName: function (date) {
            return getDayClassName(date, selected, selectsRange, startDate, endDate, showTimeSelect, dayClassName);
        }, timeClassName: function (date) { return getTimeClassName(date, timeClassName); }, locale: locale, shouldCloseOnSelect: shouldCloseOnSelect, disabled: disabled, onCalendarOpen: onCalendarOpen, onCalendarClose: onCalendarClose }, datePickerProps, rest)));
};

function CameraIcon() {
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", fill: "none" },
        React__default.createElement("path", { fill: "#16A34A", fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z", clipRule: "evenodd" })));
}

var DangerIcon = function (props) { return (React.createElement("svg", __assign({ width: 24, height: 24, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
    React.createElement("path", { d: "M21.76 15.92 15.36 4.4C14.5 2.85 13.31 2 12 2s-2.5.85-3.36 2.4l-6.4 11.52c-.81 1.47-.9 2.88-.25 3.99.65 1.11 1.93 1.72 3.61 1.72h12.8c1.68 0 2.96-.61 3.61-1.72.65-1.11.56-2.53-.25-3.99ZM11.25 9c0-.41.34-.75.75-.75s.75.34.75.75v5c0 .41-.34.75-.75.75s-.75-.34-.75-.75V9Zm1.46 8.71-.15.12c-.06.04-.12.07-.18.09-.06.03-.12.05-.19.06-.06.01-.13.02-.19.02s-.13-.01-.2-.02a.636.636 0 0 1-.18-.06.757.757 0 0 1-.18-.09l-.15-.12c-.18-.19-.29-.45-.29-.71 0-.26.11-.52.29-.71l.15-.12c.06-.04.12-.07.18-.09.06-.03.12-.05.18-.06.13-.03.27-.03.39 0 .07.01.13.03.19.06.06.02.12.05.18.09l.15.12c.18.19.29.45.29.71 0 .26-.11.52-.29.71Z", fill: "#F59E0B" }))); };

var InfoIcon = function (props) { return (React.createElement("svg", __assign({ width: 24, height: 24, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
    React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M21.6 12a9.6 9.6 0 1 1-19.2 0 9.6 9.6 0 0 1 19.2 0Zm-8.4-4.8a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Zm-2.4 3.6a1.2 1.2 0 0 0 0 2.4v3.6A1.2 1.2 0 0 0 12 18h1.2a1.2 1.2 0 1 0 0-2.4V12a1.2 1.2 0 0 0-1.2-1.2h-1.2Z", fill: "#3B82F6" }))); };

function FailureIcon() {
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", fill: "none" },
        React__default.createElement("path", { fill: "#EF4444", fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z", clipRule: "evenodd" })));
}

var ToastTypes = {
    success: "SUCCESS",
    failure: "FAILURE",
    info: "INFO",
    warning: "WARNING",
    copy: "COPY",
};

var getContainerStyles = function (color, toastType) {
    var defaultStyles = "flex shadow-xl items-start px-16px py-12px rounded-12px";
    if (toastType === ToastTypes.copy)
        return defaultStyles + " " + getColorClassesOf(color).bg900 + " text-base-white";
    return defaultStyles + " " + getColorClassesOf(color).bg100 + " " + getColorClassesOf(color).text800;
};
var getDeviceBasedStyles = function () {
    return isMobile$1 ? "w-312px" : "w-800px";
};

var Toast = function (props) {
    var title = props.title, _a = props.toastType, toastType = _a === void 0 ? "INFO" : _a, _b = props.dismissToast, dismissToast = _b === void 0 ? function () { } : _b, color = props.color, _c = props.description, description = _c === void 0 ? "" : _c, _d = props.actions, actions = _d === void 0 ? [] : _d, _e = props.noteList, noteList = _e === void 0 ? [] : _e, rightLink = props.rightLink, renderTitleIcon = props.renderTitleIcon, _f = props.showDismissButton, showDismissButton = _f === void 0 ? true : _f;
    var success = ToastTypes.success, failure = ToastTypes.failure, info = ToastTypes.info, warning = ToastTypes.warning, copy = ToastTypes.copy;
    var renderIconBasedOnToastType = function () {
        if (renderTitleIcon)
            return renderTitleIcon();
        switch (toastType) {
            case success:
                return React__default.createElement(CameraIcon, null);
            case failure:
                return React__default.createElement(FailureIcon, null);
            case warning:
                return React__default.createElement(DangerIcon, null);
            case copy:
                return null;
            case info:
            default:
                return React__default.createElement(InfoIcon, null);
        }
    };
    var getToastColor = function () {
        if (color)
            return color;
        switch (toastType) {
            case success:
                return "green";
            case failure:
                return "red";
            case warning:
                return "amber";
            case copy:
                return "blue-gray";
            case info:
            default:
                return "blue";
        }
    };
    var renderAdditionalInfo = function () {
        return (React__default.createElement("div", null,
            description ? (React__default.createElement("span", { className: "text-sm leading-5 font-normal " + getColorClassesOf(getToastColor()).text700 }, description)) : null,
            noteList.length ? (React__default.createElement("ul", { className: "list-disc ml-18px" }, noteList.map(function (note, index) { return (React__default.createElement("li", { className: " text-sm leading-5 font-normal " + getColorClassesOf(getToastColor()).text700, key: index }, note)); }))) : null,
            actions.length ? (React__default.createElement("div", { className: "flex items-center mt-16px" }, actions.map(function (action) { return (React__default.createElement(Button, { variant: "Plain", className: getColorClassesOf(getToastColor()).text800 + " pl-0", key: action.value, onClick: action.onClickAction, size: "Small" }, action.label)); }))) : null));
    };
    return (React__default.createElement("div", { className: getContainerStyles(getToastColor(), toastType) + " " + getDeviceBasedStyles() },
        React__default.createElement("div", { className: "mr-2" }, renderIconBasedOnToastType()),
        React__default.createElement("div", { className: "flex justify-center flex-col" },
            React__default.createElement("span", { className: "text-sm leading-5 font-medium" }, title),
            renderAdditionalInfo()),
        showDismissButton || rightLink ? (rightLink ? (React__default.createElement(Button, { variant: "Plain", onClick: function () { return rightLink.onClickLink(); }, rightIcon: rightLink.renderIcon, className: "ml-auto " + getColorClassesOf(getToastColor()).text800 + " pt-0 pb-0" }, rightLink.label)) : (React__default.createElement("div", { className: "ml-auto", onClick: function () { return dismissToast && dismissToast(); } },
            React__default.createElement(CrossIcon, { fill: "currentColor" })))) : null));
};
var ToastContainer = function (props) { return (React__default.createElement(ToastContainer$1, __assign({ position: "bottom-center", autoClose: 1000, hideProgressBar: true, newestOnTop: true, closeOnClick: true, rtl: false, draggable: true, pauseOnHover: true }, props))); };

var getPopoverPositionStylesFn = function (positionProps, triggerRef, inputRef) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    return __assign(__assign({}, positionProps.style), { maxHeight: typeof ((_a = positionProps.style) === null || _a === void 0 ? void 0 : _a.maxHeight) === "string"
            ? parseInt(positionProps.style.maxHeight) - 5
            : ((_b = positionProps.style) === null || _b === void 0 ? void 0 : _b.maxHeight)
                ? positionProps.style.maxHeight - 5
                : undefined, paddingTop: "2px", paddingBottom: "2px", width: (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) && (triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current)
            ? ((_c = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth) + ((_d = inputRef.current) === null || _d === void 0 ? void 0 : _d.offsetWidth) + 24
            : 0, top: typeof ((_e = positionProps.style) === null || _e === void 0 ? void 0 : _e.top) === "string"
            ? parseInt(positionProps.style.top) + 12
            : ((_f = positionProps.style) === null || _f === void 0 ? void 0 : _f.top)
                ? positionProps.style.top + 12
                : undefined, left: typeof ((_g = positionProps.style) === null || _g === void 0 ? void 0 : _g.left) === "string"
            ? parseInt(positionProps.style.left) + 4
            : ((_h = positionProps.style) === null || _h === void 0 ? void 0 : _h.left)
                ? positionProps.style.left + 4
                : undefined });
};
function ComboBoxComponent(props) {
    var _a;
    var inputRef = React__default.useRef(null);
    var error = props.error, isDisabled = props.isDisabled, autoFocus = props.autoFocus, helperText = props.helperText, fieldContainerStyles = props.fieldContainerStyles, wrapperWithPopOverStyles = props.wrapperWithPopOverStyles, inputFieldStyles = props.inputFieldStyles, buttonStyles = props.buttonStyles, popOverStyles = props.popOverStyles, _b = props.overlayOptions, overlayOptions = _b === void 0 ? {} : _b, _c = props.getPopoverPositionStyles, getPopoverPositionStyles = _c === void 0 ? getPopoverPositionStylesFn : _c, label = props.label, _d = props.listboxLayoutOptions, listboxLayoutOptions = _d === void 0 ? {} : _d, loadingState = props.loadingState, onLoadMore = props.onLoadMore, name = props.name;
    var isAsync = loadingState != null;
    var popoverRef = useRef(null);
    var unwrappedPopoverRef = useUnwrapDOMRef(popoverRef);
    var triggerRef = useRef(null);
    var unwrappedTriggerRef = useUnwrapDOMRef(triggerRef);
    var listBoxRef = useRef(null);
    var contains = useFilter({ sensitivity: "base" }).contains;
    var state = useComboBoxState(__assign(__assign({}, props), { allowsEmptyCollection: isAsync, defaultFilter: contains, autoFocus: false }));
    var _e = __read(useState(false), 2), isFocus = _e[0], setFocus = _e[1];
    var _f = useComboBox(__assign(__assign({}, props), { inputRef: inputRef, buttonRef: unwrappedTriggerRef, listBoxRef: listBoxRef, popoverRef: unwrappedPopoverRef, autoFocus: false }), state), triggerProps = _f.buttonProps, inputProps = _f.inputProps, labelProps = _f.labelProps;
    var layout = useListBoxLayout(state, listboxLayoutOptions);
    var onFocus = function (e) {
        var onFocus = inputProps.onFocus;
        setFocus(true);
        if (onFocus)
            onFocus(e);
    };
    var onBlur = function (e) {
        var onBlur = inputProps.onBlur;
        setFocus(false);
        if (onBlur)
            onBlur(e);
    };
    var onChange = function (e) {
        var onChange = inputProps.onChange;
        var onInputChange = props.onInputChange;
        if (onInputChange)
            onInputChange(e.target.value);
        if (onChange)
            onChange(e);
    };
    var hoverProps = useHover({ isDisabled: isDisabled }).hoverProps;
    var _g = __read(React__default.useState(false), 2); _g[0]; var setFocusWithin = _g[1];
    var focusProps = useFocus({
        onFocusChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusProps;
    function focusStateRingColor(color) {
        switch (color) {
            case colors$1.indigo:
                return "ring-indigo-200";
            default:
                return "ring-blue-200";
        }
    }
    var ref = React__default.useRef(null);
    var menuProps = useSelect(props, state, ref).menuProps;
    var isMobileDevice = useMediaQuery$1({
        query: "(max-width: 768px)",
    });
    var _h = useOverlayPosition(__assign({ targetRef: unwrappedTriggerRef, overlayRef: unwrappedPopoverRef, scrollRef: listBoxRef, offset: 0, isOpen: state.isOpen && !isMobileDevice, placement: positionConstants.bottomEnd, onClose: state.close }, overlayOptions)), positionProps = _h.overlayProps, placement = _h.placement;
    var renderNoResults = function () { return (React__default.createElement("div", { className: cn("m-auto pb-3 w-full h-full flex items-center justify-center flex-col") },
        !(isAsync && loadingState === "loading") && React__default.createElement(NoResultsFoundIcon, null),
        React__default.createElement("p", { className: cn("text-center text-blue-gray-600 font-medium") }, isAsync && loadingState === "loading"
            ? "Loading..."
            : "No Results Found"))); };
    var listBox = (React__default.createElement(FocusScope, { restoreFocus: true },
        React__default.createElement(_ListBoxBase, __assign({}, menuProps, { state: state, ref: listBoxRef, layout: layout, shouldSelectOnPressUp: true, focusOnPointerEnter: true, isLoading: loadingState === "loadingMore" || loadingState === "filtering", onLoadMore: onLoadMore, renderEmptyState: renderNoResults, UNSAFE_style: { maxHeight: (_a = positionProps.style) === null || _a === void 0 ? void 0 : _a.maxHeight }, UNSAFE_className: cn("outline-none") }))));
    var popover = (React__default.createElement(_Popover, { isOpen: state.isOpen, onClose: state.close, popOverStyles: popOverStyles, ref: popoverRef, style: getPopoverPositionStyles(positionProps, unwrappedTriggerRef, inputRef), placement: placement }, listBox));
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(MediaQuery, { minWidth: 769 },
            React__default.createElement("div", { className: cn("relative", fieldContainerStyles) },
                React__default.createElement(Label, __assign({}, labelProps, { labelClassName: cn("mb-1") }), label),
                React__default.createElement(FocusRing, { autoFocus: autoFocus, within: true, focusClassProps: "ring-offset-0 " + (error
                        ? "ring-0 border-1 border-red-400"
                        : isFocus
                            ? "ring " + focusStateRingColor(colors$1.blue)
                            : "") },
                    React__default.createElement("div", { className: getComboBoxWrapperWithPopOverStyles(isFocus, error, isDisabled, wrapperWithPopOverStyles) },
                        React__default.createElement("input", __assign({}, inputProps, { ref: inputRef, onFocus: onFocus, onBlur: onBlur, onChange: onChange, className: cn("outline-none h-20px", inputFieldStyles) })),
                        React__default.createElement(HiddenSelect, { state: state, triggerRef: unwrappedTriggerRef, label: label, name: name }),
                        React__default.createElement(PressResponder, __assign({}, mergeProps(hoverProps, triggerProps)),
                            React__default.createElement(ComboBoxButton, { buttonStyles: cn(buttonStyles, "py-6px"), state: state, triggerProps: triggerProps, ref: triggerRef, hoverProps: hoverProps, focusProps: focusProps })))),
                React__default.createElement(Hint, { id: "combobox-hint", errorText: error, hint: helperText }),
                popover)),
        React__default.createElement(MediaQuery, { maxWidth: 768 },
            React__default.createElement(MobileComboBox, __assign({}, props, { comboboxButtonClassname: cn("shadow-small1") })))));
}

function ComboBox(props) {
    //NOTE: react-aria supports Internalization (uses Intl.PluralRules) which is only supported in iOS >=13
    if (typeof Intl.PluralRules !== "function") {
        throw new InternationalizationNotSupportedError("Internationalization is not supported");
    }
    var label = props.label, options = props.options, onSelectionChange = props.onSelectionChange, selectedKey = props.selectedKey, placeholder = props.placeholder, getListItemKey = props.getListItemKey, RenderListView = props.renderListItem, onBlur = props.onBlur, inputValue = props.inputValue, onInputChange = props.onInputChange, onOpenChange = props.onOpenChange, error = props.error, isDisabled = props.isDisabled, apiStatus = props.apiStatus, _a = props.dropDownIcon, dropDownIcon = _a === void 0 ? DropDownIcon$1 : _a, _b = props.selectedOptionIcon, selectedOptionIcon = _b === void 0 ? TickIcon : _b, autoFocus = props.autoFocus, fieldContainerStyles = props.fieldContainerStyles, wrapperWithPopOverStyles = props.wrapperWithPopOverStyles, inputFieldStyles = props.inputFieldStyles, buttonStyles = props.buttonStyles, popOverStyles = props.popOverStyles, listBoxStyles = props.listBoxStyles, listItemStyles = props.listItemStyles, itemContentStyles = props.itemContentStyles, overlayOptions = props.overlayOptions, getListItemTextValue = props.getListItemTextValue; props.name; var others = __rest(props, ["label", "options", "onSelectionChange", "selectedKey", "placeholder", "getListItemKey", "renderListItem", "onBlur", "inputValue", "onInputChange", "onOpenChange", "error", "isDisabled", "apiStatus", "dropDownIcon", "selectedOptionIcon", "autoFocus", "fieldContainerStyles", "wrapperWithPopOverStyles", "inputFieldStyles", "buttonStyles", "popOverStyles", "listBoxStyles", "listItemStyles", "itemContentStyles", "overlayOptions", "getListItemTextValue", "name"]);
    return (React__default.createElement(ComboBoxComponent, __assign({ label: label, onSelectionChange: onSelectionChange, selectedKey: selectedKey, placeholder: placeholder, onBlur: onBlur, defaultItems: options, inputValue: inputValue, onInputChange: onInputChange, onOpenChange: onOpenChange, error: error, isDisabled: isDisabled, apiStatus: apiStatus, dropDownIcon: dropDownIcon, autoFocus: autoFocus, fieldContainerStyles: fieldContainerStyles, wrapperWithPopOverStyles: wrapperWithPopOverStyles, inputFieldStyles: inputFieldStyles, buttonStyles: buttonStyles, popOverStyles: popOverStyles, listBoxStyles: listBoxStyles, listItemStyles: listItemStyles, itemContentStyles: itemContentStyles, selectedOptionIcon: selectedOptionIcon, overlayOptions: overlayOptions }, others), function (eachItem) { return (React__default.createElement(Item, { key: getListItemKey(eachItem), textValue: getListItemTextValue
            ? getListItemTextValue(eachItem)
            : getListItemKey(eachItem) }, RenderListView ? (React__default.createElement(RenderListView, { item: eachItem })) : (React__default.createElement(ListElement, { item: eachItem })))); }));
}

var BandedDropdownOpenStateIncorrectError = /** @class */ (function (_super) {
    __extends(BandedDropdownOpenStateIncorrectError, _super);
    function BandedDropdownOpenStateIncorrectError(message, isOpen, defaultOpen) {
        var _this = _super.call(this, message) || this;
        _this.isOpen = isOpen;
        _this.defaultOpen = defaultOpen;
        return _this;
    }
    return BandedDropdownOpenStateIncorrectError;
}(Error));

var OptionContext = React__default.createContext({
    labelProps: {},
    descriptionProps: {},
});
function Option(_a) {
    var item = _a.item, state = _a.state, SelectedOptionIcon = _a.selectedOptionIcon, listItemStyles = _a.listItemStyles, selectedListItemStyles = _a.selectedListItemStyles, itemContentStyles = _a.itemContentStyles, CustomOption = _a.customOption;
    var ref = React__default.useRef(null);
    var _b = useOption({
        key: item.key,
        shouldSelectOnPressUp: false,
    }, state, ref), optionProps = _b.optionProps, labelProps = _b.labelProps, descriptionProps = _b.descriptionProps, isSelected = _b.isSelected, isFocused = _b.isFocused;
    var _c = useFocusRing(); _c.isFocusVisible; var focusProps = _c.focusProps;
    var renderOption = function () {
        return CustomOption ? (React__default.createElement(CustomOption, { optionProps: __assign({}, optionProps), optionRef: ref, item: item, labelProps: labelProps, descriptionProps: descriptionProps, isSelected: isSelected, isFocused: isFocused })) : (React__default.createElement("li", __assign({ className: cn("text-14px flex items-center justify-between text-blue-gray-700 py-2 px-4 my-6px rounded-12px focus:outline-none hover:bg-blue-50 cursor-pointer " + (isFocused && "bg-blue-50 text-blue-600") + " " + (isSelected
                ? "bg-blue-50 text-blue-600 font-medium " + selectedListItemStyles
                : "bg-base-white font-normal"), listItemStyles) }, optionProps, mergeProps(optionProps, focusProps), { ref: ref }),
            React__default.createElement("div", { className: cn("flex items-center", itemContentStyles) },
                React__default.createElement(OptionContext.Provider, { value: { labelProps: labelProps, descriptionProps: descriptionProps } }, item.rendered)),
            isSelected && SelectedOptionIcon && React__default.createElement(SelectedOptionIcon, null)));
    };
    return renderOption();
}

var getListBoxContainerStyles = function (isSearchable) {
    return cn(" flex justify-between items-center bg-base-white w-full rounded-t-16px flex-shrink-0 z-l10");
};
var getListBoxLabelStyles = function () {
    return cn("text-blue-gray-500 font-semibold text-xs left-0 mb-0 px-4 pt-20px pb-3");
};
var getMobileListBoxStyles = function (mobileListBoxStyles) {
    return cn("list-none p-0 w-full overflow-auto bg-base-white px-2 mt-2 flex-1 overflow-y-auto", mobileListBoxStyles);
};
var getListBoxStyles = function (listBoxStyles, isMobile) {
    return cn("list-none p-0 w-full overflow-auto px-2", listBoxStyles, {
        "mt-54px": isMobile,
    });
};
var getCloseIconContainerStyles = function () {
    return cn("px-2 pt-20px pb-3 cursor-pointer");
};

function ListBox(props) {
    var ref = React__default.useRef(null);
    var _a = __read(useState(""), 2), searchKey = _a[0], setSearchKey = _a[1];
    var _b = props.listBoxRef, listBoxRef = _b === void 0 ? ref : _b, state = props.state, selectedOptionIcon = props.selectedOptionIcon, listBoxStyles = props.listBoxStyles, listItemStyles = props.listItemStyles, itemContentStyles = props.itemContentStyles, customOption = props.customOption, mobileListBoxStyles = props.mobileListBoxStyles, label = props.label, isDesktop = props.isDesktop, isMobile = props.isMobile, isSearchable = props.isSearchable, _c = props.noOptionsMessage, noOptionsMessage = _c === void 0 ? "No Results Found" : _c, _d = props.searchInputPlaceholder, searchInputPlaceholder = _d === void 0 ? "Search" : _d, _e = props.shouldFilterOptions, shouldFilterOptions = _e === void 0 ? true : _e, onInputChange = props.onInputChange, selectedListItemStyles = props.selectedListItemStyles;
    var _f = __read(useState(__spreadArray([], __read(state.collection), false)), 2), filteredItems = _f[0], setFilteredItems = _f[1];
    var listBoxProps = useListBox(props, state, listBoxRef).listBoxProps;
    useEffect(function () {
        var filteredArray = __spreadArray([], __read(state.collection), false).filter(function (item) {
            return item.value.label.toLowerCase().includes(searchKey.toLowerCase());
        });
        shouldFilterOptions
            ? setFilteredItems(filteredArray)
            : setFilteredItems(__spreadArray([], __read(state.collection), false));
    }, [searchKey, state]);
    var renderNoResults = function () { return (React__default.createElement("div", { className: cn("m-auto pb-3 w-full h-full flex items-center justify-center flex-col") },
        React__default.createElement(NoResultsFoundIcon, null),
        React__default.createElement("p", { className: cn("text-center text-blue-gray-600 font-medium") }, noOptionsMessage))); };
    var onChangeInput = function (value) {
        setSearchKey(value);
        onInputChange && onInputChange(value);
    };
    var onClickClearIcon = function (e) {
        e.preventDefault();
        e.stopPropagation();
        state.open();
        setSearchKey("");
        onInputChange && onInputChange("");
    };
    var clearIcon = function () {
        return searchKey ? (React__default.createElement("div", { className: cn(" cursor-pointer"), onClick: onClickClearIcon },
            React__default.createElement(CrossIcon, null))) : null;
    };
    return (React__default.createElement(FocusScope, { contain: true, restoreFocus: true, autoFocus: true },
        React__default.createElement(MediaQuery, { maxWidth: 768 },
            React__default.createElement(React__default.Fragment, null,
                !isDesktop && (React__default.createElement("div", { className: getListBoxContainerStyles() },
                    isSearchable ? (React__default.createElement(TextField, { rightElement: clearIcon, containerClassName: cn("ml-8px w-3/4 xsm:w-4/5 sm:w-full mt-8px pb-2"), leftElement: SearchIcon, placeholder: searchInputPlaceholder, value: searchKey, onChange: function (e) {
                            onChangeInput(e);
                        }, inputContainerClassName: cn("rounded-12px"), shouldShowHint: false })) : (React__default.createElement("span", { className: getListBoxLabelStyles() }, label)),
                    React__default.createElement("div", { onClick: function () { return state.close(); }, className: getCloseIconContainerStyles() },
                        React__default.createElement("p", { className: cn("h-full text-center font-medium text-blue-600") }, "Cancel")))),
                React__default.createElement("ul", __assign({ className: getMobileListBoxStyles(mobileListBoxStyles) }, listBoxProps, { ref: listBoxRef }), filteredItems.length
                    ? filteredItems.map(function (item) { return (React__default.createElement(Option, { key: item.key, item: item, state: state, selectedOptionIcon: selectedOptionIcon, listItemStyles: listItemStyles, itemContentStyles: itemContentStyles, customOption: customOption, selectedListItemStyles: selectedListItemStyles })); })
                    : renderNoResults()))),
        React__default.createElement(MediaQuery, { minWidth: 769 },
            React__default.createElement(React__default.Fragment, null,
                isMobile && (React__default.createElement("div", { className: getListBoxContainerStyles() },
                    isSearchable ? (React__default.createElement(TextField, { rightElement: clearIcon, containerClassName: cn("ml-8px w-full mt-8px pb-2"), leftElement: SearchIcon, placeholder: searchInputPlaceholder, value: searchKey, onChange: function (e) {
                            onChangeInput(e);
                        }, shouldShowHint: false, inputContainerClassName: cn("rounded-12px") })) : (React__default.createElement("span", { className: getListBoxLabelStyles() }, label)),
                    React__default.createElement("div", { onClick: function () { return state.close(); }, className: getCloseIconContainerStyles() },
                        React__default.createElement("p", { className: cn("h-full text-center font-medium text-blue-600") }, "Cancel")))),
                !isMobile && isSearchable && (React__default.createElement(TextField, { containerClassName: cn("w-unset m-8px pb-2 border-0"), leftElement: SearchIcon, placeholder: searchInputPlaceholder, value: searchKey, onChange: function (e) {
                        onChangeInput(e);
                    }, shouldShowHint: false, inputContainerClassName: cn("rounded-12px") })),
                React__default.createElement("ul", __assign({ className: getListBoxStyles(listBoxStyles, isMobile) }, listBoxProps, { ref: listBoxRef }), filteredItems.length
                    ? filteredItems.map(function (item) { return (React__default.createElement(Option, { key: item.key, item: item, state: state, selectedOptionIcon: selectedOptionIcon, listItemStyles: listItemStyles, itemContentStyles: itemContentStyles, customOption: customOption, selectedListItemStyles: selectedListItemStyles })); })
                    : renderNoResults())))));
}

function Popover(props) {
    var ref = React.useRef(null);
    var _a = props.popoverRef, popoverRef = _a === void 0 ? ref : _a, isOpen = props.isOpen, onClose = props.onClose, children = props.children, popOverStyles = props.popOverStyles, otherProps = __rest(props, ["popoverRef", "isOpen", "onClose", "children", "popOverStyles"]);
    var overlayProps = useOverlay({
        isOpen: isOpen,
        onClose: onClose,
        shouldCloseOnBlur: true,
        isDismissable: true,
    }, popoverRef).overlayProps;
    var modalProps = useModal().modalProps;
    var dialogProps = useDialog({}, ref).dialogProps;
    return (React.createElement(FocusScope, { restoreFocus: true },
        React.createElement("div", __assign({ className: cn("bg-base-white rounded-lg border-solid border-1 border-blue-gray-200 p-8px shadow-xl mt-1 min-w-196px", popOverStyles) }, mergeProps(overlayProps, dialogProps, otherProps, modalProps), { ref: popoverRef }),
            children,
            React.createElement(DismissButton, { onDismiss: onClose }))));
}

var mapToAriaProps = function (ariaLabel, options) { return ({
    "aria-label": ariaLabel,
    children: function (eachOption) { return (React__default.createElement(Item, { key: eachOption.value, textValue: eachOption.value },
        React__default.createElement(ListElement, { item: eachOption }))); },
}); };

var DropDownIcon = function (props) {
    var _a = props.fill, fill = _a === void 0 ? colors.blueGray500 : _a, _b = props.height, height = _b === void 0 ? 12 : _b, _c = props.width, width = _c === void 0 ? 8 : _c;
    return (React.createElement("svg", __assign({ width: width, height: height, fill: "none", xmlns: "http://www.w3.org/2000/svg" }, props),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.293 7.293a1 1 0 0 1 1.414 0L10 10.586l3.293-3.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414z", fill: fill })));
};

var DefaultTrigger = function (props) {
    var buttonProps = props.buttonProps, triggerProps = props.triggerProps, triggerContainerClassName = props.triggerContainerClassName, displayValue = props.displayValue, triggerDisplayValueContainerClassName = props.triggerDisplayValueContainerClassName, displayValueClassName = props.displayValueClassName, renderCustomTriggerIcon = props.renderCustomTriggerIcon, iconContainerClassName = props.iconContainerClassName, triggerIconFillColor = props.triggerIconFillColor, triggerIconStyles = props.triggerIconStyles, triggerRef = props.triggerRef, isOpen = props.isOpen, isLoading = props.isLoading, triggerBackgroundColor = props.triggerBackgroundColor;
    return (React__default.createElement("div", __assign({}, buttonProps, triggerProps, { ref: triggerRef, style: { backgroundColor: triggerBackgroundColor }, className: cn("flex bg-blue-600 rounded-8px outline-none cursor-pointer", triggerContainerClassName) }),
        React__default.createElement("div", { className: cn("px-16px py-8px", triggerDisplayValueContainerClassName) }, isLoading ? (React__default.createElement(Loader, { fill: "#fff" })) : (React__default.createElement("p", { className: cn("text-base-white", displayValueClassName) }, displayValue))),
        renderCustomTriggerIcon ? (renderCustomTriggerIcon()) : (React__default.createElement("div", { style: { borderColor: triggerBackgroundColor }, className: cn("p-8px border-blue-700 border-l-solid border-l-default flex items-center justify-center h-full", iconContainerClassName) }, isOpen ? (React__default.createElement(DropDownIcon, { style: __assign({ transform: "rotate(180deg)", transition: "transform 0.5s" }, triggerIconStyles), fill: triggerIconFillColor, height: 20, width: 20 })) : (React__default.createElement(DropDownIcon, { fill: triggerIconFillColor, style: __assign({ transition: "transform 0.5s" }, triggerIconStyles), height: 20, width: 20 }))))));
};

/** @class */ ((function (_super) {
    __extends(OpenStateIncorrectError, _super);
    function OpenStateIncorrectError(message, isOpen, defaultOpen) {
        var _this = _super.call(this, message) || this;
        _this.isOpen = isOpen;
        _this.defaultOpen = defaultOpen;
        return _this;
    }
    return OpenStateIncorrectError;
})(Error));
var BandedDropdown = function (props) {
    var _a = props.ariaLabel, ariaLabel = _a === void 0 ? "Dropdown" : _a, options = props.options, selectedOptionValue = props.selectedOptionValue, onChange = props.onChange, customOption = props.customOption, listBoxClassName = props.listBoxClassName, listItemClassName = props.listItemClassName, itemContentClassName = props.itemContentClassName, mobileListBoxClassName = props.mobileListBoxClassName, _b = props.closeOnSelect, closeOnSelect = _b === void 0 ? true : _b, _c = props.popoverPlacement, popoverPlacement = _c === void 0 ? "bottom end" : _c, isLoading = props.isLoading, isDisabled = props.isDisabled, _d = props.overlayOptions, overlayOptions = _d === void 0 ? {} : _d, isOpen = props.isOpen, defaultOpen = props.defaultOpen, onOpenChange = props.onOpenChange, popOverStyles = props.popOverStyles;
    if (typeof isOpen === "boolean" && typeof defaultOpen === "boolean") {
        throw new BandedDropdownOpenStateIncorrectError("Dropdown Options Open state can either be controlled or uncontrolled", isOpen, defaultOpen);
    }
    //#region Popover logic
    var state = useOverlayTriggerState({
        isOpen: isOpen,
        defaultOpen: defaultOpen,
        onOpenChange: onOpenChange,
    });
    var triggerRef = React__default.useRef(null);
    var overlayRef = React__default.useRef(null);
    var _e = useOverlayTrigger({ type: "dialog" }, state, triggerRef), triggerProps = _e.triggerProps, overlayProps = _e.overlayProps;
    var positionProps = useOverlayPosition(__assign({ targetRef: triggerRef, overlayRef: overlayRef, offset: 5, isOpen: state.isOpen, placement: popoverPlacement }, overlayOptions)).overlayProps;
    var buttonProps = useButton({
        onPress: function () { return !isLoading && !isDisabled && state.open(); },
    }, triggerRef).buttonProps;
    // #endregion
    //#region Listbox Logic
    var propsWithChildren = mapToAriaProps(ariaLabel);
    var selectState = useSelectState(__assign(__assign({}, propsWithChildren), { items: options, defaultSelectedKey: selectedOptionValue, onSelectionChange: function (key) {
            onChange(key);
            if (closeOnSelect) {
                state.close();
            }
        } }));
    //#endregion
    var renderTrigger = function () {
        var displayValue = props.displayValue, triggerContainerClassName = props.triggerContainerClassName, triggerDisplayValueContainerClassName = props.triggerDisplayValueContainerClassName, displayValueClassName = props.displayValueClassName, renderCustomTriggerIcon = props.renderCustomTriggerIcon, iconContainerClassName = props.iconContainerClassName, _a = props.triggerIconFillColor, triggerIconFillColor = _a === void 0 ? colors.baseWhite : _a, triggerIconStyles = props.triggerIconStyles, renderCustomTrigger = props.renderCustomTrigger, isLoading = props.isLoading, triggerBackgroundColor = props.triggerBackgroundColor;
        var defaultTriggerProps = {
            displayValue: displayValue,
            triggerContainerClassName: triggerContainerClassName,
            triggerDisplayValueContainerClassName: triggerDisplayValueContainerClassName,
            displayValueClassName: displayValueClassName,
            renderCustomTriggerIcon: renderCustomTriggerIcon,
            iconContainerClassName: iconContainerClassName,
            triggerIconFillColor: triggerIconFillColor,
            triggerIconStyles: triggerIconStyles,
            renderCustomTrigger: renderCustomTrigger,
            buttonProps: buttonProps,
            triggerProps: triggerProps,
            triggerRef: triggerRef,
            isLoading: isLoading,
            triggerBackgroundColor: triggerBackgroundColor,
            isOpen: state.isOpen,
        };
        return renderCustomTrigger ? (renderCustomTrigger(__assign(__assign(__assign({}, buttonProps), triggerProps), { ref: triggerRef }), state.isOpen)) : (React__default.createElement(DefaultTrigger, __assign({}, defaultTriggerProps)));
    };
    return (React__default.createElement(React__default.Fragment, null,
        renderTrigger(),
        state.isOpen ? (React__default.createElement(OverlayContainer, null,
            React__default.createElement(Popover, __assign({}, overlayProps, positionProps, { popOverStyles: popOverStyles, popoverRef: overlayRef, isOpen: state.isOpen, onClose: state.close }),
                React__default.createElement(ListBox, { state: selectState, selectedOptionIcon: TickIcon, customOption: customOption, listBoxStyles: listBoxClassName, listItemStyles: listItemClassName, itemContentStyles: itemContentClassName, mobileListBoxStyles: cn(mobileListBoxClassName) })))) : null));
};

var OutlineImage = function () { return (React.createElement("svg", { width: 48, height: 48, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M18 21.5c-3.04 0-5.5-2.46-5.5-5.5s2.46-5.5 5.5-5.5 5.5 2.46 5.5 5.5-2.46 5.5-5.5 5.5zm0-8a2.5 2.5 0 0 0 0 5 2.5 2.5 0 0 0 0-5z", fill: "#94A3B8" }),
    React.createElement("path", { d: "M30 45.5H18C7.14 45.5 2.5 40.86 2.5 30V18C2.5 7.14 7.14 2.5 18 2.5h8c.82 0 1.5.68 1.5 1.5s-.68 1.5-1.5 1.5h-8C8.78 5.5 5.5 8.78 5.5 18v12c0 9.22 3.28 12.5 12.5 12.5h12c9.22 0 12.5-3.28 12.5-12.5V20c0-.82.68-1.5 1.5-1.5s1.5.68 1.5 1.5v10c0 10.86-4.64 15.5-15.5 15.5z", fill: "#94A3B8" }),
    React.createElement("path", { d: "M42.5 11.5h-11c-.82 0-1.5-.68-1.5-1.5s.68-1.5 1.5-1.5h11c.82 0 1.5.68 1.5 1.5s-.68 1.5-1.5 1.5z", fill: "#94A3B8" }),
    React.createElement("path", { d: "M37 17c-.82 0-1.5-.68-1.5-1.5v-11c0-.82.68-1.5 1.5-1.5s1.5.68 1.5 1.5v11c0 .82-.68 1.5-1.5 1.5zM5.34 39.4c-.48 0-.96-.24-1.24-.66-.46-.68-.28-1.62.4-2.08l9.86-6.62c2.16-1.44 5.14-1.28 7.1.38l.66.58c1 .86 2.7.86 3.68 0l8.32-7.14c2.14-1.82 5.46-1.82 7.6 0l3.26 2.8c.62.54.7 1.48.16 2.12-.54.62-1.48.7-2.12.16l-3.26-2.8c-1-.86-2.7-.86-3.68 0l-8.32 7.14c-2.12 1.82-5.46 1.82-7.6 0l-.66-.58c-.92-.78-2.44-.86-3.46-.16L6.2 39.16c-.28.16-.58.24-.86.24z", fill: "#94A3B8" }))); };

var UploadDocumentOutline = function () { return (React.createElement("svg", { width: 48, height: 48, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M32 45.5H16C8.7 45.5 4.5 41.3 4.5 34V14C4.5 6.7 8.7 2.5 16 2.5h16c7.3 0 11.5 4.2 11.5 11.5v20c0 7.3-4.2 11.5-11.5 11.5Zm-16-40c-5.72 0-8.5 2.78-8.5 8.5v20c0 5.72 2.78 8.5 8.5 8.5h16c5.72 0 8.5-2.78 8.5-8.5V14c0-5.72-2.78-8.5-8.5-8.5H16Z", fill: "#94A3B8" }),
    React.createElement("path", { d: "M37 18.5h-4c-3.04 0-5.5-2.46-5.5-5.5V9c0-.82.68-1.5 1.5-1.5s1.5.68 1.5 1.5v4a2.5 2.5 0 0 0 2.5 2.5h4c.82 0 1.5.68 1.5 1.5s-.68 1.5-1.5 1.5ZM24 27.5h-8c-.82 0-1.5-.68-1.5-1.5s.68-1.5 1.5-1.5h8c.82 0 1.5.68 1.5 1.5s-.68 1.5-1.5 1.5ZM32 35.5H16c-.82 0-1.5-.68-1.5-1.5s.68-1.5 1.5-1.5h16c.82 0 1.5.68 1.5 1.5s-.68 1.5-1.5 1.5Z", fill: "#94A3B8" }))); };

var UploaderUI = function (props) {
    var uploadFile = props.uploadFile, onClickUploader = props.onClickUploader, isImageFile = props.isImageFile, placeholderText = props.placeholderText, hint = props.hint, errorMessage = props.errorMessage, hintClassName = props.hintClassName;
    var _a = __read(React__default.useState(false), 2); _a[0]; var setFocusWithin = _a[1];
    var focusWithinProps = useFocusWithin({
        isDisabled: false,
        onFocusWithinChange: function (isFocusWithin) { return setFocusWithin(isFocusWithin); },
    }).focusWithinProps;
    var hintId = useId();
    var DropZoneWithoutClick = function () {
        var _a = useDropzone(), getRootProps = _a.getRootProps, acceptedFiles = _a.acceptedFiles, isDragActive = _a.isDragActive;
        if (acceptedFiles[0]) {
            uploadFile(acceptedFiles[0]);
        }
        return (React__default.createElement("div", __assign({}, focusWithinProps, getRootProps({ className: "dropzone" })),
            React__default.createElement("div", { onClick: onClickUploader, className: cn("flex flex-col items-center p-30px rounded-8px border-dashed border-1 border-blue-gray-300 cursor-pointer ", isDragActive ? "bg-blue-gray-200" : "bg-base-white") },
                isImageFile ? React__default.createElement(OutlineImage, null) : React__default.createElement(UploadDocumentOutline, null),
                React__default.createElement("div", { className: cn("text-14px text-center mt-6") },
                    React__default.createElement("span", { className: cn("text-sm text-blue-600") }, "Upload a file"),
                    React__default.createElement("span", { className: cn("text-sm ml-1 text-blue-gray-700") }, "or drag and drop"),
                    React__default.createElement("div", { className: cn("text-blue-gray-500 text-14px mt-2") }, placeholderText))),
            React__default.createElement(Hint, { id: hintId, hint: hint, errorText: errorMessage ? errorMessage : "", hintClassName: hintClassName })));
    };
    return React__default.createElement(DropZoneWithoutClick, null);
};

var DeleteIcon = function () { return (React.createElement("svg", { width: 16, height: 16, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M14 4.487h-.053C10.42 4.133 6.9 4 3.414 4.353l-1.36.134a.503.503 0 0 1-.107-1l1.36-.134c3.547-.36 7.14-.22 10.74.134a.5.5 0 0 1 .447.546.494.494 0 0 1-.494.454Z", fill: "#DC2626" }),
    React.createElement("path", { d: "M5.667 3.813c-.027 0-.054 0-.087-.006a.502.502 0 0 1-.407-.574l.147-.873c.107-.64.253-1.527 1.807-1.527h1.746c1.56 0 1.707.92 1.807 1.534l.146.866a.495.495 0 0 1-.406.574.495.495 0 0 1-.573-.407L9.7 2.533c-.093-.58-.113-.693-.82-.693H7.133c-.706 0-.72.093-.82.687l-.153.866a.5.5 0 0 1-.493.42ZM10.14 15.167H5.86c-2.327 0-2.42-1.287-2.494-2.327l-.433-6.713a.505.505 0 0 1 .467-.534.505.505 0 0 1 .533.467l.433 6.713c.074 1.014.1 1.394 1.494 1.394h4.28c1.4 0 1.426-.38 1.493-1.394l.433-6.713a.51.51 0 0 1 .534-.467.5.5 0 0 1 .466.534l-.433 6.713c-.073 1.04-.167 2.327-2.493 2.327Z", fill: "#DC2626" }),
    React.createElement("path", { d: "M9.107 11.5h-2.22a.504.504 0 0 1-.5-.5c0-.273.226-.5.5-.5h2.22c.273 0 .5.227.5.5s-.227.5-.5.5ZM9.666 8.833H6.333a.504.504 0 0 1-.5-.5c0-.273.227-.5.5-.5h3.333c.274 0 .5.227.5.5 0 .274-.226.5-.5.5Z", fill: "#DC2626" }))); };

var OutlineDocument = function () { return (React.createElement("svg", { width: 32, height: 32, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M21.333 30.333H10.667C5.8 30.333 3 27.533 3 22.667V9.333c0-4.866 2.8-7.666 7.667-7.666h10.666C26.2 1.667 29 4.467 29 9.333v13.334c0 4.866-2.8 7.666-7.667 7.666zM10.667 3.667C6.853 3.667 5 5.52 5 9.333v13.334c0 3.813 1.853 5.666 5.667 5.666h10.666c3.814 0 5.667-1.853 5.667-5.666V9.333c0-3.813-1.853-5.666-5.667-5.666H10.667z", fill: "#475569" }),
    React.createElement("path", { d: "M24.666 12.333H22a3.665 3.665 0 0 1-3.667-3.666V6c0-.547.453-1 1-1 .547 0 1 .453 1 1v2.667c0 .92.747 1.666 1.667 1.666h2.666c.547 0 1 .454 1 1 0 .547-.453 1-1 1zm-8.666 6h-5.333c-.547 0-1-.453-1-1 0-.546.453-1 1-1H16c.547 0 1 .454 1 1 0 .547-.453 1-1 1zm5.334 5.334H10.667c-.547 0-1-.454-1-1 0-.547.453-1 1-1h10.667c.546 0 1 .453 1 1 0 .546-.454 1-1 1z", fill: "#475569" }))); };

var RetryIcon = function () { return (React.createElement("svg", { width: 16, height: 16, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M8 15.167a6.286 6.286 0 0 1-6.28-6.28c0-1.247.366-2.454 1.06-3.494a.503.503 0 0 1 .693-.14.504.504 0 0 1 .14.694 5.283 5.283 0 0 0 4.393 8.213 5.283 5.283 0 0 0 5.28-5.28A5.29 5.29 0 0 0 8 3.6c-.614 0-1.214.087-1.78.26a.5.5 0 0 1-.627-.333.5.5 0 0 1 .333-.627c.667-.2 1.36-.307 2.074-.307 3.466 0 6.28 2.82 6.28 6.28 0 3.46-2.814 6.294-6.28 6.294Z", fill: "#334155" }),
    React.createElement("path", { d: "M5.246 4.047a.499.499 0 0 1-.38-.827l1.927-2.213c.18-.207.5-.234.707-.047.206.18.226.5.046.707L5.62 3.873a.505.505 0 0 1-.374.174Z", fill: "#334155" }),
    React.createElement("path", { d: "M7.493 5.687a.527.527 0 0 1-.293-.094L4.947 3.947a.5.5 0 0 1 .593-.807l2.247 1.64a.5.5 0 0 1 .106.7.473.473 0 0 1-.4.207Z", fill: "#334155" }))); };

var UploadedFileUI = function (props) {
    var fileName = props.fileName, onClickDeleteButton = props.onClickDeleteButton; props.isUploadFailed; var uploadAgain = props.uploadAgain, isFileUploaded = props.isFileUploaded, _a = props.uploadPercentage, uploadPercentage = _a === void 0 ? 0 : _a, isLoading = props.isLoading, hint = props.hint, errorMessage = props.errorMessage, hintClassName = props.hintClassName;
    var hintId = useId();
    return (React__default.createElement("div", { className: cn("mb-4 w-full p-30px rounded-8px border-dashed border-1 border-blue-gray-300 bg-base-white cursor-default") },
        React__default.createElement("div", { className: cn("flex w-full justify-center items-center ") },
            React__default.createElement(OutlineDocument, null),
            React__default.createElement("div", { className: cn("flex flex-col ml-8px") },
                React__default.createElement("div", { className: cn("flex items-center justify-between w-full") },
                    React__default.createElement("span", { className: cn("text-ellipsis text-14px font-medium text-blue-gray-700 whitespace-nowrap overflow-hidden") }, fileName),
                    isLoading && (React__default.createElement("div", { className: cn("cursor-pointer"), onClick: onClickDeleteButton },
                        React__default.createElement(CrossIcon, null)))),
                isLoading && (React__default.createElement("div", { className: cn("w-full h-4px bg-blue-100 rounded-4px min-w-200px mt-10px") },
                    React__default.createElement("div", { style: { width: uploadPercentage + "%" }, className: cn("rounded-4px bg-blue-600 h-4px") }))))),
        React__default.createElement(Hint, { id: hintId, hint: hint, errorText: errorMessage ? errorMessage : "", hintClassName: hintClassName }),
        React__default.createElement("div", { className: cn("flex mt-6 w-full items-center justify-center") },
            isFileUploaded && (React__default.createElement("div", { className: cn("flex justify-center items-center mr-32px cursor-pointer"), onClick: onClickDeleteButton },
                React__default.createElement(DeleteIcon, null),
                React__default.createElement("span", { className: cn("text-red-600 ml-2px mt-2px") }, "Delete"))),
            React__default.createElement("div", { className: cn("flex justify-center items-center cursor-pointer"), onClick: uploadAgain },
                React__default.createElement(RetryIcon, null),
                React__default.createElement("span", { className: cn("text-blue-gray-700 ml-2px mt-2px") }, "Upload again")))));
};

var FileUploader = function (props) {
    var _a = __read(useState(), 2), selectedFile = _a[0], setSelectedFile = _a[1];
    var onUpload = props.onUpload, isNotValid = props.isNotValid, isError = props.isError, errorMessage = props.errorMessage, isLoading = props.isLoading, isSuccess = props.isSuccess, onDelete = props.onDelete, acceptTypes = props.acceptTypes, isImageFile = props.isImageFile, label = props.label, labelClassName = props.labelClassName, labelProps = props.labelProps, uploadPercentage = props.uploadPercentage, imageUrl = props.imageUrl, placeholderText = props.placeholderText, hint = props.hint, hintClassName = props.hintClassName, cancelUpload = props.cancelUpload, isRequired = props.isRequired;
    var inputRef = useRef();
    var checkIt = function () {
        var selectedFiles = inputRef.current.files;
        if (selectedFiles && selectedFiles.length > 0) {
            setSelectedFile(selectedFiles[0]);
            onUpload(selectedFiles[0]);
        }
        document.body.onfocus = null;
    };
    var onClickUploadFile = function () {
        inputRef.current.click();
    };
    var onClickDeleteButton = function () {
        isSuccess
            ? onDelete && onDelete(selectedFile)
            : cancelUpload && cancelUpload();
        setSelectedFile(undefined);
        inputRef.current.value = "";
    };
    var onDragAndDrop = function (file) {
        setSelectedFile(file);
        onUpload(file);
    };
    var hintId = useId();
    return (React__default.createElement("div", { className: cn("flex flex-col  w-full") },
        label ? (React__default.createElement(Label, { labelProps: labelProps, labelClassName: cn("mb-4px", labelClassName), isRequired: isRequired }, label)) : null,
        isSuccess || isError || isLoading ? (React__default.createElement(UploadedFileUI, { imageUrl: imageUrl, fileName: selectedFile === null || selectedFile === void 0 ? void 0 : selectedFile.name, onClickDeleteButton: onClickDeleteButton, uploadAgain: onClickUploadFile, isUploadFailed: isError, errorMessage: errorMessage, isFileUploaded: isSuccess, isLoading: isLoading, uploadPercentage: uploadPercentage })) : (React__default.createElement(UploaderUI, { onClickUploader: onClickUploadFile, isNotValid: isNotValid, uploadFile: onDragAndDrop, isImageFile: isImageFile, placeholderText: placeholderText })),
        React__default.createElement("input", { className: cn("hidden"), type: "file", ref: inputRef, onChange: checkIt, accept: acceptTypes }),
        React__default.createElement(Hint, { id: hintId, hint: hint, errorText: "", hintClassName: hintClassName })));
};

function MoreIcon(props) {
    var _a = props.fill, fill = _a === void 0 ? "#475569" : _a;
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "24", height: "24", fill: "none", viewBox: "0 0 24 24" },
        React__default.createElement("path", { fill: fill, d: "M4.723 13.447a1.723 1.723 0 100-3.447 1.723 1.723 0 000 3.447zM12 13.447A1.723 1.723 0 1012 10a1.723 1.723 0 000 3.447zM19.276 13.447a1.723 1.723 0 100-3.447 1.723 1.723 0 000 3.447z" })));
}

var MenuDropdown = function (props) {
    var renderCustomTrigger = props.renderCustomTrigger, other = __rest(props, ["renderCustomTrigger"]);
    var renderTrigger = function (props, isOpen) {
        return renderCustomTrigger ? (renderCustomTrigger(props, isOpen)) : (React__default.createElement("div", __assign({}, props),
            React__default.createElement(MoreIcon, null)));
    };
    return React__default.createElement(BandedDropdown, __assign({}, other, { renderCustomTrigger: renderTrigger }));
};

export { Avatar, Badge, BandedDropdown, BottomSheet, BottomSheetWithTrigger, Button, Card, Checkbox, CheckboxGroup, CheckboxGroupItem, ComboBox, ComboBoxButton, CountriesList, index$4 as CrossIcon, DatePicker, index$2 as DropDownIcon, FileUploader, FocusRing, HiddenInput, Hint, InternationalMobileNumber, InternationalizationNotSupportedError, Label, ListBox, _ListBoxBase as ListBoxBase, ListElement, Loader, MenuDropdown, MultiSelect, index as NoResultsFoundIcon, OmegaProvider, Picker, _Picker as PickerComponent, _Popover as Popover, Radio, RadioGroup, index$1 as SearchIcon, TextArea, TextField, index$3 as TickIcon, Toast, ToastContainer, ToastTypes, _Tray as Tray, cardShadowSize, getComboBoxWrapperWithPopOverStyles, getMobileComboboxButtonStyles, getMobileComboboxWrapperStyles, positionConstants, themeContext, useListBoxLayout };
//# sourceMappingURL=lib.esm.js.map
